import QuickAccessPanel from "../components/QuickAccessPanel";
import { FavoriteButton, FavoriteAnalyticsShell } from "../components/AnalyticsFavorites";
import analyticsCatalogue from "../lib/analyticsSearchCatalogue.json";
import { useAnalyticsSearchTarget } from "../lib/analyticsSearch";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import WidgetRenderer from "../components/WidgetRenderer";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { MasterSearchPanel } from "./MasterSearchDetail";
import { downloadCsv } from "../lib/csv";
import { setAskAiContext, ceoFiltersToAskAiContext, openAskAi } from "../lib/askAiContext";
import {
  Crown,
  ShoppingCart,
  Factory,
  Warehouse,
  Truck,
  DollarSign,
  Search,
  Brain,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  FlaskConical,
  Loader2,
  Eye,
  Rocket,
  LayoutDashboard,
  Download,
  X
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const analyticsById = Object.fromEntries(analyticsCatalogue.map(entry => [entry.id, entry]));

const TABS = [
  { id: "quick-access", label: "Quick Access" },
  { id: "overview", label: "CEO Overview" },
  { id: "buyer", label: "Buyer & Sales" },
  { id: "purchase", label: "Purchase / Procurement" },
  { id: "commercial", label: "Commercial" },
  { id: "po", label: "PO Fulfilment" },
  { id: "store", label: "Store / Cold Store" },
  { id: "production", label: "Production" },
  { id: "shipment", label: "Shipment / Dispatch" },
  { id: "qc", label: "QC" },
  { id: "comparisons", label: "Comparisons" },
  { id: "master", label: "Master Search" },
  { id: "ai", label: "AI Ask" },
];

const CEO_AI_EXAMPLES = [
  "Create a CEO dashboard for buyer wise sales, stock availability, PO fulfilment, production received quantity, shipment trend, and QC table.",
  "Create a dashboard showing buyer wise sales, destination wise sales, grade wise sales, and invoice level details.",
  "Create a stock availability dashboard by cold store, grade, production date, and barcode stock register.",
  "Create a production dashboard showing plant wise received quantity, finished variety freezing quantity, VA quantity, and yield details.",
  "Create a shipment dashboard showing shipment trend, stock-out by cold store, shipment details, and PO linked dispatch quantity.",
  "Create a QC dashboard showing sample number, sample location, product type, testing lab, date, and linked invoice IDs.",
  "Compare sales value, ordered MCS, production quantity, stock availability, and shipment quantity for the selected period.",
];

const DS = {
  sales: "ds-camaroo-qa-sales",
  payments: "ds-camaroo-qa-payments",
  po: "ds-camaroo-qa-po",
  stock: "ds-camaroo-qa-stock",
  preprocessing: "ds-camaroo-qa-preprocessing",
  shipment: "ds-camaroo-qa-shipment",
  shipmentOut: "ds-camaroo-qa-shipment-out",
  movement: "ds-camaroo-qa-movement",
  qc: "ds-camaroo-qa-qc-lab",
  rmPurchase: "ds-camaroo-qa-rm-purchase",
  vaPlant: "ds-camaroo-qa-va-plant",
  soakingPlant: "ds-camaroo-qa-soaking-plant",
  freezingType: "ds-camaroo-qa-freezing-type",
  deheading: "ds-camaroo-qa-deheading",
  dailyProduction: "ds-camaroo-qa-daily-production",
  packingType: "ds-camaroo-qa-packing-type",
  dummyStock: "ds-camaroo-qa-dummy-stock",
};

const DATE_FIELD_BY_DATASET = {
  [DS.sales]: "InvoiceDate",
  [DS.po]: "PoDate",
  [DS.stock]: "ProductionDate",
  [DS.preprocessing]: "ArrivalDate",
  [DS.shipment]: "ShipmentDate",
  [DS.shipmentOut]: "ShipmentDate",
  [DS.movement]: "TransferDate",
  [DS.qc]: "AnalysisDate",
  [DS.rmPurchase]: "PurchaseDate",
  [DS.vaPlant]: "ProcessingDate",
  [DS.soakingPlant]: "SoakingDate",
  [DS.freezingType]: "ProductionDate",
  [DS.deheading]: "ArrivalDate",
  [DS.dailyProduction]: "ProductionDate",
  [DS.packingType]: "ProductionDate",
  [DS.dummyStock]: "ProductionDate",
};

const FILTER_FIELDS_BY_DATASET = {
  [DS.sales]: {
    buyer: "BuyerName",
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    variety: "Variety",
    brand: "BrandName",
  },
  [DS.po]: {
    country: "DestinationCountry",
    grade: "Grade",
    variety: "Variety",
    brand: "Brand",
  },
  [DS.stock]: {
    grade: "Grade",
    variety: "Variety",
    coldStore: "ColdStore",
  },
  [DS.preprocessing]: {
    plant: "ProductionPlant",
    variety: "FinishedVarieties",
  },
  [DS.shipment]: {
    grade: "Grade",
    coldStore: "ColdStore",
  },
  [DS.shipmentOut]: {
    country: "DestinationCountry",
    grade: "Grade",
    coldStore: "ColdStore",
  },
  [DS.qc]: {
    plant: "Plant",
  },
  [DS.vaPlant]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
  },
  [DS.soakingPlant]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
  },
  [DS.freezingType]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
  },
  [DS.deheading]: {
    plant: "Plant",
  },
  [DS.dailyProduction]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
  },
  [DS.packingType]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
  },
  [DS.dummyStock]: {
    plant: "Plant",
    grade: "Grade",
    buyer: "BuyerName",
  },
};

function currentFinancialYearRange() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    from: `${year}-04-01`,
    to: `${year + 1}-03-31`,
  };
}

function currentCalendarYearRange() {
  const year = new Date().getFullYear();
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

function previousCalendarYearRange(offset = 1) {
  const year = new Date().getFullYear() - offset;
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

function createCeoFilters(widget, filters) {
  const datasetId = widget?.dataset_id;
  const mapped = [];
  const dateField = DATE_FIELD_BY_DATASET[datasetId];

  if (dateField && (filters.dateFrom || filters.dateTo)) {
    mapped.push({
      field: dateField,
      op: "date_range",
      value: {
        from: filters.dateFrom || undefined,
        to: filters.dateTo || undefined,
      },
    });
  }

  const fieldMap = FILTER_FIELDS_BY_DATASET[datasetId] || {};

  [
    ["buyer", filters.buyer],
    ["consignee", filters.consignee],
    ["country", filters.country],
    ["grade", filters.grade],
    ["variety", filters.variety],
    ["brand", filters.brand],
    ["coldStore", filters.coldStore],
    ["plant", filters.plant],
  ].forEach(([key, value]) => {
    const field = fieldMap[key];
    if (field && value) {
      mapped.push({ field, op: "in", value: [value] });
    }
  });

  return mapped;
}

function makeWidget(id, type, title, dataset_id, extra = {}) {
  return {
    id,
    type,
    title,
    dataset_id,
    limit: 20,
    filters: [],
    ...extra,
  };
}

const W = {
  purchaseWeight: makeWidget("ceo-purchase-weight", "kpi", "RM Purchase Weight", DS.rmPurchase, {
    measure: "PurchaseWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  purchaseProductionWeight: makeWidget("ceo-purchase-production-weight", "kpi", "RM Production Weight", DS.rmPurchase, {
    measure: "ProductionWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  purchaseLotCount: makeWidget("ceo-purchase-lot-count", "kpi", "RM Lots", DS.rmPurchase, {
    measure: "LotRecordID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  purchaseSupplierWeight: makeWidget("ceo-purchase-supplier-weight", "bar", "Supplier-wise RM Purchase", DS.rmPurchase, {
    dimension: "SupplierName",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 15,
  }),
  purchaseCentreWeight: makeWidget("ceo-purchase-centre-weight", "bar", "Centre-wise RM Purchase", DS.rmPurchase, {
    dimension: "Centre",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 15,
  }),
  purchaseStationWeight: makeWidget("ceo-purchase-station-weight", "bar", "Purchase Station-wise RM Purchase", DS.rmPurchase, {
    dimension: "PurchaseStation",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 15,
  }),

  purchaseTrend: makeWidget("ceo-purchase-trend", "line", "RM Purchase Weight Trend", DS.rmPurchase, {
    dimension: "PurchaseDate",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 60,
    sort: { by: "dimension", dir: "desc" },
  }),
  purchaseLotStatus: makeWidget("ceo-purchase-lot-status", "donut", "RM Lot Status", DS.rmPurchase, {
    dimension: "LotStatus",
    measure: "LotRecordID",
    aggregation: "count_distinct",
    limit: 10,
  }),
  rmRequirementGap: {
    id: "ceo-rm-requirement-gap",
    type: "ceo-rm-requirement-gap",
    title: "RM Requirement & Procurement Gap",
    limit: 1,
    filters: [],
  },
  crossModuleLineage: {
    id: "ceo-cross-module-lineage",
    type: "ceo-client-closure-status",
    title: "Cross-Module Record Lineage",
    endpoint: "/ai/ceo-chat/cross-module-lineage",
    limit: 1,
    filters: [],
  },
  documentationStatus: {
    id: "ceo-documentation-status",
    type: "ceo-client-closure-status",
    title: "Order Documentation Completion Status",
    endpoint: "/ai/ceo-chat/documentation-status",
    limit: 1,
    filters: [],
  },

  purchaseTable: makeWidget("ceo-purchase-table", "table", "Raw Material Purchase Details", DS.rmPurchase, {
    selected_columns: [
      "LotNumber",
      "PurchaseDate",
      "SupplierName",
      "SupplierType",
      "SupplierCentre",
      "PurchaseStation",
      "Centre",
      "PurchaseCount",
      "PurchaseWeight",
      "ProductionWeight",
      "LotStatus"
    ],
    limit: 100,
  }),

  commercialSalesUsd: makeWidget("ceo-commercial-sales-usd", "kpi", "Commercial Sales USD", DS.sales, { measure: "LineValueUSD", aggregation: "sum", number_format: { prefix: "$", decimals: 0 } }),
  commercialSalesKgs: makeWidget("ceo-commercial-sales-kgs", "kpi", "Commercial Sales KGS", DS.sales, { measure: "InvoiceGrossKGS", aggregation: "sum", number_format: { decimals: 0 } }),
  commercialUtilizedValue: makeWidget("ceo-commercial-utilized-value", "kpi", "Utilized Collection Value", DS.payments, { measure: "UtilizedValue", aggregation: "sum", number_format: { decimals: 2 } }),
  commercialBankCharges: makeWidget("ceo-commercial-bank-charges", "kpi", "Bank Charges", DS.payments, { measure: "BankCharges", aggregation: "sum", number_format: { decimals: 2 } }),
  commercialBuyerSales: makeWidget("ceo-commercial-buyer-sales", "bar", "Buyer-wise Commercial Sales", DS.sales, { dimension: "ConsigneeName", measure: "LineValueUSD", aggregation: "sum", limit: 10 }),
  commercialDestinationSales: makeWidget("ceo-commercial-destination-sales", "bar", "Destination-wise Commercial Sales", DS.sales, { dimension: "DestinationCountry", measure: "LineValueUSD", aggregation: "sum", limit: 10 }),
  commercialCollectionRate: makeWidget("ceo-commercial-collection-rate", "bar", "Collection Rate by Invoice", DS.payments, { dimension: "InvNumFull", measure: "CollectionRate", aggregation: "avg", limit: 10 }),
  commercialVarietyAnalysis: {
    id: "ceo-commercial-variety-analysis",
    type: "ceo-variety-analysis",
    title: "Variety Analysis",
    limit: 10,
    filters: [],
  },
  commercialAntidumping: {
    id: "ceo-antidumping",
    type: "ceo-antidumping",
    title: "Antidumping — Annual Sales Contribution",
    limit: 10,
    filters: [],
  },
  annualCountryContribution: {
    id: "ceo-annual-country-contribution",
    type: "ceo-annual-country-contribution",
    title: "Annual % Contribution — USA, China, Russia & Belgium",
    limit: 10,
    filters: [],
  },
  commercialPaymentsReceivedPending: {id:"ceo-payments-received-pending",type:"ceo-payments-received-pending",title:"Payments Received / Pending",dataset_id:DS.payments,limit:1,filters:[]},
  commercialPendingShipments: { id: "ceo-commercial-pending-shipments", type: "ceo-shipment-pending-risk", title: "Pending Shipments", dataset_id: DS.po, limit: 1, filters: [] },
  commercialTable: makeWidget("ceo-commercial-table", "table", "Commercial Sales Details", DS.sales, { selected_columns: ["InvoiceNumber", "InvoiceDate", "BuyerName", "BuyerCountry", "PoNumber", "PoDate", "BrandName", "Grade", "Variety", "MCS", "InvoiceGrossKGS", "PriceNative", "Currency", "QtyUnit", "LineValueNative", "LineValueUSD", "DestinationCountry", "ShipmentDate"], limit: 100 }),
  commercialPaymentTable: makeWidget("ceo-commercial-payment-table", "table", "Payment & Collection Details", DS.payments, { selected_columns: ["PoID", "InvNumFull", "PoNo", "PoDate", "CollectionRate", "DueDate", "ETAPOD", "BLDate", "BankCharges", "UtilizedValue", "FirstCollectionDate", "LastCollectionDate", "LastUtilizedDate", "SpotRate"], limit: 100 }),
  totalSalesUsd: makeWidget("ceo-total-sales-usd", "kpi", "Total Sales Value USD", DS.sales, {
    measure: "LineValueUSD",
    aggregation: "sum",
    number_format: { prefix: "$", decimals: 0 },
  }),
  totalSalesKgs: makeWidget("ceo-total-sales-kgs", "kpi", "Total Sales Gross KGS", DS.sales, {
    measure: "InvoiceGrossKGS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  orderedMcs: makeWidget("ceo-ordered-mcs", "kpi", "Ordered MCS", DS.po, {
    measure: "OrderedMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  dispatchedMcs: makeWidget("ceo-dispatched-mcs", "kpi", "Dispatched MCS", DS.po, {
    measure: "DispatchedMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  availableMcs: makeWidget("ceo-available-mcs", "kpi", "Available Stock MCS", DS.stock, {
    measure: "AvailableMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  availableKgs: makeWidget("ceo-available-kgs", "kpi", "Available Stock KGS", DS.stock, {
    measure: "AvailableKGS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  receivedQty: makeWidget("ceo-received-qty", "kpi", "Production Received Qty", DS.preprocessing, {
    measure: "ReceivedQty",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),

  deheadingReceivedWeight: makeWidget("ceo-deheading-received-weight", "kpi", "Deheading Input Weight", DS.deheading, {
    measure: "ReceivedWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  deheadingOutputWeight: makeWidget("ceo-deheading-output-weight", "kpi", "Weight After Deheading", DS.deheading, {
    measure: "WeightAfterDeheading",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  looseShellWeight: makeWidget("ceo-loose-shell-weight", "kpi", "Loose Shell Weight", DS.deheading, {
    measure: "LooseShellWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  deheadingByPlant: makeWidget("ceo-deheading-by-plant", "bar", "Deheading Output by Plant", DS.deheading, {
    dimension: "Plant",
    measure: "WeightAfterDeheading",
    aggregation: "sum",
    limit: 12,
  }),
  deheadingTable: makeWidget("ceo-deheading-table", "table", "Deheading / Headless Processing Details", DS.deheading, {
    selected_columns: [
      "DeheadingID",
      "TrackID",
      "ArrivalDate",
      "ProcessingDate",
      "Plant",
      "Centre",
      "CentreName",
      "HeadOnCount",
      "NetCount",
      "CountAfterDeheading",
      "HeadOnWeight",
      "ReceivedWeight",
      "WeightAfterDeheading",
      "LooseShellWeight",
    ],
    limit: 100,
  }),

  vaInputWeight: makeWidget("ceo-va-input-weight", "kpi", "VA Input Weight", DS.vaPlant, {
    measure: "VAInputWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  vaOutputWeight: makeWidget("ceo-va-output-weight", "kpi", "VA Output Weight", DS.vaPlant, {
    measure: "VAOutputWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  vaPercentByPlant: makeWidget(
    "ceo-va-percent-by-plant",
    "ceo-va-percent-by-plant",
    "Peeling (VA) % by Plant",
    DS.vaPlant,
    {}
  ),
  vaByPlant: makeWidget("ceo-va-by-plant", "bar", "Value Addition Output by Plant", DS.vaPlant, {
    dimension: "Plant",
    measure: "VAOutputWeight",
    aggregation: "sum",
    limit: 12,
  }),
  vaByVariety: makeWidget("ceo-va-by-variety", "bar", "Value Addition Output by Variety", DS.vaPlant, {
    dimension: "Variety",
    measure: "VAOutputWeight",
    aggregation: "sum",
    limit: 15,
  }),
  vaTable: makeWidget("ceo-va-table", "table", "Value Addition Processing Details", DS.vaPlant, {
    selected_columns: [
      "VARecordID",
      "TrackID",
      "ProcessingDate",
      "Plant",
      "Grade",
      "Variety",
      "VAInputWeight",
      "VAOutputWeight",
      "VAReceivedWeight",
      "VAReturnWeight",
    ],
    limit: 100,
  }),

  soakInQty: makeWidget("ceo-soak-in-qty", "kpi", "Soaking Input Qty", DS.soakingPlant, {
    measure: "SoakInQty",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  soakOutQty: makeWidget("ceo-soak-out-qty", "kpi", "Soaking Output Qty", DS.soakingPlant, {
    measure: "SoakOutQty",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  soakingByPlant: makeWidget("ceo-soaking-by-plant", "bar", "Soaking Output by Plant", DS.soakingPlant, {
    dimension: "Plant",
    measure: "SoakOutQty",
    aggregation: "sum",
    limit: 12,
  }),
  soakingByTreatment: makeWidget("ceo-soaking-by-treatment", "bar", "Soaking by Treatment", DS.soakingPlant, {
    dimension: "SoakingTreatment",
    measure: "SoakOutQty",
    aggregation: "sum",
    limit: 12,
  }),
  soakingPickupByItemPlant: makeWidget(
    "ceo-soaking-pickup-item-plant",
    "ceo-soaking-pickup-item-plant",
    "Soaking Pickup — Least to Highest by Item & Plant",

    DS.soakingPlant,
    {}
  ),
  soakingTable: makeWidget("ceo-soaking-table", "table", "Soaking Processing Details", DS.soakingPlant, {
    selected_columns: [
      "SoakingRecordID",
      "TrackID",
      "SoakingDate",
      "Plant",
      "Grade",
      "Variety",
      "SoakingTreatment",
      "SoakInQty",
      "SoakOutQty",
    ],
    limit: 100,
  }),

  freezingProductionWeight: makeWidget("ceo-freezing-production-weight", "kpi", "Freezing Production Weight", DS.freezingType, {
    measure: "ProductionWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  freezingRejectionWeight: makeWidget("ceo-freezing-rejection-weight", "kpi", "Freezing Rejection Weight", DS.freezingType, {
    measure: "RejectionWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  freezingByType: makeWidget("ceo-freezing-by-type", "bar", "Production by Freezing Type", DS.freezingType, {
    dimension: "FreezingType",
    measure: "ProductionWeight",
    aggregation: "sum",
    limit: 12,
  }),
  freezingByPlant: makeWidget("ceo-freezing-by-plant", "bar", "Freezing Production by Plant", DS.freezingType, {
    dimension: "Plant",
    measure: "ProductionWeight",
    aggregation: "sum",
    limit: 12,
  }),
  freezingTable: makeWidget("ceo-freezing-table", "table", "Freezing Processing Details", DS.freezingType, {
    selected_columns: [
      "FreezingRecordID",
      "TrackID",
      "ProductionDate",
      "Plant",
      "FreezingType",
      "Grade",
      "TypeOfMaterial",
      "Variety",
      "ShiftID",
      "StartTime",
      "EndTime",
      "ProductionWeight",
      "RejectionWeight",
    ],
    limit: 100,
  }),

  dailyActualMcs: makeWidget("ceo-daily-actual-mcs", "kpi", "Packed Production MCS", DS.dailyProduction, {
    measure: "ActualMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  dailyActualKgs: makeWidget("ceo-daily-actual-kgs", "kpi", "Packed Production KGS", DS.dailyProduction, {
    measure: "ActualKGS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  productionByPlantValidated: makeWidget("ceo-production-by-plant-validated", "bar", "Packed Production by Plant", DS.dailyProduction, {
    dimension: "Plant",
    measure: "ActualMCS",
    aggregation: "sum",
    limit: 12,
  }),
  productionByFreezingType: makeWidget("ceo-production-by-freezing-type", "bar", "Packed MCS by Freezing Type", DS.dailyProduction, {
    dimension: "FreezingType",
    measure: "ActualMCS",
    aggregation: "sum",
    limit: 12,
  }),
  dailyProductionTable: makeWidget("ceo-daily-production-table", "table", "Daily Production & Packing Details", DS.dailyProduction, {
    selected_columns: [
      "ProductionRecordID",
      "ProductionDate",
      "Plant",
      "Grade",
      "Variety",
      "TypeProduct",
      "FreezingType",
      "PackingCount",
      "PoID",
      "ProductionType",
      "ActualMCS",
      "AvailableMCS",
      "ActualKGS",
      "AvailableKGS",
    ],
    limit: 100,
  }),

  dummyMcs: makeWidget("ceo-dummy-mcs", "kpi", "Dummy Packed MCS", DS.packingType, {
    measure: "DummyMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  masterMcs: makeWidget("ceo-master-mcs", "kpi", "Master Packed MCS", DS.packingType, {
    measure: "MasterMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  packingTypeDistribution: makeWidget("ceo-packing-type-distribution", "donut", "Dummy vs Master Packing", DS.packingType, {
    dimension: "PackingType",
    measure: "ActualMCS",
    aggregation: "sum",
    limit: 6,
  }),
  dummyCartonUsageByPeriod: {
    id: "ceo-dummy-carton-usage-by-period",
    type: "ceo-dummy-carton-usage-by-period",
    title: "Dummy Carton Usage by Plant — Week / Month",
    dataset_id: DS.packingType,
    limit: 12,
    filters: [],
  },
  packingByPlant: makeWidget("ceo-packing-by-plant", "bar", "Packed MCS by Plant", DS.packingType, {
    dimension: "Plant",
    measure: "ActualMCS",
    aggregation: "sum",
    limit: 12,
  }),
  packingTable: makeWidget("ceo-packing-table", "table", "Dummy / Master Packing Details", DS.packingType, {
    selected_columns: [
      "ProductionRecordID",
      "ProductionDate",
      "PackingType",
      "Plant",
      "Grade",
      "Variety",
      "FreezingType",
      "PoID",
      "ActualMCS",
      "AvailableMCS",
      "ActualKGS",
      "DummyMCS",
      "MasterMCS",
    ],
    limit: 100,
  }),
  shipmentOutMcs: makeWidget("ceo-shipment-out-mcs", "kpi", "Shipment Out MCS", DS.shipmentOut, {
    measure: "OutMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),

  shipmentOutKgs: makeWidget("ceo-shipment-out-kgs", "kpi", "Shipment Out KGS", DS.shipmentOut, {
    measure: "OutActualKGS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),

  shipmentDispatchStatus: {
    id: "ceo-shipment-dispatch-status",
    type: "ceo-shipment-dispatch-status",
    title: "Shipment / Dispatch Operational Status",
    limit: 500,
    filters: [],
  },

  shipmentPendingRisk: {
    id: "ceo-shipment-pending-risk",
    type: "ceo-shipment-pending-risk",
    title: "Pending Shipments",
    limit: 1,
    filters: [],
  },

  onTimeDeliveries: {
    id: "ceo-on-time-deliveries",
    type: "ceo-on-time-deliveries",
    title: "On-time Deliveries",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  earlyShipments: {
    id: "ceo-early-shipments",
    type: "ceo-early-shipments",
    title: "Early Shipments",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  delayedShipments: {
    id: "ceo-delayed-shipments",
    type: "ceo-delayed-shipments",
    title: "Delayed Shipments",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  onWaterShipments: {
    id: "ceo-on-water-shipments",
    type: "ceo-on-water-shipments",
    title: "On-water Shipments",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  fdaReleaseHold: {
    id: "ceo-fda-release-hold",
    type: "ceo-fda-release-hold",
    title: "FDA Release / Hold",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  salesMcs: makeWidget("ceo-sales-mcs", "kpi", "Sales MCS", DS.sales, {
    measure: "MCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  salesInvoiceCount: makeWidget("ceo-sales-invoice-count", "kpi", "Invoices", DS.sales, {
    measure: "InvoiceID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  salesConsigneeCount: makeWidget("ceo-sales-consignee-count", "kpi", "Consignees", DS.sales, {
    measure: "ConsigneeID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  dashboardCustomerSales: makeWidget("ceo-dashboard-customer-sales", "bar", "Consignee / Buyer-wise Sales Value", DS.sales, {
    dimension: "ConsigneeName",
    display_dimension: "ConsigneeCode",
    number_format: { prefix: "$", decimals: 0 },
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
    orientation: "buyer-sales",
    axis_labels: {
      x: "Consignee",
      y: "Sales Value (USD)",
    },
  }),
  buyerSales: makeWidget("ceo-buyer-sales", "contribution-list", "Buyer Sales Contribution", DS.sales, {
    dimension: "BuyerName",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 100,
    exclude_unassigned: true,
    entity_label: "Buyer",
  }),
  consigneeSales: makeWidget("ceo-consignee-sales", "contribution-list", "Consignee Sales Contribution", DS.sales, {
    dimension: "ConsigneeName",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 120,
    exclude_unassigned: true,
    entity_label: "Consignee",
  }),
  destinationSales: makeWidget("ceo-destination-sales", "bar", "Destination-wise Sales", DS.sales, {
    dimension: "DestinationCountry",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
  }),
  gradeSales: makeWidget("ceo-grade-sales", "bar", "Grade-wise Sales Contribution", DS.sales, {
    dimension: "Grade",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
  }),
  brandSales: makeWidget("ceo-brand-sales", "bar", "Brand-wise Sales Value", DS.sales, {
    dimension: "BrandName",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 12,
  }),
  varietySales: makeWidget("ceo-variety-sales", "bar", "Variety-wise Sales Value", DS.sales, {
    dimension: "Variety",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 16,
  }),
  salesYearComparison: makeWidget("ceo-sales-year-comparison", "bar", "Year-wise Sales Value", DS.sales, {
    dimension: "SalesYear",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
    sort: { by: "dimension", dir: "asc" },
  }),
  salesAnnualContribution: makeWidget(
    "ceo-sales-annual-contribution",
    "sales-annual-contribution",
    "Annual Sales Contribution %",
    DS.sales,
    {
      dimension: "SalesYear",
      measure: "LineValueUSD",
      aggregation: "sum",
      limit: 10,
      sort: { by: "dimension", dir: "asc" },
      number_format: { prefix: "$", decimals: 0 },
    }
  ),
  salesTable: makeWidget("ceo-sales-table", "table", "Invoice / Customer / Sales Details", DS.sales, {
    selected_columns: [
      "InvoiceNumber",
      "InvoiceDate",
      "SalesYear",
      "PoNumber",
      "BuyerName",
      "BuyerSegment",
      "ConsigneeName",
      "BrandName",
      "Grade",
      "Variety",
      "PackingStyle",
      "MCS",
      "PriceNative",
      "Currency",
      "QtyUnit",
      "LineValueUSD",
      "DestinationCountry",
      "ContainerNo",
      "ShipmentDate",
      "ShippingBillNo",
      "ShippingBillDate"
    ],
    limit: 100,
  }),

  poBrand: makeWidget("ceo-po-brand", "bar", "PO Ordered MCS by Brand", DS.po, {
    dimension: "Brand",
    measure: "OrderedMCS",
    aggregation: "sum",
    limit: 10,
  }),
  poStatus: makeWidget("ceo-po-status", "donut", "PO Status Distribution", DS.po, {
    dimension: "PoStatus",
    measure: "PoID",
    aggregation: "count_distinct",
    limit: 8,
  }),
  poTable: makeWidget("ceo-po-table", "table", "PO Fulfilment Details", DS.po, {
    selected_columns: ["PoNumber", "PoDate", "PoStatus", "Grade", "Variety", "Brand", "DestinationCountry", "OrderedMCS", "RevisedMCS", "DispatchedMCS", "Price"],
    limit: 100,
  }),

  poFulfilmentRisk: {
    id: "ceo-po-fulfilment-risk",
    type: "ceo-po-fulfilment-risk",
    title: "Current Running PO Coverage & Risk",
    limit: 500,
    filters: [],
  },

  poCriticalRisk: {
    id: "ceo-po-critical-risk",
    type: "ceo-po-critical-risk",
    title: "Critical POs <50%",
    limit: 1,
    filters: [],
  },

  poWarningRisk: {
    id: "ceo-po-warning-risk",
    type: "ceo-po-warning-risk",
    title: "Warning POs 50–<90%",
    limit: 1,
    filters: [],
  },

  coldStoreStock: makeWidget("ceo-coldstore-stock", "bar", "Cold Store-wise Available Stock", DS.stock, {
    dimension: "ColdStore",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 10,
  }),
  gradeStock: makeWidget("ceo-grade-stock", "bar", "Grade-wise Available Stock", DS.stock, {
    dimension: "Grade",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 10,
  }),
  stockAgeing: makeWidget("ceo-stock-ageing", "line", "Stock by Production Date", DS.stock, {
    dimension: "ProductionDate",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 30,
  }),
  stockTable: makeWidget("ceo-stock-table", "table", "Stock Register", DS.stock, {
    selected_columns: ["Barcode", "ProductionDate", "ActualMCS", "AvailableMCS", "ActualKGS", "AvailableKGS", "Grade", "Variety", "PackingStyle", "ColdStore", "RackLocation", "PoID"],
    limit: 100,
  }),
  stockTransfersByPlant: makeWidget("ceo-stock-transfers-by-plant", "bar", "MCS Moved by Plant — Least to Highest", DS.movement, {
    dimension: "FromPlant",
    measure: "MovedMCS",
    aggregation: "sum",
    limit: 12,
    orientation: "vertical",
    sort: { dir: "asc" },
  }),

  dummyStockMcs: makeWidget("ceo-dummy-stock-mcs", "kpi", "PO-linked Stock MCS", DS.dummyStock, {
    measure: "AvailableMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  dummyStockKgs: makeWidget("ceo-dummy-stock-kgs", "kpi", "PO-linked Stock KGS", DS.dummyStock, {
    measure: "AvailableKGS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  poPendingMcsStore: makeWidget("ceo-store-po-pending-mcs", "kpi", "Linked PO Pending MCS", DS.dummyStock, {
    measure: "PoPendingMCS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  stockByPackingType: makeWidget("ceo-store-packing-type", "donut", "Stock by Packing Type", DS.dummyStock, {
    dimension: "PackingType",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 8,
  }),
  stockReadyLabAwaiting: makeWidget("ceo-stock-ready-lab-awaiting", "table", "Stock-ready Contracts — Lab Sample Awaiting", DS.dummyStock, {
    selected_columns: [
      "PoID",
      "PoNo",
      "ConsigneeID",
      "ConsigneeName",
      "Plant",
      "Grade",
      "AvailableMCS",
      "AvailableKGS",
      "LabSampleStatus",
    ],
    filters: [
      { field: "LabSampleStatus", operator: "eq", value: "Awaiting" },
    ],
    limit: 100,
  }),
  stockByContractState: makeWidget("ceo-store-contract-state", "donut", "Stock by Contract State", DS.dummyStock, {
    dimension: "ContractState",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 8,
  }),
  stockByPlant: makeWidget("ceo-store-plant", "bar", "PO-linked Stock by Plant", DS.dummyStock, {
    dimension: "Plant",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 12,
  }),
  stockByBuyer: makeWidget("ceo-store-buyer", "bar", "PO-linked Stock by Buyer", DS.dummyStock, {
    dimension: "BuyerName",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 15,
  }),
  stockByPo: makeWidget("ceo-store-po", "bar", "Stock by PO", DS.dummyStock, {
    dimension: "PoNo",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 15,
  }),
  poLinkedStockTable: makeWidget("ceo-po-linked-stock-table", "table", "PO / Contract-linked Stock Details", DS.dummyStock, {
    selected_columns: [
      "StockID",
      "ProductionDate",
      "PackingType",
      "Plant",
      "Grade",
      "PoID",
      "PoNo",
      "BuyerName",
      "ContractState",
      "AvailableMCS",
      "AvailableKGS",
      "PoPendingMCS",
    ],
    limit: 100,
  }),

  plantProduction: makeWidget("ceo-plant-production", "bar", "Plant-wise Received Qty", DS.preprocessing, {
    dimension: "ProductionPlant",
    measure: "ReceivedQty",
    aggregation: "sum",
    limit: 10,
  }),
  freezingVariety: makeWidget("ceo-freezing-variety", "bar", "Finished Variety-wise Freezing Qty", DS.preprocessing, {
    dimension: "FinishedVarieties",
    measure: "FreezingQty",
    aggregation: "sum",
    limit: 10,
  }),
  vaVariety: makeWidget("ceo-va-variety", "bar", "VA Variety-wise Qty", DS.preprocessing, {
    dimension: "VAVarieties",
    measure: "VAQty",
    aggregation: "sum",
    limit: 10,
  }),
  productionTable: makeWidget("ceo-production-table", "table", "Production / Preprocessing Details", DS.preprocessing, {
    selected_columns: ["ReceivingID", "ArrivalDate", "ArrivalCenter", "ProductionPlant", "TrackCode", "GradingVarieties", "VAVarieties", "FinishedVarieties", "SizeConversionPaths", "ReceivedQty", "DeheadingYieldPct", "VAYieldPct", "SoakingYieldPct", "FreezingYieldPct"],
    limit: 100,
  }),
  productionYearComparison: {
    id: "ceo-production-year-comparison",
    type: "ceo-production-year-comparison",
    title: "2–3 Year Capacity & Production Comparison",
    dataset_id: DS.preprocessing,
    limit: 3,
    filters: [],
  },

  shipmentByStore: makeWidget("ceo-shipment-store", "bar", "Shipment Out by Cold Store", DS.shipmentOut, {
    dimension: "ColdStore",
    measure: "OutMCS",
    aggregation: "sum",
    limit: 10,
  }),
  shipmentTrend: makeWidget("ceo-shipment-trend", "line", "Shipment Trend", DS.shipmentOut, {
    dimension: "ShipmentDate",
    measure: "OutMCS",
    aggregation: "sum",
    limit: 30,
    sort: { by: "dimension", dir: "desc" },
  }),
  salesTrend: makeWidget("ceo-sales-trend", "line", "Sales Value Trend", DS.sales, {
    dimension: "InvoiceDate",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 60,
    sort: { by: "dimension", dir: "desc" },
  }),
  poTrend: makeWidget("ceo-po-trend", "line", "PO Ordered MCS Trend", DS.po, {
    dimension: "PoDate",
    measure: "OrderedMCS",
    aggregation: "sum",
    limit: 60,
  }),
  productionTrend: makeWidget("ceo-production-trend", "line", "Production Received Qty Trend", DS.preprocessing, {
    dimension: "ArrivalDate",
    measure: "ReceivedQty",
    aggregation: "sum",
    limit: 60,
    sort: { by: "dimension", dir: "desc" },
  }),
  stockTrend: makeWidget("ceo-stock-trend", "line", "Available Stock Trend by Production Date", DS.stock, {
    dimension: "ProductionDate",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 60,
  }),
  priceComparisonOverYears: {
    id: "ceo-price-comparison-over-years",
    type: "ceo-price-comparison-over-years",
    title: "Price Comparison Over Years",
    dataset_id: DS.po,
    limit: 10,
    filters: [],
  },
  buyerOrderAnalysis: {
    id: "ceo-buyer-order-analysis",
    type: "ceo-buyer-order-analysis",
    title: "Buyer Total Orders vs Confirmed Orders",
    dataset_id: DS.po,
    limit: 10,
    filters: [],
  },

  shipmentTable: makeWidget("ceo-shipment-table", "table", "Shipment Dispatch Details", DS.shipmentOut, {
    selected_columns: [
      "ShipmentDate",
      "PoNo",
      "ConsigneeName",
      "DestinationCountry",
      "Grade",
      "BatchCode",
      "ColdStore",
      "RackLocation",
      "OutMCS",
      "OutActualKGS",
      "LooseBags"
    ],
    limit: 100,
  }),

  qcTotalTests: makeWidget("ceo-qc-total-tests", "kpi", "Total QC Tests", DS.qc, {
    measure: "QcRecordID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  qcClearTests: makeWidget("ceo-qc-clear-tests", "kpi", "Clear Tests", DS.qc, {
    measure: "TestClear",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  qcDetectedTests: makeWidget("ceo-qc-detected-tests", "kpi", "Antibiotic Detected / Failed", DS.qc, {
    measure: "AntibioticDetected",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  qcResultDistribution: makeWidget("ceo-qc-result-distribution", "donut", "QC Result Distribution", DS.qc, {
    dimension: "Result",
    measure: "QcRecordID",
    aggregation: "count_distinct",
    limit: 12,
  }),
  qcDetectedByPlant: makeWidget("ceo-qc-detected-by-plant", "bar", "Detected / Failed by Plant", DS.qc, {
    dimension: "Plant",
    measure: "AntibioticDetected",
    aggregation: "sum",
    limit: 12,
  }),
  qcDetectedByCentre: makeWidget("ceo-qc-detected-by-centre", "bar", "Detected / Failed by Centre", DS.qc, {
    dimension: "Centre",
    measure: "AntibioticDetected",
    aggregation: "sum",
    limit: 15,
  }),
  qcParameterVolume: makeWidget("ceo-qc-parameter-volume", "bar", "Parameter-wise Test Volume", DS.qc, {
    dimension: "Parameter",
    measure: "QcRecordID",
    aggregation: "count_distinct",
    limit: 15,
  }),
  qcExternalResult: makeWidget("ceo-qc-external-result", "donut", "External Lab Result Status", DS.qc, {
    dimension: "ExternalResult",
    measure: "QcRecordID",
    aggregation: "count_distinct",
    limit: 12,
  }),
  farmCertificationSummary: {
    id: "ceo-farm-certification-summary",
    type: "ceo-farm-audit-summary",
    title: "Farm Certification Summary",
    limit: 1,
    filters: [],
  },
  farmAuditSchedule: {
    id: "ceo-farm-audit-schedule",
    type: "ceo-farm-audit-schedule",
    title: "Plant Audit Schedule & Certificate Renewals",
    limit: 1,
    filters: [],
  },
  qcTable: makeWidget("ceo-qc-table", "table", "QC / Antibiotic Test Details", DS.qc, {
    selected_columns: [
      "QcRecordID",
      "AnalysisDate",
      "ReportDate",
      "LotNo",
      "Centre",
      "Plant",
      "Result",
      "Parameter",
      "Sulphite",
      "ExternalLab",
      "ExternalResult",
      "AbStatus",
      "TestedBy",
      "AntibioticDetected",
      "TestClear",
    ],
    limit: 100,
  }),
};


function ExecutiveAttentionQueue({ filters }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shipmentNeedsPeriod, setShipmentNeedsPeriod] = useState(false);
  const [attentionExpanded, setAttentionExpanded] = useState(false);

  const attentionRows = attentionExpanded ? rows : rows.slice(0, 5);

  const {
    dateFrom,
    dateTo,
    buyer,
    country,
    grade,
    variety,
    brand,
    coldStore,
    plant,
  } = filters || {};

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setShipmentNeedsPeriod(false);

      try {
        const filterContext = {
          dateFrom,
          dateTo,
          buyer,
          country,
          grade,
          variety,
          brand,
          coldStore,
          plant,
        };

        const qcWidget = {
          ...W.qcTable,
          limit: 50,
          filters: [
            ...(W.qcTable.filters || []),
            {
              field: "AntibioticDetected",
              op: "gt",
              value: 0,
            },
          ],
        };

        const requests = [
          api.post("/ai/ceo-chat/po-fulfilment-risk"),
          api.post(
            "/widget/data",
            {
              widget: qcWidget,
              filters: createCeoFilters(qcWidget, filterContext),
            },
            { timeout: 35000 }
          ),
        ];

        const hasShipmentPeriod = Boolean(dateFrom && dateTo);

        if (hasShipmentPeriod) {
          requests.push(
            api.post("/ai/ceo-chat/shipment-dispatch-status", {
              from_date: dateFrom,
              to_date: dateTo,
            })
          );
        }

        const results = await Promise.allSettled(requests);

        if (cancelled) return;

        const combined = [];

        // ----------------------------------------------------
        // Critical PO queue
        // ----------------------------------------------------
        const poResult =
          results[0].status === "fulfilled"
            ? results[0].value?.data ?? results[0].value
            : null;

        if (poResult?.ok && Array.isArray(poResult?.rows)) {
          poResult.rows
            .filter((row) => String(row?.RiskBand || "").toUpperCase() === "RED")
            .slice(0, 10)
            .forEach((row) => {
              combined.push({
                priority: "Critical",
                rank: 1,
                area: "PO",
                reference: row?.PoNo || row?.PoID || "—",
                issue: "PO coverage below 50%",
                context: [
                  row?.ConsigneeName,
                  row?.CountryName,
                  row?.CoveragePct != null ? `${row.CoveragePct}% coverage` : null,
                ].filter(Boolean).join(" · "),
                status: "Action Required",
              });
            });
        }

        // ----------------------------------------------------
        // QC detected / failed queue
        // ----------------------------------------------------
        const qcResult =
          results[1].status === "fulfilled"
            ? results[1].value?.data ?? results[1].value
            : null;

        const qcColumns = Array.isArray(qcResult?.columns) ? qcResult.columns : [];
        const qcRows = Array.isArray(qcResult?.rows) ? qcResult.rows : [];

        qcRows.slice(0, 10).forEach((raw) => {
          const row = {};
          qcColumns.forEach((column, index) => {
            row[column] = raw?.[index];
          });

          combined.push({
            priority: "Critical",
            rank: 1,
            area: "QA",
            reference: row?.LotNo || row?.QcRecordID || "—",
            issue: "Antibiotic detected / failed",
            context: [
              row?.Plant ? `Plant ${row.Plant}` : null,
              row?.Centre ? `Centre ${row.Centre}` : null,
              row?.Parameter,
              row?.Result || row?.AbStatus,
            ].filter(Boolean).join(" · "),
            status: "QA Attention",
          });
        });

        // ----------------------------------------------------
        // Shipment / dispatch queue
        // ----------------------------------------------------
        if (hasShipmentPeriod) {
          const shipmentResult =
            results[2]?.status === "fulfilled"
              ? results[2].value?.data ?? results[2].value
              : null;

          if (shipmentResult?.ok) {
            (shipmentResult.stockout_pending_rows || [])
              .slice(0, 8)
              .forEach((row) => {
                combined.push({
                  priority: "High",
                  rank: 2,
                  area: "Dispatch",
                  reference: row?.PoNo || row?.PoID || "—",
                  issue: "Stock-out recorded; Shipment Out pending",
                  context: [
                    row?.ConsigneeName,
                    row?.DestinationCountry,
                    row?.StockOutDate ? `Stock Out ${row.StockOutDate}` : null,
                  ].filter(Boolean).join(" · "),
                  status: "Dispatch Pending",
                });
              });

            (shipmentResult.pending_rows || [])
              .slice(0, 8)
              .forEach((row) => {
                combined.push({
                  priority: "Warning",
                  rank: 3,
                  area: "Shipment",
                  reference: row?.PoNo || row?.PoID || "—",
                  issue: "Shipment due; StockOutDate not recorded",
                  context: [
                    row?.ConsigneeName,
                    row?.DestinationCountry,
                    row?.ShipByDate ? `Ship By ${row.ShipByDate}` : null,
                  ].filter(Boolean).join(" · "),
                  status: "Pending Shipment",
                });
              });
          }
        } else {
          setShipmentNeedsPeriod(true);
        }

        combined.sort((a, b) => a.rank - b.rank);

        setRows(combined.slice(0, 20));

        const successfulSources = results.filter(
          (result) => result.status === "fulfilled"
        ).length;

        if (successfulSources === 0) {
          setError("Validated exception sources could not be loaded.");
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Unable to load executive attention queue."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    dateFrom,
    dateTo,
    buyer,
    country,
    grade,
    variety,
    brand,
    coldStore,
    plant,
  ]);

  const priorityStyle = (priority) => {
    if (priority === "Critical") {
      return { background: "#fee2e2", color: "#991b1b" };
    }
    if (priority === "High") {
      return { background: "#ffedd5", color: "#9a3412" };
    }
    return { background: "#fef3c7", color: "#92400e" };
  };

  return (
    <div className="ceo-attention-queue">
      <div className="ceo-attention-summary">
        <div>
          <strong>{rows.length}</strong>
          <span> priority records surfaced</span>
        </div>
        <div className="ceo-attention-legend">
          <span>Validated PO</span>
          <span>QA</span>
          <span>Dispatch</span>
          <span>Shipment</span>
        </div>
      </div>

      {shipmentNeedsPeriod && (
        <div className="ceo-attention-period-note">
          Shipment and dispatch exceptions are fail-closed until both From Date
          and To Date are selected. PO and QA exceptions remain available.
        </div>
      )}

      {loading ? (
        <div className="ceo-attention-state">Loading validated exception queues...</div>
      ) : error && rows.length === 0 ? (
        <div className="ceo-attention-state">{error}</div>
      ) : rows.length === 0 ? (
        <div className="ceo-attention-state">
          No validated executive exceptions found for the current filter context.
        </div>
      ) : (
        <div className="ceo-attention-table-wrap">
          <div className="flex justify-end pb-2">
            <button
              type="button"
              onClick={() =>
                downloadCsv(
                  "ceo-executive-attention.csv",
                  ["Priority", "Area", "Reference", "Issue", "Business Context", "Status"],
                  attentionRows.map((row) => [
                    row.priority,
                    row.area,
                    row.reference,
                    row.issue,
                    row.context || "—",
                    row.status,
                  ])
                )
              }
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
              style={{ borderColor: "var(--c-border)", background: "#fff" }}
              title="Download all executive attention rows as CSV"
            >
              Download CSV
            </button>
          </div>


          <table className="ceo-attention-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Area</th>
                <th>Reference</th>
                <th>Issue</th>
                <th>Business Context</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {attentionRows.map((row, index) => (
                <tr key={`${row.area}-${row.reference}-${index}`}>
                  <td>
                    <span
                      className="ceo-attention-priority"
                      style={priorityStyle(row.priority)}
                    >
                      {row.priority}
                    </span>
                  </td>
                  <td className="font-semibold">{row.area}</td>
                  <td className="font-semibold">{row.reference}</td>
                  <td>{row.issue}</td>
                  <td>{row.context || "—"}</td>
                  <td>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {rows.length > 5 && (
            <div className="flex justify-center py-3">
              <button
                type="button"
                onClick={() => setAttentionExpanded((current) => !current)}
                className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                style={{
                  borderColor: "var(--c-border)",
                  background: "#fff",
                }}
              >
                {attentionExpanded
                  ? "Collapse"
                  : `Expand · Show all ${rows.length.toLocaleString()} rows`}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="ceo-attention-footnote">
        The queue surfaces only validated ERP exceptions. RM Requirement Gap is
        intentionally excluded while business validation is pending.
      </div>
    </div>
  );
}


function SalesAnnualContribution({ widget, filters, onDrillDown }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const queryWidget = {
          ...widget,
          type: "bar",
          title: "Year-wise Sales Value",
        };

        const response = await api.post(
          "/widget/data",
          {
            widget: queryWidget,
            filters: createCeoFilters(queryWidget, filters || {}),
          },
          {
            signal: controller.signal,
            timeout: 35000,
          }
        );

        if (!cancelled) {
          setData(response?.data ?? null);
        }
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Unable to calculate annual Sales contribution."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch (_) {
        // noop
      }
    };
  }, [widget, filters]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center gap-2 text-sm text-slate-500">
        <Loader2 size={17} className="animate-spin" />
        Loading annual contribution...
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center px-4 text-center text-sm text-red-700">
        {error}
      </div>
    );
  }

  const rawRows = Array.isArray(data?.rows) ? data.rows : [];

  const mappedRows = rawRows
    .filter((row) => {
      const label = row?.label;
      return (
        label !== null &&
        label !== undefined &&
        String(label).trim() !== "" &&
        String(label).trim() !== "—" &&
        String(label).trim().toLowerCase() !== "unassigned"
      );
    })
    .map((row) => ({
      year: String(row.label),
      salesValue: Number(row.value || 0),
    }))
    .filter((row) => Number.isFinite(row.salesValue));

  const unresolvedValue = rawRows
    .filter((row) => {
      const label = row?.label;
      return (
        label === null ||
        label === undefined ||
        String(label).trim() === "" ||
        String(label).trim() === "—" ||
        String(label).trim().toLowerCase() === "unassigned"
      );
    })
    .reduce((sum, row) => sum + Number(row?.value || 0), 0);

  const mappedTotal = mappedRows.reduce(
    (sum, row) => sum + row.salesValue,
    0
  );

  const rows = mappedRows.map((row) => ({
    ...row,
    contributionPct:
      mappedTotal > 0 ? (row.salesValue / mappedTotal) * 100 : 0,
  }));

  if (!rows.length) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-500">
        No mapped annual Sales data for the selected filters.
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: 8, right: 18, left: 0, bottom: 8 }}
          >
            <CartesianGrid
              strokeDasharray="2 3"
              stroke="#eef2f7"
              vertical={false}
            />

            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: "#374151" }}
            />

            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10.5, fill: "#6b7280" }}
              tickFormatter={(value) => `${Number(value).toFixed(0)}%`}
              width={48}
            />

            <Tooltip
              formatter={(value, name, entry) => {
                const row = entry?.payload || {};
                return [
                  `${Number(value || 0).toFixed(2)}% · $${Number(
                    row.salesValue || 0
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
                  "Contribution",
                ];
              }}
              labelFormatter={(label) => `Sales Year ${label}`}
            />

            <Bar
              dataKey="contributionPct"
              name="Annual Contribution %"
              fill="#2563eb"
              radius={[4, 4, 0, 0]}
              cursor={onDrillDown ? "pointer" : "default"}
              onClick={(entry) => {
                if (!onDrillDown) return;

                const row = entry?.payload || entry;
                if (!row?.year) return;

                onDrillDown({
                  widget: {
                    ...widget,
                    type: "bar",
                  },
                  point: {
                    label: row.year,
                    value: row.salesValue,
                  },
                });
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div
        className="text-[11px] leading-5 mt-2"
        style={{ color: "var(--c-text-muted)" }}
      >
        Contribution is calculated from validated mapped-year Sales within the
        currently selected Sales filters.
        {unresolvedValue > 0
          ? ` $${unresolvedValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} remains outside the percentage denominator because its Sales Year is unresolved.`
          : ""}
      </div>
    </div>
  );
}

function WidgetCard({ widget, filters, compact = false, dummyCartonPeriod, setDummyCartonPeriod }) {
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");
  const [soakingExpanded, setSoakingExpanded] = useState(false);
  const [poCoverageExpanded, setPoCoverageExpanded] = useState(false);
  const openWidgetDrillDown = async ({ widget: sourceWidget, point }) => {
    if (!sourceWidget || sourceWidget.type === "text") return;

    const dim = sourceWidget.group_by || sourceWidget.dimension;
    const clickedLabel = point?.label ?? point?.name;
    const mergedFilters = [...createCeoFilters(sourceWidget, filters || {})];

    if (
      dim &&
      clickedLabel !== undefined &&
      clickedLabel !== null &&
      clickedLabel !== "" &&
      clickedLabel !== "Unassigned" &&
      clickedLabel !== "Others"
    ) {
      mergedFilters.push({
        field: dim,
        op: "in",
        value: [clickedLabel],
      });
    }

    const tableWidget = {
      ...sourceWidget,
      type: "table",
      selected_columns: undefined,
      limit: 2000,
    };

    setDrill({
      title: sourceWidget.title || sourceWidget.type,
      dimension: dim,
      clickedLabel,
      metricField: sourceWidget.measure || "",
      metricValue: point?.value,
      aggregation: sourceWidget.aggregation || "sum",
      payload: {
        widget: tableWidget,
        filters: mergedFilters,
      },
    });
    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);

    try {
      const r = await api.post(
        "/widget/data",
        { widget: tableWidget, filters: mergedFilters },
        { timeout: 35000 }
      );
      setDrillData(r.data);
    } catch (e) {
      setDrillError(e?.response?.data?.detail || e.message || "Unable to load detailed data.");
    } finally {
      setDrillLoading(false);
    }
  };
  const openProcessStage = async (stage, row) => {
    const trackId = row?.ReceivingID;
    if (!trackId) return;

    const stageColumns = {
      grading: ["ReceivingID", "TrackCode", "GradingVarieties"],
      va: ["ReceivingID", "TrackCode", "VAVarieties"],
      finished: ["ReceivingID", "TrackCode", "FinishedVarieties", "FreezingQty"],
      conversion: ["ReceivingID", "TrackCode", "SizeConversionPaths"],
    };

    const stageLabels = {
      grading: "Grading Details",
      va: "Value Addition Details",
      finished: "Finished Product Details",
      conversion: "Head-on → Finished Product Size Conversion",
    };

    const sourceWidget = widget?.dataset_id === DS.preprocessing ? widget : null;
    if (!sourceWidget) return;

    const tableWidget = {
      ...sourceWidget,
      type: "table",
      selected_columns: stageColumns[stage],
      limit: 2000,
    };

    const processFilters = [{
      field: "ReceivingID",
      op: "in",
      value: [trackId],
    }];

    setDrill({
      title: `${stageLabels[stage]} — ${row?.TrackCode || trackId}`,
      dimension: "ReceivingID",
      clickedLabel: String(trackId),
      metricField: "",
      metricValue: null,
      aggregation: "sum",
      payload: {
        widget: tableWidget,
        filters: processFilters,
      },
    });

    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);

    try {
      const r = await api.post(
        "/widget/data",
        { widget: tableWidget, filters: processFilters },
        { timeout: 35000 }
      );
      setDrillData(r.data);
    } catch (e) {
      setDrillError(
        e?.response?.data?.detail ||
          e.message ||
          "Unable to load process stage details."
      );
    } finally {
      setDrillLoading(false);
    }
  };

  const [specialData, setSpecialData] = useState(null);
  const [specialLoading, setSpecialLoading] = useState(false);
  const [dummyCartonYear, setDummyCartonYear] = useState("");
  useEffect(() => { if (widget.type === "ceo-dummy-carton-usage-by-period" && specialData?.rows?.length) { const years = [...new Set(specialData.rows.filter((row) => row.period_type === "month").map((row) => String(row.period).slice(0, 4)).filter((year) => /^20\d{2}$/.test(year)))].sort().reverse(); if (years.length && (!dummyCartonYear || !years.includes(dummyCartonYear))) setDummyCartonYear(years[0]); } }, [widget.type, specialData, dummyCartonYear]);

  const shipmentPromptnessStatus =
    widget.type === "ceo-early-shipments"
      ? "early"
      : widget.type === "ceo-delayed-shipments"
        ? "delayed"
        : widget.type === "ceo-on-time-deliveries"
          ? "on time"
          : null;

  const isOnWaterShipmentKpi =
    widget.type === "ceo-on-water-shipments";

  const isFdaReleaseHoldKpi =
    widget.type === "ceo-fda-release-hold";

  const isPaymentsReceivedPendingKpi =
    widget.type === "ceo-payments-received-pending";

  const openShipmentPromptnessKpi = () => {
    if (!shipmentPromptnessStatus) return;

    window.dispatchEvent(
      new CustomEvent("ceo-shipment-promptness-drill", {
        detail: {
          status: shipmentPromptnessStatus,
          title: widget.title,
        },
      })
    );
  };

  const openOnWaterShipmentKpi = () => {
    if (!isOnWaterShipmentKpi) return;

    window.dispatchEvent(
      new CustomEvent("ceo-shipment-on-water-drill", {
        detail: {
          title: widget.title,
        },
      })
    );
  };

  const shipmentSpecialClickable =
    Boolean(shipmentPromptnessStatus) ||
    isOnWaterShipmentKpi ||
    isFdaReleaseHoldKpi ||
    isPaymentsReceivedPendingKpi;

  const openShipmentSpecialKpi = () => {
    if (isPaymentsReceivedPendingKpi) {
      window.dispatchEvent(
        new CustomEvent("ceo-payments-received-pending-drill", {
          detail: { title: widget.title },
        })
      );
      return;
    }

    if (isFdaReleaseHoldKpi) {
      window.dispatchEvent(
        new CustomEvent("ceo-fda-release-hold-drill", {
          detail: { title: widget.title },
        })
      );
      return;
    }

    if (isOnWaterShipmentKpi) {
      openOnWaterShipmentKpi();
      return;
    }

    openShipmentPromptnessKpi();
  };

  useEffect(() => {
    const specialTypes = [
      "ceo-on-time-deliveries",
      "ceo-early-shipments",
      "ceo-delayed-shipments",
      "ceo-on-water-shipments",
      "ceo-fda-release-hold",
      "ceo-price-comparison-over-years",
      "ceo-buyer-order-analysis",
      "ceo-commercial-pending-shipments","ceo-payments-received-pending","ceo-antidumping",
      "ceo-annual-country-contribution",
      "ceo-production-year-comparison",
      "ceo-rm-requirement-gap",
      "ceo-client-closure-status",
      "ceo-va-percent-by-plant",
      "ceo-soaking-pickup-item-plant",
      "ceo-po-fulfilment-risk",
      "ceo-po-critical-risk",
      "ceo-po-warning-risk",
      "ceo-shipment-dispatch-status",
      "ceo-shipment-pending-risk",
      "ceo-dummy-carton-usage-by-period",
      "ceo-farm-audit-summary",
      "ceo-farm-audit-schedule",
    ];

    if (!specialTypes.includes(widget.type)) return;

    let cancelled = false;

    const load = async () => {
      try {
        setSpecialLoading(true);

        const endpoint =
          [
            "ceo-on-time-deliveries",
            "ceo-early-shipments",
            "ceo-delayed-shipments",
            "ceo-on-water-shipments",
          ].includes(widget.type)
            ? "/ai/ceo-chat/shipment-performance"
            : widget.type === "ceo-fda-release-hold"
                ? "/ai/ceo-chat/fda-release-hold"
                : widget.type === "ceo-price-comparison-over-years"
                  ? "/ai/ceo-chat/price-comparison-over-years"
                  : widget.type === "ceo-buyer-order-analysis"
                    ? "/ai/ceo-chat/buyer-order-analysis"
                    : widget.type === "ceo-commercial-pending-shipments"
                      ? "/ai/ceo-chat/commercial-pending-shipments"
                    : widget.type === "ceo-payments-received-pending"
                      ? "/ai/ceo-chat/payments-received-pending"
                    : widget.type === "ceo-antidumping"
                      ? "/ai/ceo-chat/antidumping"
                    : widget.type === "ceo-annual-country-contribution"
                      ? "/ai/ceo-chat/annual-country-contribution"
                    : widget.type === "ceo-production-year-comparison"
                      ? "/ai/ceo-chat/capacity-comparison"
                    : widget.type === "ceo-dummy-carton-usage-by-period"
                      ? "/ai/ceo-chat/dummy-carton-usage-by-period"
                    : widget.type === "ceo-rm-requirement-gap"
                      ? "/ai/ceo-chat/rm-requirement-gap"
                    : widget.type === "ceo-client-closure-status"
                      ? widget.endpoint
                    : widget.type === "ceo-farm-audit-summary"
                      ? "/ai/ceo-chat/farm-audit-summary"
                    : widget.type === "ceo-farm-audit-schedule"
                      ? "/ai/ceo-chat/farm-audit-schedule"

                    : widget.type === "ceo-va-percent-by-plant"
                      ? "/ai/ceo-chat/va-percent-by-plant"

                    : widget.type === "ceo-soaking-pickup-item-plant"
                      ? "/ai/ceo-chat/soaking-pickup-item-plant"
                    : ["ceo-po-fulfilment-risk", "ceo-po-critical-risk", "ceo-po-warning-risk"].includes(widget.type)
                      ? "/ai/ceo-chat/po-fulfilment-risk"
                    : ["ceo-shipment-dispatch-status", "ceo-shipment-pending-risk"].includes(widget.type)
                      ? "/ai/ceo-chat/shipment-dispatch-status"
                      : "/ai/ceo-chat/on-time-deliveries";

        if (
          ["ceo-shipment-dispatch-status", "ceo-shipment-pending-risk"].includes(widget.type) &&
          (!filters?.dateFrom || !filters?.dateTo)
        ) {
          if (!cancelled) {
            setSpecialData({
              ok: false,
              needs_date_range: true,
              message: "Select both From Date and To Date in CEO Global Filters to load Shipment / Dispatch operational stages.",
            });
          }
          return;
        }

        const payload =
          [
            "ceo-on-time-deliveries",
            "ceo-early-shipments",
            "ceo-delayed-shipments",
            "ceo-on-water-shipments",
            "ceo-fda-release-hold",
            "ceo-payments-received-pending",
            "ceo-soaking-pickup-item-plant",
            "ceo-shipment-dispatch-status",
            "ceo-shipment-pending-risk",
          ].includes(widget.type)
            ? {
                from_date: filters?.dateFrom || undefined,
                to_date: filters?.dateTo || undefined,
              }
            : undefined;

        const response = await api.post(endpoint, payload);
        console.log("CEO SPECIAL API:", widget.type, response);
        const data = response?.data ?? response;
        console.log("CEO SPECIAL DATA:", widget.type, data);

        if (!cancelled) {
          setSpecialData(data);
        }
      } catch (error) {
        console.error(`Failed to load ${widget.title}:`, error);

        if (!cancelled) {
          setSpecialData(null);
        }
      } finally {
        if (!cancelled) {
          setSpecialLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [widget.type, widget.title, filters?.dateFrom, filters?.dateTo]);

  const isSpecial =
    widget.type === "ceo-on-time-deliveries" ||
    widget.type === "ceo-early-shipments" ||
    widget.type === "ceo-delayed-shipments" ||
    widget.type === "ceo-on-water-shipments" ||
    widget.type === "ceo-fda-release-hold" ||
    widget.type === "ceo-price-comparison-over-years" ||
    widget.type === "ceo-buyer-order-analysis" ||
    widget.type === "ceo-commercial-pending-shipments" ||
    widget.type === "ceo-payments-received-pending" ||
    widget.type === "ceo-antidumping" ||
    widget.type === "ceo-annual-country-contribution" ||
    widget.type === "ceo-production-year-comparison" ||
    widget.type === "ceo-rm-requirement-gap" ||
    widget.type === "ceo-client-closure-status" ||
    widget.type === "ceo-va-percent-by-plant" ||
    widget.type === "ceo-soaking-pickup-item-plant" ||
    widget.type === "ceo-po-fulfilment-risk" ||
    widget.type === "ceo-po-critical-risk" ||
    widget.type === "ceo-po-warning-risk" ||
    widget.type === "ceo-shipment-dispatch-status" ||
    widget.type === "ceo-shipment-pending-risk" ||
    widget.type === "ceo-dummy-carton-usage-by-period" ||
    widget.type === "ceo-farm-audit-summary" ||
    widget.type === "ceo-farm-audit-schedule";

  const [tableExportData, setTableExportData] = useState(null);

  return (
    <div className={`ceo-live-widget ${compact ? "ceo-process-stage-card" : ""}`} data-analytics-target={widget.id}>
      <div className={`ceo-live-widget-head ${compact ? "ceo-process-stage-head" : ""}`}>
        <div className="ceo-live-widget-title">{widget.title}</div>
        <FavoriteButton target={widget.id} />
        {widget.type === "table" &&
        tableExportData?.columns?.length &&
        tableExportData?.rows?.length ? (
          <button
            type="button"
            onClick={() =>
              downloadCsv(
                "ceo-command-center-table.csv",
                tableExportData.columns,
                tableExportData.rows
              )
            }
            className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold transition hover:bg-slate-50"
            style={{
              borderColor: "var(--c-border)",
              color: "var(--c-text)",
              background: "#fff",
            }}
            title="Download all table rows as CSV"
          >
            Download CSV
          </button>
        ) : null}
      </div>

      <div
        className={`ceo-live-widget-body ${
          ["bar", "line", "pie", "donut"].includes(widget.type)
            ? "ceo-chart-widget-body"
            : ""
        }`}
      >
        {widget.type === "ceo-po-critical-risk" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">Loading current PO risk...</div>
          ) : specialData?.ok && specialData?.summary ? (
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#991b1b" }}>
                  Critical Running POs
                </div>
                <div className="text-3xl font-semibold mt-2" style={{ color: "#b91c1c" }}>
                  {specialData.summary.red_pos ?? 0}
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Coverage below 50% · {specialData.summary.running_pos ?? 0} running POs
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">PO risk could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-po-warning-risk" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">Loading current PO risk...</div>
          ) : specialData?.ok && specialData?.summary ? (
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#92400e" }}>
                  Warning Running POs
                </div>
                <div className="text-3xl font-semibold mt-2" style={{ color: "#b45309" }}>
                  {specialData.summary.warning_pos ?? 0}
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Coverage 50–&lt;90% · {specialData.summary.green_pos ?? 0} ready at ≥90%
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">PO risk could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-shipment-pending-risk" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">Loading pending shipments...</div>
          ) : specialData?.needs_date_range ? (
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#92400e" }}>
                  Pending Shipments
                </div>
                <div className="text-2xl font-semibold mt-2" style={{ color: "#92400e" }}>
                  Select Period
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Choose From Date and To Date to load the validated dispatch queue.
              </div>
            </div>
          ) : specialData?.ok && specialData?.summary ? (
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#92400e" }}>
                  Pending Shipments
                </div>
                <div className="text-3xl font-semibold mt-2" style={{ color: "#b45309" }}>
                  {specialData.summary.pending_shipments ?? 0}
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Ship-by date in selected period with no StockOutDate recorded
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Shipment status could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-shipment-dispatch-status" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading Shipment / Dispatch operational status...
            </div>
          ) : specialData?.needs_date_range ? (
            <div
              className="rounded-xl border p-4"
              style={{
                background: "#fffbeb",
                borderColor: "#fde68a",
              }}
            >
              <div className="text-[13px] font-semibold" style={{ color: "#92400e" }}>
                Date range required
              </div>
              <div className="text-[12px] mt-1" style={{ color: "#78350f" }}>
                {specialData.message}
              </div>
            </div>
          ) : specialData?.ok && specialData?.summary ? (
            <div className="space-y-5">

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    style={{ background: "#dcfce7", color: "#166534" }}
                  >
                    ERP Workflow Validated
                  </span>

                  <span
                    className="text-[11.5px]"
                    style={{ color: "var(--c-text-muted)" }}
                  >
                    {specialData.date_range?.from_date || "—"}
                    {" → "}
                    {specialData.date_range?.to_date || "—"}
                  </span>
                </div>

                <span
                  className="text-[11px]"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  Executive stage classification does not use raw OutStatus codes
                </span>
              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">

                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#fde68a", background: "#fffbeb" }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{ color: "#92400e" }}
                  >
                    Pending Shipments
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.pending_shipments ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "#78350f" }}
                  >
                    Ship-by date is in the selected period but StockOutDate is not recorded.
                  </div>
                </div>


                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#bfdbfe", background: "#eff6ff" }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{ color: "#1d4ed8" }}
                  >
                    Stock-out Recorded
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.stockout_candidates ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "#1e40af" }}
                  >
                    POs with ScrmCustomInvoices.StockOutDate inside the selected period.
                  </div>
                </div>


                <div
                  className="rounded-xl border p-4"
                  style={{
                    borderColor:
                      Number(specialData.summary.stockout_pending || 0) > 0
                        ? "#fecaca"
                        : "#bbf7d0",
                    background:
                      Number(specialData.summary.stockout_pending || 0) > 0
                        ? "#fef2f2"
                        : "#f0fdf4",
                  }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{
                      color:
                        Number(specialData.summary.stockout_pending || 0) > 0
                          ? "#991b1b"
                          : "#166534",
                    }}
                  >
                    Stock-out Pending
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.stockout_pending ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "var(--c-text-muted)" }}
                  >
                    Stock-out recorded but final Shipment Out transaction is not yet present.
                  </div>
                </div>


                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#bbf7d0", background: "#f0fdf4" }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{ color: "#166534" }}
                  >
                    Shipment Completed
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.completed_shipments ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "#166534" }}
                  >
                    Shipment Out transaction exists with an actual OutDate.
                  </div>
                </div>

              </div>


              <div
                className="rounded-xl border p-4"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="text-[12px] font-semibold mb-3">
                  ERP Shipment Workflow
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center gap-2">

                  {[
                    {
                      title: "1. Shipment Due",
                      text: "PO ShipByDate",
                    },
                    {
                      title: "2. Pending Shipment",
                      text: "No StockOutDate",
                    },
                    {
                      title: "3. Stock Out",
                      text: "StockOutDate recorded",
                    },
                    {
                      title: "4. Dispatch Completion",
                      text: "OutType = Shipment Out",
                    },
                  ].map((stage, index, arr) => (
                    <React.Fragment key={stage.title}>
                      <div
                        className="flex-1 rounded-lg border px-3 py-3"
                        style={{
                          borderColor: "var(--c-border)",
                          background: "#f8fafc",
                        }}
                      >
                        <div className="text-[11.5px] font-semibold">
                          {stage.title}
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          {stage.text}
                        </div>
                      </div>

                      {index < arr.length - 1 && (
                        <div
                          className="hidden lg:block text-lg"
                          style={{ color: "#94a3b8" }}
                        >
                          →
                        </div>
                      )}
                    </React.Fragment>
                  ))}

                </div>
              </div>


              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

                <div
                  className="rounded-xl border overflow-hidden"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div
                    className="px-4 py-3 border-b"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-semibold">
                          Pending Shipment Queue
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          POs due in the selected period without a recorded StockOutDate.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          downloadCsv(
                            "ceo-pending-shipment-queue.csv",
                            ["PO", "PO Date", "Ship By", "Consignee", "Destination"],
                            (specialData.pending_rows || []).map((row) => [
                              row.PoNo || row.PoID,
                              row.PoDate || "—",
                              row.ShipByDate || "—",
                              row.ConsigneeName || "—",
                              row.DestinationCountry || "—",
                            ])
                          )
                        }

                        className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                        style={{ borderColor: "var(--c-border)", background: "#fff" }}
                        title="Download all pending shipment rows as CSV"
                      >
                        Download CSV
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-h-[360px]">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-white">



                        <tr
                          className="border-b"
                          style={{ borderColor: "var(--c-border)" }}
                        >
                          {[
                            "PO",
                            "PO Date",
                            "Ship By",
                            "Consignee",
                            "Destination",
                          ].map((h) => (
                            <th
                              key={h}
                              className="text-left px-3 py-2 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {(specialData.pending_rows || []).slice(0, 100).map((row) => (
                          <tr
                            key={row.PoID}
                            className="border-b"
                            style={{ borderColor: "var(--c-border)" }}
                          >
                            <td className="px-3 py-2 whitespace-nowrap font-medium">
                              {row.PoNo || row.PoID}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.PoDate || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.ShipByDate || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.ConsigneeName || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.DestinationCountry || "—"}
                            </td>
                          </tr>
                        ))}

                        {(specialData.pending_rows || []).length === 0 && (
                          <tr>
                            <td
                              colSpan="5"
                              className="px-4 py-8 text-center"
                              style={{ color: "var(--c-text-muted)" }}
                            >
                              No pending shipment POs found for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>


                <div
                  className="rounded-xl border overflow-hidden"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div
                    className="px-4 py-3 border-b"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-semibold">
                          Stock-out Exception Queue
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          StockOutDate exists, but Shipment Out completion has not been recorded.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          downloadCsv(
                            "ceo-stockout-exception-queue.csv",
                            ["PO", "Ship By", "Stock Out", "Consignee", "Destination"],
                            (specialData.stockout_pending_rows || []).map((row) => [
                              row.PoNo || row.PoID,
                              row.ShipByDate || "—",
                              row.StockOutDate || "—",
                              row.ConsigneeName || "—",
                              row.DestinationCountry || "—",
                            ])
                          )
                        }

                        className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                        style={{ borderColor: "var(--c-border)", background: "#fff" }}
                        title="Download all stock-out exception rows as CSV"
                      >
                        Download CSV
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-h-[360px]">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-white">
                        <tr
                          className="border-b"
                          style={{ borderColor: "var(--c-border)" }}
                        >
                          {[
                            "PO",
                            "Ship By",
                            "Stock Out",
                            "Consignee",
                            "Destination",
                          ].map((h) => (
                            <th
                              key={h}
                              className="text-left px-3 py-2 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {(specialData.stockout_pending_rows || [])
                          .slice(0, 100)
                          .map((row) => (
                            <tr
                              key={row.PoID}
                              className="border-b"
                              style={{ borderColor: "var(--c-border)" }}
                            >
                              <td className="px-3 py-2 whitespace-nowrap font-medium">
                                {row.PoNo || row.PoID}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.ShipByDate || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.StockOutDate || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.ConsigneeName || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.DestinationCountry || "—"}
                              </td>
                            </tr>
                          ))}

                        {(specialData.stockout_pending_rows || []).length === 0 && (
                          <tr>
                            <td
                              colSpan="5"
                              className="px-4 py-8 text-center"
                              style={{ color: "var(--c-text-muted)" }}
                            >
                              No stock-out exceptions found for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>


              <div
                className="rounded-xl border p-4"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="text-[12px] font-semibold mb-2">
                  Calculation Lineage
                </div>

                <div
                  className="text-[11px] leading-5"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  Pending Shipment uses <strong>ScrmPoData.ShipByDate</strong>.
                  {" "}Stock-out Recorded, Stock-out Pending and Shipment Completed
                  use <strong>ScrmCustomInvoices.StockOutDate</strong>.
                  {" "}Shipment completion requires
                  <strong> CsStockOutDetails.OutType = "Shipment Out"</strong>
                  with a nonblank OutDate.
                </div>
              </div>

            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Shipment / Dispatch operational status could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-po-fulfilment-risk" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading current running PO coverage...
            </div>
          ) : specialData?.ok && specialData?.summary ? (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{ background: "#dcfce7", color: "#166534" }}
                >
                  Live ERP Validated
                </span>
                <span
                  className="text-[11.5px]"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  Current running orders · Global dashboard filters are not applied to this queue
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  ["Running POs", specialData.summary.running_pos],
                  ["Green ≥90%", specialData.summary.green_pos],
                  ["Warning 50–89%", specialData.summary.warning_pos],
                  ["Red <50%", specialData.summary.red_pos],
                  ["Avg Coverage", `${specialData.summary.avg_coverage_pct ?? 0}%`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border p-3"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div
                      className="text-[10.5px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      {label}
                    </div>
                    <div className="text-xl font-semibold mt-1">
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div className="text-[12px] font-semibold mb-3">
                    MASTER Coverage
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.master_green}</div>
                      <div className="text-[10.5px] text-slate-500">Green</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.master_warning}</div>
                      <div className="text-[10.5px] text-slate-500">Warning</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.master_red}</div>
                      <div className="text-[10.5px] text-slate-500">Red</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.avg_master_pct}%</div>
                      <div className="text-[10.5px] text-slate-500">Average</div>
                    </div>
                  </div>
                </div>

                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div className="text-[12px] font-semibold mb-3">
                    DUMMY Coverage
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.dummy_green}</div>
                      <div className="text-[10.5px] text-slate-500">Green</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.dummy_warning}</div>
                      <div className="text-[10.5px] text-slate-500">Warning</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.dummy_red}</div>
                      <div className="text-[10.5px] text-slate-500">Red</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.avg_dummy_pct}%</div>
                      <div className="text-[10.5px] text-slate-500">Average</div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="rounded-xl border overflow-hidden"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="px-4 py-3 border-b" style={{ borderColor: "var(--c-border)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[12px] font-semibold">
                        Lowest Coverage / Highest Risk Running POs
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                        Ordered MCS compared with available MCS-equivalent stock. Coverage may exceed 100% because the ERP does not cap over-coverage.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        downloadCsv(
                          "ceo-running-po-coverage-risk.csv",
                          [
                            "PO",
                            "PO Date",
                            "Consignee",
                            "Country",
                            "Ordered MCS",
                            "Available",
                            "Coverage",
                            "Master %",
                            "Dummy %",
                            "Risk",
                          ],
                          (specialData.rows || []).map((row) => [
                            row.PoNo || row.PoID,
                            row.PoDate || "—",
                            row.ConsigneeName || "—",
                            row.CountryName || "—",
                            Number(row.OrderedMCS || 0).toLocaleString(),
                            Number(row.AvailableMCSEquivalent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
                            `${row.CoveragePct}%`,
                            `${row.MasterCoveragePct}%`,
                            `${row.DummyCoveragePct}%`,
                            row.RiskBand || "—",
                          ])
                        )
                      }
                      className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                      style={{ borderColor: "var(--c-border)", background: "#fff" }}
                      title="Download all running PO coverage rows as CSV"
                    >
                      Download CSV
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-[11.5px]">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "var(--c-border)" }}>
                        {[
                          "PO",
                          "PO Date",
                          "Consignee",
                          "Country",
                          "Ordered MCS",
                          "Available",
                          "Coverage",
                          "Master %",
                          "Dummy %",
                          "Risk",
                        ].map((h) => (
                          <th key={h} className="text-left px-3 py-2 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(poCoverageExpanded
                        ? (specialData.rows || [])
                        : (specialData.rows || []).slice(0, 5)
                      ).map((row) => (
                        <tr
                          key={row.PoID}
                          className="border-b"
                          style={{ borderColor: "var(--c-border)" }}
                        >
                          <td className="px-3 py-2 whitespace-nowrap font-medium">
                            {row.PoNo || row.PoID}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.PoDate || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.ConsigneeName || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.CountryName || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{Number(row.OrderedMCS || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{Number(row.AvailableMCSEquivalent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-semibold">{row.CoveragePct}%</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.MasterCoveragePct}%</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.DummyCoveragePct}%</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className="px-2 py-1 rounded-full text-[10.5px] font-semibold"
                              style={{
                                background:
                                  row.RiskBand === "GREEN"
                                    ? "#dcfce7"
                                    : row.RiskBand === "WARNING"
                                      ? "#fef3c7"
                                      : "#fee2e2",
                                color:
                                  row.RiskBand === "GREEN"
                                    ? "#166534"
                                    : row.RiskBand === "WARNING"
                                      ? "#92400e"
                                      : "#991b1b",
                              }}
                            >
                              {row.RiskBand}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {(specialData.rows || []).length > 5 && (
                    <div className="flex justify-center py-3">
                      <button
                        type="button"
                        onClick={() => setPoCoverageExpanded((current) => !current)}
                        className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                        style={{
                          borderColor: "var(--c-border)",
                          background: "#fff",
                        }}
                      >
                        {poCoverageExpanded
                          ? "Collapse"
                          : `Expand · Show all ${(specialData.rows || []).length.toLocaleString()} rows`}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="rounded-xl border p-4"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="text-[12px] font-semibold mb-2">
                  ERP Formula & Risk Definition
                </div>
                <div
                  className="text-[11.5px] leading-5"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  {specialData.formula}
                  {" "}Green {specialData.risk_thresholds?.green},
                  Warning {specialData.risk_thresholds?.warning},
                  Red {specialData.risk_thresholds?.red}.
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Current running PO coverage could not be loaded safely.
            </div>
          )
         ) : widget.type === "ceo-client-closure-status" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">Loading client requirement closure status...</div>
          ) : specialData ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{
                    background: specialData.publishable ? "#dcfce7" : "#fef3c7",
                    color: specialData.publishable ? "#166534" : "#92400e",
                  }}
                >
                  {specialData.publishable ? "ERP-backed & Approved" : (specialData.validation_status || "Validation Pending")}
                </span>
                {specialData.formula_version ? (
                  <span className="text-xs text-slate-500">Version {specialData.formula_version}</span>
                ) : null}
              </div>
              <div className="text-sm text-slate-700">{specialData.message}</div>
              {specialData.publishable && Array.isArray(specialData.rows) && specialData.rows.length ? (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        {Object.keys(specialData.rows[0]).slice(0, 10).map((key) => (
                          <th key={key} className="px-3 py-2 text-left">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {specialData.rows.slice(0, 25).map((row, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          {Object.keys(specialData.rows[0]).slice(0, 10).map((key) => (
                            <td key={key} className="px-3 py-2 whitespace-nowrap">{String(row?.[key] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {!specialData.publishable && Array.isArray(specialData.source_tables) && specialData.source_tables.length ? (
                <div className="text-xs text-slate-500">Mapped ERP sources: {specialData.source_tables.join(", ")}</div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-slate-500">Closure mapping could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-rm-requirement-gap" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading RM requirement mapping...
            </div>
          ) : specialData ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{
                    background: specialData.publishable ? "#dcfce7" : "#fef3c7",
                    color: specialData.publishable ? "#166534" : "#92400e",
                  }}
                >
                  {specialData.publishable
                    ? "Approved"
                    : "Business Validation Pending"}
                </span>

                <span className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                  {specialData.calculation_status || "—"}
                </span>
              </div>

              {!specialData.publishable && (
                <div
                  className="rounded-xl border p-4"
                  style={{
                    background: "#fffbeb",
                    borderColor: "#fde68a",
                  }}
                >
                  <div className="text-[13px] font-semibold" style={{ color: "#92400e" }}>
                    Procurement value is intentionally not published yet
                  </div>
                  <div className="text-[12px] mt-1 leading-5" style={{ color: "#78350f" }}>
                    {specialData.message}
                  </div>
                </div>
              )}

              <div className="rounded-xl border p-4" style={{ borderColor: "var(--c-border)" }}>
                <div className="text-[12px] font-semibold mb-2">Mapped ERP Formula</div>
                <div className="text-[12px] leading-5" style={{ color: "var(--c-text-muted)" }}>
                  {specialData.formula || "Formula mapping unavailable."}
                </div>
              </div>

              {(specialData.data_lineage || []).length > 0 && (
                <div>
                  <div className="text-[12px] font-semibold mb-2">Calculation Lineage</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(specialData.data_lineage || []).map((step) => (
                      <div
                        key={step.step}
                        className="rounded-lg border px-3 py-2.5"
                        style={{ borderColor: "var(--c-border)", background: "#f8fafc" }}
                      >
                        <div className="text-[10.5px] uppercase tracking-wide" style={{ color: "#64748b" }}>
                          Step {step.step}
                        </div>
                        <div className="text-[12px] mt-1">
                          {step.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {specialData.validation_notes && (
                <div className="text-[11.5px] leading-5" style={{ color: "var(--c-text-muted)" }}>
                  Validation: {specialData.validation_notes}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              RM requirement mapping is unavailable.
            </div>
          )
        ) : widget.type === "ceo-farm-audit-summary" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading farm certification summary...
            </div>
          ) : specialData?.ok && specialData?.data?.summary ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Total Farms", specialData.data.summary.total_farms],
                  ["BAP", specialData.data.summary.bap_farms],
                  ["ASC", specialData.data.summary.asc_farms],
                  ["Non-Certified", specialData.data.summary.non_certified_farms],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border px-3 py-2"
                    style={{
                      borderColor: "var(--c-border)",
                      background: "var(--c-surface)",
                    }}
                  >
                    <div
                      className="text-[10.5px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      {label}
                    </div>
                    <div className="text-2xl font-semibold mt-1">
                      {Number(value || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px]"
                style={{ color: "var(--c-text-muted)" }}
              >
                <span>
                  Non-BAP:{" "}
                  <span className="numeric">
                    {Number(specialData.data.summary.non_bap_farms || 0).toLocaleString()}
                  </span>
                </span>
                <span>
                  Unmarked:{" "}
                  <span className="numeric">
                    {Number(specialData.data.summary.unmarked_farms || 0).toLocaleString()}
                  </span>
                </span>
                {specialData.data.categories_are_non_exclusive && (
                  <span>
                    Certification categories are non-exclusive
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Farm certification summary could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-farm-audit-schedule" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading plant audit schedule and certificate renewals...
            </div>
          ) : specialData?.ok && specialData?.data ? (
            <div className="space-y-4">
              {(() => {
                const data = specialData.data;
                const summary = data.summary || {};
                const plantSummary = data.plant_summary || [];
                const rows = data.rows || [];

                const cards = [
                  ["Total", summary.total],
                  ["Valid", summary.valid],
                  ["Expired", summary.expired],
                  ["Renewal Due", summary.renewal_due],
                  ["Upcoming ≤90d", summary.upcoming_renewal],
                ];

                const exceptionRows = plantSummary.filter(
                  (row) =>
                    Number(row.expired || 0) > 0 ||
                    Number(row.renewal_due || 0) > 0 ||
                    Number(row.upcoming_renewal || 0) > 0
                );

                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {cards.map(([label, value]) => (
                        <div
                          key={label}
                          className="rounded-lg border px-3 py-2"
                          style={{
                            borderColor: "var(--c-border)",
                            background: "var(--c-surface)",
                          }}
                        >
                          <div
                            className="text-[10.5px] uppercase tracking-wide"
                            style={{ color: "var(--c-text-muted)" }}
                          >
                            {label}
                          </div>
                          <div className="text-2xl font-semibold mt-1">
                            {Number(value || 0).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      className="rounded-xl border overflow-hidden"
                      style={{ borderColor: "var(--c-border)" }}
                    >
                      <div
                        className="px-4 py-3 border-b"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <div className="text-[12px] font-semibold">
                          Plant Exceptions
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          Expired certificates and renewals requiring attention.
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr
                              className="border-b"
                              style={{ borderColor: "var(--c-border)" }}
                            >
                              {[
                                "Plant",
                                "Total",
                                "Valid",
                                "Expired",
                                "Renewal Due",
                                "Upcoming ≤90d",
                              ].map((label) => (
                                <th
                                  key={label}
                                  className="px-3 py-2 text-left font-semibold"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  {label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {exceptionRows.map((row, index) => (
                              <tr
                                key={`${row.plant || "plant"}-${index}`}
                                className="border-b"
                                style={{ borderColor: "var(--c-border)" }}
                              >
                                <td className="px-3 py-2 font-medium">
                                  {row.plant || "Unknown plant"}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.total || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.valid || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.expired || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.renewal_due || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.upcoming_renewal || 0).toLocaleString()}
                                </td>
                              </tr>
                            ))}

                            {!exceptionRows.length && (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="px-3 py-4 text-center"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  No plant exceptions found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div
                      className="rounded-xl border overflow-hidden"
                      style={{ borderColor: "var(--c-border)" }}
                    >
                      <div
                        className="px-4 py-3 border-b"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <div className="text-[12px] font-semibold">
                          Audit / Certificate Drill-down
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          AuditDate · ValidUpto · RenewalDate · ERP status
                        </div>
                      </div>

                      <div className="max-h-[420px] overflow-auto">
                        <table className="w-full text-[11px]">
                          <thead className="sticky top-0 bg-white">
                            <tr
                              className="border-b"
                              style={{ borderColor: "var(--c-border)" }}
                            >
                              {[
                                "Plant",
                                "Certificate",
                                "Certification Body",
                                "Audit Date",
                                "Valid Upto",
                                "Renewal Date",
                                "Status",
                                "Renewal Status",
                              ].map((label) => (
                                <th
                                  key={label}
                                  className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  {label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, index) => (
                              <tr
                                key={`${row.farm_audit_id || "audit"}-${index}`}
                                className="border-b"
                                style={{ borderColor: "var(--c-border)" }}
                              >
                                <td className="px-3 py-2 font-medium whitespace-nowrap">
                                  {row.plant || "Unknown plant"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.certificate_no || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.certification_body || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.audit_date || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.valid_upto || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.renewal_date || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap font-semibold">
                                  {row.status || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.renewal_status || "—"}
                                </td>
                              </tr>
                            ))}

                            {!rows.length && (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="px-3 py-4 text-center"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  No audit/certificate records available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div
                      className="flex flex-wrap gap-x-4 gap-y-1 text-[10.5px]"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      <span>
                        Source: {data.source || "QcFarmAuditDetails"}
                      </span>
                      <span>
                        Renewal window: {Number(data.renewal_window_days || 90)} days
                      </span>
                      <span>
                        Date basis: AuditDate / ValidUpto / RenewalDate
                      </span>
                      <span>
                        Reconciliation: {data.reconciliation?.status || "PENDING"}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Plant audit schedule and certificate renewals could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-va-percent-by-plant" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading Peeling (VA) percentage comparison...
            </div>
          ) : specialData?.ok && specialData?.data?.rows?.length ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs" style={{ color: "var(--c-text-muted)" }}>
                    VA% = SUM(VA Output Weight) / SUM(VA Input Weight) × 100
                  </div>
                  <div className="text-[10.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                    Period: {specialData.data.period || "all available data"}
                  </div>
                </div>
                {specialData.data.overall_percent != null && (
                  <div className="text-right">
                    <div
                      className="text-[10.5px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      Overall VA%
                    </div>
                    <div className="text-2xl font-semibold">
                      {Number(specialData.data.overall_percent).toFixed(2)}%
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {specialData.data.rows.map((row, index) => {
                  const percent = Number(row.percent);
                  const width = Math.max(0, Math.min(percent, 100));

                  return (
                    <div key={`${row.plant || "plant"}-${index}`} className="space-y-1">
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="font-medium">
                          {row.plant || "Unknown plant"}
                        </span>
                        <span className="numeric font-semibold">
                          {Number.isFinite(percent) ? percent.toFixed(2) : "—"}%
                        </span>
                      </div>
                      <div
                        className="h-2 rounded-full overflow-hidden"
                        style={{ background: "var(--c-border)" }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${width}%`,
                            background: "var(--c-primary)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Peeling (VA) percentage comparison could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-dummy-carton-usage-by-period" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">Loading dummy carton usage comparison...</div>
          ) : specialData?.rows?.length ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex rounded-lg border p-1" style={{ borderColor: "var(--c-border)" }}>
                  {[
                    ["month", "Month"],
                    ["week", "Week"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setDummyCartonPeriod(value)}
                      className="rounded-md px-3 py-1.5 text-[11px] font-semibold"
                      style={{
                        background: dummyCartonPeriod === value ? "#0f62fe" : "transparent",
                        color: dummyCartonPeriod === value ? "#fff" : "var(--c-text-muted)",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <select value={dummyCartonYear} onChange={(e) => setDummyCartonYear(e.target.value)} className="rounded-lg border px-3 py-1.5 text-[11px] font-semibold" style={{ borderColor: "var(--c-border)", background: "var(--c-surface)", color: "var(--c-text)" }}>
                  {[...new Set((specialData.rows || []).map((row) => String(row.period || "").slice(0, 4)).filter((year) => /^20\d{2}$/.test(year)))].sort().reverse().map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>
                  {specialData.summary?.plants || 0} plants · {dummyCartonPeriod === "month" ? specialData.summary?.months || 0 : specialData.summary?.weeks || 0} periods
                </div>
              </div>

              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(() => {
                      const filtered = specialData.rows.filter((row) => row.period_type === dummyCartonPeriod && String(row.period || "").slice(0, 4) === dummyCartonYear);
                      const periods = [...new Set(filtered.map((row) => row.period))].sort().reverse().slice(0, 24).reverse();
                      const plants = [...new Set(filtered.map((row) => row.plant))].sort();
                      return periods.map((period) => {
                        const item = { period };
                        plants.forEach((plant) => {
                          const row = filtered.find((entry) => entry.period === period && entry.plant === plant);
                          item[plant] = row?.dummy_mcs || 0;
                        });
                        return item;
                      });
                    })()}
                    margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="2 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={(value) => dummyCartonPeriod === "month" ? new Date(String(value) + "-01T00:00:00").toLocaleString("en-US", { month: "short" }) : `Week ${String(value).slice(4)}`} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => Number(value || 0).toLocaleString()} />
                    <Legend />
                    {[...new Set(specialData.rows.filter((row) => row.period_type === dummyCartonPeriod && String(row.period || "").slice(0, 4) === dummyCartonYear).map((row) => row.plant))].sort().map((plant, plantIndex) => (
                      <Bar key={plant} dataKey={plant} name={plant} fill={["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706", "#db2777", "#4f46e5", "#64748b"][plantIndex % 8]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => nav("/ceo-command-center/dummy-carton-usage-by-period")}
                  className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: "var(--c-border)",
                    color: "var(--c-text)",
                  }}
                >
                  View Detail
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Dated MCS", specialData.summary?.dated_dummy_mcs],
                  ["Undated MCS", specialData.summary?.undated_dummy_mcs],
                  ["Total Dummy MCS", specialData.summary?.total_dummy_mcs],
                  ["Validation", specialData.reconciliation?.pass ? "PASS" : "FAIL"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border p-3" style={{ borderColor: "var(--c-border)" }}>
                    <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>{label}</div>
                    <div className="text-lg font-semibold mt-1">{typeof value === "number" ? value.toLocaleString() : value}</div>
                  </div>
                ))}
              </div>

              <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>
                Source: CsDailyProductionData · PackingTypeID = DUMMY · quantity = SUM(ActualMcs) · date basis = ProductionDate · ISO weeks
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">No dummy carton usage comparison data available.</div>
          )

        ) : widget.type === "ceo-soaking-pickup-item-plant" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading soaking pickup comparison...
            </div>
          ) : specialData?.data?.rows?.length ? (
            <div className="space-y-3">
              <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--c-border)" }}>
                <div
                  className="px-4 py-3 border-b"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[12px] font-semibold">
                      Soaking Pickup Item / Plant
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        downloadCsv(
                          "ceo-soaking-pickup-item-plant.csv",
                          ["Item", "Plant", "Records", "Soak In", "Soak Out", "Pickup %"],
                          (specialData.data.rows || []).map((row) => [
                            row.type_product && row.type_product !== "UNMAPPED"
                              ? `${row.type_product}-${row.item}`
                              : row.item || "UNMAPPED",
                            row.plant || "—",
                            Number(row.records || 0).toLocaleString(),
                            Number(row.soak_in || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }),
                            Number(row.soak_out || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }),
                            `${Number(row.pickup_percent || 0).toFixed(2)}%`,
                          ])
                        )
                      }
                      className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                      style={{ borderColor: "var(--c-border)", background: "#fff" }}
                      title="Download all soaking pickup rows as CSV"
                    >
                      Download CSV
                    </button>
                  </div>
                </div>

                <table className="min-w-full text-[11.5px]">
                  <thead>
                    <tr style={{ background: "var(--c-surface-muted)" }}>

                      {["Item", "Plant", "Records", "Soak In", "Soak Out", "Pickup %"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 whitespace-nowrap font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(soakingExpanded
                      ? specialData.data.rows
                      : specialData.data.rows.slice(0, 5)
                    ).map((row, index) => (
                      <tr
                        key={`${row.item}-${row.plant_id}-${index}`}
                        className="border-t"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <td className="px-3 py-2 whitespace-nowrap font-medium">
                          {row.type_product && row.type_product !== "UNMAPPED"
                            ? `${row.type_product}-${row.item}`
                            : row.item || "UNMAPPED"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.plant || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {Number(row.records || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {Number(row.soak_in || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {Number(row.soak_out || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap font-semibold">
                          {Number(row.pickup_percent || 0).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {specialData.data.rows.length > 5 && (
                  <div className="flex justify-center py-3">
                    <button
                      type="button"
                      onClick={() => setSoakingExpanded((current) => !current)}
                      className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                      style={{
                        borderColor: "var(--c-border)",
                        background: "#fff",
                      }}
                    >
                      {soakingExpanded
                        ? "Collapse"
                        : `Expand · Show all ${specialData.data.rows.length.toLocaleString()} rows`}
                    </button>
                  </div>
                )}
              </div>

              <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>
                Least → highest · Pickup % = (SUM(SoakOutQty) − SUM(SoakInQty)) / SUM(SoakInQty) × 100
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No valid soaking pickup comparison data available.
            </div>
          )
        ) : widget.type === "ceo-production-year-comparison" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading 2–3 year capacity and production comparison...
            </div>
          ) : specialData?.data?.rows?.length ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={specialData.data.rows.filter(
                    (row) => row.production_year != null
                  )}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="2 3" />
                  <XAxis
                    dataKey="production_year"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${Number(value || 0).toLocaleString()} KGS`,
                      "Production",
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="production_kgs"
                    name="Production KGS"
                    fill="#2563eb"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No capacity / production comparison data available
            </div>
          )
        ) : widget.type === "ceo-buyer-order-analysis" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading Buyer total vs confirmed orders...
            </div>
          ) : specialData?.rows?.length ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Total Orders", specialData.summary?.total_orders],
                  ["Confirmed Orders", specialData.summary?.confirmed_orders],
                  ["Not Confirmed", specialData.summary?.non_confirmed_orders],
                  ["Confirmation %", `${specialData.summary?.confirmation_pct ?? 0}%`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border p-3"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div
                      className="text-[10px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      {label}
                    </div>
                    <div className="text-lg font-semibold mt-1">
                      {typeof value === "number"
                        ? value.toLocaleString()
                        : value}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={specialData.rows.slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 10, right: 20, left: 40, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="2 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="buyer_name"
                      width={170}
                      tick={{ fontSize: 10 }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="total_orders"
                      name="Total Orders"
                      fill="#64748b"
                      radius={[0, 4, 4, 0]}
                    />
                    <Bar
                      dataKey="confirmed_orders"
                      name="Confirmed Orders"
                      fill="#2563eb"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div
                  className="text-[11px] leading-5"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  {Number(specialData.summary?.identified_buyers || 0).toLocaleString()} buyers.
                  Buyer is the consignee on the order, as on the ERP order list.
                </div>

                <button
                  type="button"
                  onClick={() =>
                    nav("/ceo-command-center/buyer-order-analysis")
                  }
                  className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: "var(--c-border)",
                    color: "var(--c-text)",
                  }}
                >
                  View Details
                </button>
              </div>

              <div
                className="text-[11px]"
                style={{ color: "var(--c-text-muted)" }}
              >
                Reconciliation: {specialData.reconciliation?.pass ? "PASS" : "FAIL"} ·
                Confirmed = ScrmPoData.Status 5 ·
                Buyer = ScrmPoData.BuyerID → ScrmBuyerDetails.BuyerID
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No Buyer order comparison data available.
            </div>
          )
        ) : widget.type === "ceo-price-comparison-over-years" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading price comparison...
            </div>
          ) : specialData?.rows?.length ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={specialData.rows.filter(
                    (row) => row.order_year != null
                  )}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="2 3" />
                  <XAxis
                    dataKey="order_year"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="weighted_avg_price"
                    name="Weighted Avg Price"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="min_price"
                    name="Minimum Price"
                    stroke="#16a34a"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="max_price"
                    name="Maximum Price"
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>

                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    {specialData?.summary?.currency || "USD"} / {specialData?.summary?.qty_unit || "LBS"} · ERP weighted selling price
                  </span>
                  <button
                    type="button"
                    onClick={() => window.location.assign("/ceo-command-center/price-comparison-over-years")}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                  >
                    View Detail
                  </button>
                </div>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No price comparison data available
            </div>
          )
        ) : widget.type === "ceo-antidumping" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading antidumping contribution...
            </div>
          ) : specialData?.data?.rows?.length ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={specialData.data.rows.filter(
                    (row) => row?.sales_year != null
                  )}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="2 3" />
                  <XAxis
                    dataKey="sales_year"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${Number(value || 0).toFixed(2)}%`,
                      "Antidumping Contribution",
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="contribution_pct"
                    name="Antidumping Contribution"
                    fill="#2563eb"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No antidumping contribution data available
            </div>
          )
        ) : widget.type === "ceo-annual-country-contribution" ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading annual country contribution...
            </div>
          ) : specialData?.data?.rows?.length ? (
            <div>
              {specialData?.year_validation?.some((row) => !row.publishable) ? (
                <div
                  className="mb-3 rounded-lg border px-3 py-2 text-xs"
                  style={{
                    background: "#fffbeb",
                    borderColor: "#fde68a",
                    color: "#92400e",
                  }}
                >
                  Historical years with unresolved ERP exchange rates are shown as
                  Business Validation Pending. No missing value is converted to 0%.
                </div>
              ) : null}

              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={Object.values(
                      specialData.data.rows.reduce((acc, row) => {
                        const year = row.sales_year;
                        if (!acc[year]) {
                          acc[year] = {
                            sales_year: year,
                            publishable: row.publishable,
                            validation_status: row.validation_status,
                          };
                        }

                        acc[year][row.country] =
                          row.contribution_pct == null
                            ? null
                            : Number(row.contribution_pct);

                        return acc;
                      }, {})
                    )}
                    margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="2 3" />
                    <XAxis
                      dataKey="sales_year"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        value == null
                          ? "Business Validation Pending"
                          : `${Number(value).toFixed(2)}%`,
                        name,
                      ]}
                    />
                    <Legend />

                    <Bar
                      dataKey="USA"
                      name="USA"
                      fill="#2563eb"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="China"
                      name="China"
                      fill="#16a34a"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="Russia"
                      name="Russia"
                      fill="#f59e0b"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="Belgium"
                      name="Belgium"
                      fill="#dc2626"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {specialData?.year_validation?.some((row) => !row.publishable) ? (
                <div className="mt-2 text-[11px] text-slate-500">
                  {specialData.year_validation
                    .filter((row) => !row.publishable)
                    .map((row) => `${row.sales_year}: ${row.unresolved_fx_lines} unresolved FX line(s)`)
                    .join(" · ")}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No annual country contribution data available
            </div>
          )
        ) : isSpecial ? (
          specialLoading ? (
            <div className="text-sm text-slate-500">
              Loading...
            </div>
          ) : specialData ? (
            <div
              onClick={shipmentSpecialClickable ? openShipmentSpecialKpi : undefined}
              onKeyDown={
                shipmentSpecialClickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openShipmentSpecialKpi();
                      }
                    }
                  : undefined
              }
              role={shipmentSpecialClickable ? "button" : undefined}
              tabIndex={shipmentSpecialClickable ? 0 : undefined}
              title={
                shipmentSpecialClickable
                  ? `View ${widget.title} records`
                  : undefined
              }
              className={
                shipmentSpecialClickable
                  ? "cursor-pointer rounded-xl transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 -m-2 p-2"
                  : ""
              }
            >
              <div className="text-3xl font-semibold">
                {widget.type === "ceo-fda-release-hold"
                  ? specialData.released_count ?? 0
                  : widget.type === "ceo-commercial-pending-shipments"
                    ? specialData.pending_quantity ?? 0
                    : widget.type === "ceo-payments-received-pending"
                      ? `${specialData.received_count ?? 0} / ${specialData.pending_count ?? 0}`
                  : widget.type === "ceo-early-shipments"
                    ? specialData?.summary?.early_count ?? 0
                    : widget.type === "ceo-delayed-shipments"
                      ? specialData?.summary?.delayed_count ?? 0
                      : widget.type === "ceo-on-water-shipments"
                        ? specialData?.summary?.on_water_count ?? 0
                        : specialData?.summary?.on_time_count ?? 0}
              </div>

              <div className="mt-1 text-sm text-slate-500">
                {widget.type === "ceo-fda-release-hold"
                  ? `${specialData.released_count ?? 0} Released · ${specialData.hold_count ?? 0} Hold · ${specialData.pending_count ?? 0} Pending`
                  : widget.type === "ceo-commercial-pending-shipments"
                    ? "Pending dispatch quantity (MCS)"
                    : widget.type === "ceo-payments-received-pending"
                      ? `${specialData.received_count ?? 0} Received · ${specialData.pending_count ?? 0} Pending`
                  : widget.type === "ceo-early-shipments"
                    ? "Early shipments"
                    : widget.type === "ceo-delayed-shipments"
                      ? "Delayed shipments"
                      : widget.type === "ceo-on-water-shipments"
                        ? "On-water shipments"
                        : "On-time deliveries"}
              </div>

              <div className="mt-3 text-xs text-slate-500">
                {widget.type === "ceo-fda-release-hold"
                  ? `${specialData.total_count ?? 0} shipments`
                  : widget.type === "ceo-commercial-pending-shipments"
                    ? `${specialData.pending_lines ?? 0} PO lines · Pending dispatch`
                    : widget.type === "ceo-payments-received-pending"
                      ? `${specialData.total_count ?? 0} eligible POs`

                  : widget.type === "ceo-early-shipments"
                    ? `${specialData?.summary?.early_count ?? 0} shipments · ${specialData?.summary?.early_pct ?? 0}% of shipped POs`
                    : widget.type === "ceo-delayed-shipments"
                      ? `${specialData?.summary?.delayed_count ?? 0} shipments · ${specialData?.summary?.delayed_pct ?? 0}% of shipped POs`
                      : widget.type === "ceo-on-water-shipments"
                        ? `${specialData?.summary?.on_water_count ?? 0} shipments currently on water`
                        : `${specialData?.summary?.on_time_count ?? 0} shipments · ${specialData?.summary?.on_time_pct ?? 0}% of shipped POs`}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No data available
            </div>
          )
        ) : widget.type === "sales-annual-contribution" ? (
          <SalesAnnualContribution
            widget={widget}
            filters={filters}
            onDrillDown={openWidgetDrillDown}
          />
        ) : (
          <WidgetRenderer
            widget={widget}
            dashboardFilters={createCeoFilters(widget, filters || {})}
            onDrillDown={openWidgetDrillDown}
            onProcessStage={widget.id === "ceo-production-table" ? openProcessStage : undefined}
            onTableData={setTableExportData}
          />
        )}
      </div>

      {drill && (
        <CeoDrillDownModal
          drill={drill}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          onClose={() => {
            setDrill(null);
            setDrillData(null);
            setDrillError("");
          }}
        />
      )}
    </div>
  );
}

function CeoDrillDownModal({ drill, data, loading, error, onClose }) {
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(95);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortState, setSortState] = useState({
    column: "",
    direction: "asc",
  });
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(50);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const tableWrapRef = useRef(null);

  // Initial widget preview remains useful as column metadata.
  // Actual detailed-report rows are loaded server-side through /report/query.
  const rawColumns = data?.columns || [];
  const rawRows = reportData?.rows || [];

  const metricField = String(drill?.metricField || "").toLowerCase();
  const metricValue = drill?.metricValue;
  const aggregation = drill?.aggregation || "sum";

  const BUSINESS_LABELS = {
    LineValueUSD: "Sales Value USD",
    LineValueNative: "Sales Value (Native Currency)",
    InvoiceDate: "Invoice Date",
    InvoiceNumber: "Invoice Number",
    InvoiceGrossKGS: "Invoice Gross KGS",
    PoNumber: "PO Number",
    PoNo: "PO Number",
    PoDate: "PO Date",
    ConsigneeName: "Buyer / Consignee",
    BuyerName: "Buyer",
    BrandName: "Brand",
    Brand: "Brand",
    Variety: "Variety",
    Grade: "Grade",
    PackingStyle: "Packing Style",
    MCS: "MCS",
    OrderedMCS: "Ordered MCS",
    DispatchedMCS: "Dispatched MCS",
    AvailableMCS: "Available Stock MCS",
    AvailableKGS: "Available Stock KGS",
    ActualMCS: "Actual Stock MCS",
    ActualKGS: "Actual Stock KGS",
    ProductionDate: "Production Date",
    ColdStore: "Cold Store",
    RackLocation: "Rack Location",
    Barcode: "Barcode",
    ShipmentDate: "Shipment Date",
    DestinationCountry: "Destination Country",
    BatchCode: "Batch Code",
    OutMCS: "Shipment Out MCS",
    OutActualKGS: "Shipment Out KGS",
    LooseBags: "Loose Bags",
    ReceivedQty: "Production Received Qty",
    ArrivalDate: "Arrival Date",
    ArrivalCenter: "Arrival Centre",
    ProductionPlant: "Production Plant",
    PurchaseDate: "Purchase Date",
    PurchaseWeight: "RM Purchase Weight",
    ProductionWeight: "RM Production Weight",
    SupplierName: "Supplier",
    Centre: "Centre",
    PurchaseStation: "Purchase Station",
    LotNumber: "Lot Number",
    LotStatus: "Lot Status",
    QcRecordID: "QC Test Record",
    AnalysisDate: "Analysis Date",
    ReportDate: "Report Date",
    LotNo: "Lot Number",
    Plant: "Plant",
    Result: "QC Result",
    Parameter: "Test Parameter",
    Sulphite: "Sulphite",
    ExternalLab: "External Lab",
    ExternalResult: "External Lab Result",
    AbStatus: "Antibiotic Status",
    TestedBy: "Tested By",
    AntibioticDetected: "Antibiotic Detected / Failed",
    TestClear: "Clear Tests",
  };

  const businessLabel = (column) =>
    BUSINESS_LABELS[column] ||
    String(column || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\bUsd\b/g, "USD")
      .replace(/\bKgs\b/g, "KGS")
      .replace(/\bMcs\b/g, "MCS");

  const isTechnicalColumn = (column) => {
    const name = String(column || "").toLowerCase();
    return (
      name === "id" ||
      name.endsWith("id") ||
      name.includes("salelineid") ||
      name.includes("invoiceid") ||
      name.includes("poid") ||
      name.includes("buyerid") ||
      name.includes("consigneeid") ||
      name.includes("processorid") ||
      name.includes("exporterid") ||
      name.includes("countryid") ||
      name.includes("clientid")
    );
  };

  const getDefaultColumnsForMetric = () => {
    const metric = String(drill?.metricField || "").toLowerCase();

    if (metric === "receivedqty") {
      return [
        "ArrivalDate",
        "ArrivalCenter",
        "CenterCode",
        "ProductionPlant",
        "PlantCode",
        "TrackCode",
        "GradingVarieties",
        "VAVarieties",
        "FinishedVarieties",
        "ReceivedQty",
      ];
    }

    if (metric === "availablemcs" || metric === "availablekgs") {
      return [
        "ProductionDate",
        "ColdStore",
        "RackLocation",
        "Variety",
        "Grade",
        "PackingStyle",
        "AvailableMCS",
        "AvailableKGS",
        "ActualMCS",
        "ActualKGS",
        "Barcode",
      ];
    }

    if (metric === "orderedmcs" || metric === "dispatchedmcs") {
      return [
        "PoDate",
        "PoNumber",
        "ConsigneeName",
        "BuyerName",
        "Brand",
        "Variety",
        "Grade",
        "PackingStyle",
        "OrderedMCS",
        "DispatchedMCS",
        "Price",
      ];
    }

    if (
      metric === "outmcs" ||
      metric === "outavailablekgs" ||
      metric === "outactualkgs"
    ) {
      return [
        "ShipmentDate",
        "PoNo",
        "ConsigneeName",
        "DestinationCountry",
        "Grade",
        "BatchCode",
        "ColdStore",
        "RackLocation",
        "OutMCS",
        "OutActualKGS",
        "LooseBags",
      ];
    }

    return [
      "InvoiceDate",
      "PoNumber",
      "ConsigneeName",
      "BuyerName",
      "BrandName",
      "Brand",
      "Variety",
      "Grade",
      "PackingStyle",
      "MCS",
      "InvoiceGrossKGS",
      "PriceNative",
      "Currency",
      "LineValueUSD",
      "LineValueNative",
    ];
  };

  const defaultBusinessColumns = getDefaultColumnsForMetric().filter(Boolean);
  const preferredDefaults = defaultBusinessColumns;

  const defaultSelectedColumns = rawColumns.filter((column) => {
    if (isTechnicalColumn(column)) return false;
    return preferredDefaults.some((c) => String(c).toLowerCase() === String(column).toLowerCase());
  });

  const [selectedColumns, setSelectedColumns] = useState([]);

  useEffect(() => {
    if (!rawColumns.length) return;

    setSelectedColumns((current) => {
      const validCurrent = current.filter((column) => rawColumns.includes(column));

      if (validCurrent.length) {
        return validCurrent;
      }

      if (defaultSelectedColumns.length) {
        return defaultSelectedColumns;
      }

      return rawColumns.filter((column) => !isTechnicalColumn(column)).slice(0, 12);
    });
  }, [data]);

  const effectiveSelectedColumns = selectedColumns.length
    ? selectedColumns
    : defaultSelectedColumns;

  const orderedIndexes = rawColumns
    .map((column, index) => ({ column, index }))
    .filter((item) => effectiveSelectedColumns.includes(item.column))
    .sort((a, b) => {
      const aMetric = metricField && String(a.column).toLowerCase() === metricField ? -10000 : 0;
      const bMetric = metricField && String(b.column).toLowerCase() === metricField ? -10000 : 0;

      const ap = defaultBusinessColumns.findIndex((word) => String(a.column).toLowerCase() === String(word).toLowerCase());
      const bp = defaultBusinessColumns.findIndex((word) => String(b.column).toLowerCase() === String(word).toLowerCase());

      const ar = ap >= 0 ? ap : 999;
      const br = bp >= 0 ? bp : 999;

      return (aMetric + ar) - (bMetric + br);
    });

  const orderedSelectedColumns = orderedIndexes.map((item) => item.column);

  const columns = reportData?.columns?.length
    ? reportData.columns
    : orderedSelectedColumns;

  const visibleRows = rawRows;

  const activeColumnFilterCount = Object.values(columnFilters)
    .filter((value) => String(value || "").trim() !== "")
    .length;

  const totalCount = Number(reportData?.total_count || 0);
  const totalPages = Number(reportData?.total_pages || 0);
  const currentPage = Number(reportData?.page || reportPage);

  const changeSort = (column) => {
    setReportPage(1);
    setSortState((current) => {
      if (current.column === column) {
        return {
          column,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        column,
        direction: "asc",
      };
    });
  };

  const clearReportFilters = () => {
    setSearch("");
    setColumnFilters({});
    setSortState({ column: "", direction: "asc" });
    setReportPage(1);
  };

  const metricColumnIndex = columns.findIndex(
    (column) => metricField && String(column).toLowerCase() === metricField
  );

  const filteredAggregate = reportData?.filtered_aggregate;

  const reportMetricTotal =
    filteredAggregate !== null &&
    filteredAggregate !== undefined &&
    filteredAggregate !== ""
      ? filteredAggregate
      : metricValue;

  const hasKpiFooter =
    metricColumnIndex >= 0 &&
    reportMetricTotal !== null &&
    reportMetricTotal !== undefined &&
    reportMetricTotal !== "";

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(String(value).replace(/,/g, ""));
    if (!Number.isFinite(num)) return String(value);
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const scrollTable = (mode) => {
    const el = tableWrapRef.current;
    if (!el) return;
    if (mode === "start") el.scrollLeft = 0;
    if (mode === "left") el.scrollBy({ left: -700, behavior: "smooth" });
    if (mode === "right") el.scrollBy({ left: 700, behavior: "smooth" });
    if (mode === "end") el.scrollLeft = el.scrollWidth;
  };

  const toggleColumn = (column) => {
    setReportPage(1);
    setSelectedColumns((current) => {
      if (current.includes(column)) {
        return current.filter((c) => c !== column);
      }
      return [...current, column];
    });
  };

  useEffect(() => {
    if (!drill?.payload?.widget || !effectiveSelectedColumns.length) {
      return undefined;
    }

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setReportLoading(true);
      setReportError("");

      try {
        const response = await api.post(
          "/report/query",
          {
            widget: drill.payload.widget,
            filters: drill.payload.filters || [],
            page: reportPage,
            page_size: reportPageSize,
            search: String(search || "").trim() || null,
            column_filters: columnFilters,
            sort_by: sortState.column || null,
            sort_dir: sortState.direction || "asc",
            selected_columns: orderedSelectedColumns,
            aggregate_field: drill?.metricField || null,
            aggregate_func: aggregation || "sum",
          },
          {
            timeout: 60000,
          }
        );

        if (!cancelled) {
          setReportData(response.data);
        }
      } catch (e) {
        if (!cancelled) {
          setReportError(
            e?.response?.data?.detail ||
            e?.message ||
            "Unable to load detailed report."
          );
        }
      } finally {
        if (!cancelled) {
          setReportLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    drill,
    effectiveSelectedColumns,
    reportPage,
    reportPageSize,
    search,
    columnFilters,
    sortState,
  ]);

  const metricLabel = businessLabel(drill?.metricField || "Selected KPI");

  const buildReportExportPayload = () => {
    const exportWidget = {
      ...drill.payload.widget,
      selected_columns: effectiveSelectedColumns.length
        ? effectiveSelectedColumns
        : undefined,
    };

    return {
      widget: exportWidget,
      filters: drill.payload.filters || [],
      page: 1,
      page_size: reportPageSize,
      search: search || null,
      column_filters: columnFilters || {},
      sort_by: sortState?.field || null,
      sort_dir: sortState?.dir || "asc",
      selected_columns: effectiveSelectedColumns.length
        ? effectiveSelectedColumns
        : undefined,
      aggregate_field: drill?.metricField || null,
      aggregate_func: aggregation || "sum",
    };
  };

  const downloadReportBlob = (response, extension, mimeType) => {
    const blob = new Blob([response.data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `${String(drill.title || "detailed_report")
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_+|_+$/g, "")}_details.${extension}`;

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    if (!drill?.payload?.widget) return;

    try {
      const response = await api.post(
        "/report/export.csv",
        buildReportExportPayload(),
        {
          responseType: "blob",
          timeout: 120000,
        }
      );

      downloadReportBlob(
        response,
        "csv",
        "text/csv;charset=utf-8"
      );
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Unable to export detailed report as CSV."
      );
    }
  };

  const exportExcel = async () => {
    if (!drill?.payload?.widget) return;

    try {
      const response = await api.post(
        "/report/export.xlsx",
        buildReportExportPayload(),
        {
          responseType: "blob",
          timeout: 120000,
        }
      );

      downloadReportBlob(
        response,
        "xlsx",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Unable to export detailed report as Excel."
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="ceo-drilldown-fullscreen">
      <div className="px-6 py-4 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--c-border)" }}>
        <div className="min-w-0">
          <div className="text-[18px] font-semibold truncate" style={{ color: "var(--c-text)" }}>
            {drill.title} — Detailed Report
          </div>
          <div className="text-[12px] mt-1" style={{ color: "var(--c-text-muted)" }}>
            Source measure: {metricLabel} · Formula: {String(aggregation).toUpperCase()}({metricLabel})
          </div>
        </div>

        <button type="button" className="btn btn-ghost" onClick={onClose} title="Close">
          <X size={17} strokeWidth={1.8} />
        </button>
      </div>

      <div
        className="px-6 py-4 border-b space-y-4"
        style={{
          borderColor: "var(--c-border)",
          background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
        }}
      >
        <div className="grid grid-cols-12 gap-3 items-stretch">
          <div
            className="col-span-12 md:col-span-6 lg:col-span-3 rounded-xl border bg-white px-5 py-4 shadow-sm"
            style={{ borderColor: "#dbe3ef" }}
          >
            <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>
              Report Total
            </div>
            <div className="text-[22px] font-semibold numeric mt-1" style={{ color: "var(--c-text)" }}>
              {formatValue(reportMetricTotal)}
            </div>
            <div className="text-[11.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
              Full filtered result · {String(aggregation).toUpperCase()}({metricLabel})
            </div>
          </div>

          <div
            className="col-span-12 md:col-span-6 lg:col-span-2 rounded-xl border bg-white px-5 py-4 shadow-sm"
            style={{ borderColor: "#dbe3ef" }}
          >
            <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>
              Total Records
            </div>
            <div className="text-[22px] font-semibold numeric mt-1" style={{ color: "var(--c-text)" }}>
              {totalCount.toLocaleString()}
            </div>
            <div className="text-[11.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
              {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : "No matching records"}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-3 flex items-center">
            <div className="w-full">
              <div
                className="text-[10.5px] font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: "#64748b" }}
              >
                Search Report
              </div>
              <input
                className="input w-full"
                style={{
                  minHeight: 42,
                  borderRadius: 10,
                  background: "#fff",
                  borderColor: "#cbd5e1",
                }}
                placeholder="Search buyer, PO, grade, variety..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-wrap items-end justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={exportCsv}
              disabled={!drill?.payload?.widget || loading}
              title="Download the current filtered detailed report as CSV"
            >
              <Download size={14} strokeWidth={1.8} />
              Download CSV
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={exportExcel}
              disabled={!drill?.payload?.widget || loading}
              title="Download the current filtered detailed report as Excel"
            >
              <Download size={14} strokeWidth={1.8} />
              Download Excel
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowFilters((value) => !value)}
              title="Show or hide column filters"
            >
              {showFilters ? "Hide Filters" : "Filter Columns"}
              {activeColumnFilterCount > 0
                ? ` (${activeColumnFilterCount})`
                : ""}
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={clearReportFilters}
              disabled={!search && activeColumnFilterCount === 0 && !sortState.column}
            >
              Clear Filters
            </button>

            <button type="button" className="btn btn-outline" onClick={() => setShowColumnPicker((v) => !v)}>
              Select Columns
            </button>




          </div>
        </div>

        {showColumnPicker && (
          <div className="rounded-xl border bg-white p-3" style={{ borderColor: "var(--c-border)" }}>
            <div className="text-[13px] font-semibold mb-2">Select columns to display in this report</div>
            <div className="text-[11.5px] mb-3" style={{ color: "var(--c-text-muted)" }}>
              Default view shows only key ERP report columns. Select more columns only when detailed analysis is required.
            </div>
            <div className="flex flex-wrap gap-2">
              {rawColumns.map((column) => (
                <label
                  key={column}
                  className="px-2.5 py-1.5 rounded-lg border text-[11.5px] flex items-center gap-1.5"
                  style={{
                    borderColor: selectedColumns.includes(column) ? "#2563eb" : "var(--c-border)",
                    background: selectedColumns.includes(column) ? "#eff6ff" : "#fff",
                    color: isTechnicalColumn(column) ? "#64748b" : "var(--c-text)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(column)}
                    onChange={() => toggleColumn(column)}
                  />
                  {businessLabel(column)}
                  {isTechnicalColumn(column) && <span style={{ color: "#94a3b8" }}>(ID)</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3"
          style={{ borderColor: "#dbe3ef" }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: "#eff6ff", color: "#1d4ed8" }}
            >
              {visibleRows.length.toLocaleString()} visible
            </span>

            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: "#f8fafc", color: "#475569" }}
            >
              {totalCount.toLocaleString()} total records
            </span>

            {activeColumnFilterCount > 0 && (
              <span
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: "#fff7ed", color: "#c2410c" }}
              >
                {activeColumnFilterCount} column filter(s)
              </span>
            )}

            {totalPages > 0 && (
              <span
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: "#f0fdf4", color: "#15803d" }}
              >
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setZoom((z) => Math.max(70, z - 10))}
            >
              −
            </button>
            <span className="text-[12px] numeric" style={{ color: "#475569" }}>
              {zoom}%
            </span>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setZoom((z) => Math.min(130, z + 10))}
            >
              +
            </button>

            <span
              className="mx-1 h-5 w-px"
              style={{ background: "#cbd5e1" }}
            />

            <select
              className="input"
              value={reportPageSize}
              onChange={(event) => {
                setReportPageSize(Number(event.target.value));
                setReportPage(1);
              }}
              style={{
                width: 92,
                minHeight: 36,
                borderRadius: 8,
                background: "#fff",
              }}
              title="Rows per page"
            >
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={250}>250 / page</option>
              <option value={500}>500 / page</option>
            </select>

            <button
              type="button"
              className="btn btn-outline"
              disabled={currentPage <= 1 || reportLoading}
              onClick={() => setReportPage(1)}
            >
              First
            </button>

            <button
              type="button"
              className="btn btn-outline"
              disabled={currentPage <= 1 || reportLoading}
              onClick={() => setReportPage((page) => Math.max(1, page - 1))}
            >
              Previous
            </button>

            <span
              className="px-3 py-2 rounded-lg border text-[12px] font-semibold numeric"
              style={{
                borderColor: "#cbd5e1",
                background: "#fff",
                color: "#334155",
              }}
            >
              Page {currentPage} of {totalPages || 0}
            </span>

            <button
              type="button"
              className="btn btn-outline"
              disabled={!totalPages || currentPage >= totalPages || reportLoading}
              onClick={() =>
                setReportPage((page) =>
                  Math.min(totalPages || page, page + 1)
                )
              }
            >
              Next
            </button>

            <button
              type="button"
              className="btn btn-outline"
              disabled={!totalPages || currentPage >= totalPages || reportLoading}
              onClick={() => setReportPage(totalPages)}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      <div
        ref={tableWrapRef}
        className="flex-1 overflow-auto"
        style={{
          background: "#f8fafc",
          padding: "14px 16px 20px",
        }}
      >
        {loading && <div className="p-5 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>Loading detailed data…</div>}

        {error && <div className="p-5 text-[12.5px]" style={{ color: "#991b1b" }}>{error}</div>}

        {!loading && !error && columns.length === 0 && (
          <div className="p-5 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
            No columns selected.
          </div>
        )}

        {!loading && !error && columns.length > 0 && (
          <table
            className="tbl"
            style={{
              fontSize: `${Math.max(10, Math.min(14, zoom / 9))}px`,
              lineHeight: "1.35",
              minWidth: "max-content",
              background: "#fff",
              border: "1px solid #dbe3ef",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
            }}
          >
            <thead className="sticky top-0 z-20 bg-white">
              <tr>
                {columns.map((column) => {
                  const isMetric =
                    metricField &&
                    String(column).toLowerCase() === metricField;

                  const isSorted = sortState.column === column;

                  return (
                    <th
                      key={column}
                      title={`Sort by ${businessLabel(column)}`}
                      onClick={() => changeSort(column)}
                      style={{
                        whiteSpace: "nowrap",
                        padding: "9px 12px",
                        background: isMetric ? "#dbeafe" : "#eef2f7",
                        color: isMetric ? "#1d4ed8" : "#1e293b",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none",
                        position: isMetric ? "sticky" : undefined,
                        left: isMetric ? 0 : undefined,
                        zIndex: isMetric ? 32 : undefined,
                        borderBottom: "1px solid var(--c-border)",
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span>{businessLabel(column)}</span>
                        <span
                          className="text-[10px]"
                          style={{ color: isSorted ? "#2563eb" : "#94a3b8" }}
                        >
                          {isSorted
                            ? sortState.direction === "asc"
                              ? "▲"
                              : "▼"
                            : "↕"}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>

              {showFilters && (
                <tr>
                  {columns.map((column) => {
                    const isMetric =
                      metricField &&
                      String(column).toLowerCase() === metricField;

                    return (
                      <th
                        key={`${column}-filter`}
                        style={{
                          padding: "6px 8px",
                          background: isMetric ? "#eff6ff" : "#ffffff",
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 0 : undefined,
                          zIndex: isMetric ? 31 : undefined,
                          borderBottom: "1px solid var(--c-border)",
                        }}
                      >
                        <input
                          value={columnFilters[column] || ""}
                          onChange={(event) =>
                            setColumnFilters((current) => ({
                              ...current,
                              [column]: event.target.value,
                            }))
                          }
                          onClick={(event) => event.stopPropagation()}
                          placeholder={`Filter ${businessLabel(column)}`}
                          style={{
                            width: "100%",
                            minWidth: 120,
                            padding: "6px 8px",
                            border: "1px solid #cbd5e1",
                            borderRadius: 7,
                            fontSize: 11,
                            fontWeight: 500,
                            background: "#fff",
                            outline: "none",
                          }}
                        />
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>

            <tbody>
              {visibleRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    background: rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                    transition: "background 120ms ease",
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.background = "#eef6ff";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.background =
                      rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc";
                  }}
                >
                  {row.map((value, columnIndex) => {
                    const columnName = String(columns[columnIndex]);
                    const isMetric = metricField && columnName.toLowerCase() === metricField;
                    const processStage =
                      columnName === "GradingVarieties" ? "grading" :
                      columnName === "VAVarieties" ? "va" :
                      columnName === "FinishedVarieties" ? "finished" :
                      columnName === "SizeConversionPaths" ? "conversion" :
                      null;
                    const isProcessTable = drill?.payload?.widget?.id === "ceo-production-table";
                    const isSizeConversion = columnName === "SizeConversionPaths";
                    const displayValue = isSizeConversion && value
                      ? String(value).split(", ").map((path, index) => {
                          const [fromPart, toPart] = path.split(" -> ");
                          const from = (fromPart || "").split(" / ");
                          const to = (toPart || "").split(" / ");
                          const fromLabel = [from[0], from[1]].filter(Boolean).join(" ");
                          const toLabel = [to[0], to[1]].filter(Boolean).join(" ");
                          return (
                            <div
                              key={index}
                              style={{
                                marginBottom:
                                  index === String(value).split(", ").length - 1 ? 0 : 4,
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{fromLabel}</span>
                              <span style={{ margin: "0 6px", color: "#64748b" }}>→</span>
                              <span style={{ fontWeight: 600 }}>{toLabel}</span>
                            </div>
                          );
                        })
                      : (value === null || value === undefined || value === ""
                          ? "—"
                          : String(value));
                    return (
                      <td
                        key={columnIndex}
                        title={value === null || value === undefined ? "" : String(value)}
                        style={{
                          padding: "8px 12px",
                          whiteSpace: "normal",
                          minWidth:
                            String(columns[columnIndex]).toLowerCase().includes("variety")
                              ? 260
                              : 120,
                          maxWidth:
                            String(columns[columnIndex]).toLowerCase().includes("variety")
                              ? 340
                              : 240,
                          verticalAlign: "middle",
                          background: isMetric ? "#eff6ff" : undefined,
                          fontWeight: isMetric ? 700 : 500,
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 0 : undefined,
                          zIndex: isMetric ? 20 : undefined,
                          borderBottom: "1px solid #e5e7eb",
                          color: "#1e293b",
                        }}
                      >
                        {isProcessTable && processStage ? (
                          <button
                            type="button"
                            onClick={() =>
                              openProcessStage(
                                processStage,
                                Object.fromEntries(
                                  columns.map((column, index) => [column, row[index]])
                                )
                              )
                            }
                            className="font-semibold underline underline-offset-2"
                            style={{ color: "#2563eb" }}
                          >
                            View
                          </button>
                        ) : displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {hasKpiFooter && (
              <tfoot className="sticky bottom-0 z-20 bg-white shadow-[0_-1px_0_var(--c-border)]">
                <tr>
                  {columns.map((column, columnIndex) => {
                    const isMetric = metricField && String(column).toLowerCase() === metricField;
                    return (
                      <td
                        key={column}
                        className="font-semibold numeric"
                        style={{
                          padding: "12px",
                          borderTop: "2px solid #cbd5e1",
                          whiteSpace: "nowrap",
                          background: isMetric ? "#dbeafe" : "#fff",
                          color: isMetric ? "#1d4ed8" : "#334155",
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 0 : undefined,
                          zIndex: isMetric ? 30 : undefined,
                        }}
                      >
                        {isMetric
                          ? formatValue(reportMetricTotal)
                          : columnIndex === 0
                            ? "REPORT TOTAL"
                            : ""}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}



function ShipmentPerformanceMatrix({ filters, onOpenDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [shipmentTableExpanded, setShipmentTableExpanded] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [drillCustomerFilter, setDrillCustomerFilter] = useState("");
  const [drillStatus, setDrillStatus] = useState("all");
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post(
          "/ai/ceo-chat/shipment-performance",
          {
            from_date: filters?.dateFrom || undefined,
            to_date: filters?.dateTo || undefined,
          },
          { timeout: 35000 }
        );

        if (!cancelled) {
          setData(response?.data ?? response);
        }
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Shipment performance could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [filters?.dateFrom, filters?.dateTo]);

  const rows = Array.isArray(data?.customer_rows) ? data.customer_rows : [];

  const filteredRows = rows.filter((row) => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return true;

    return String(row?.CustomerName || "")
      .toLowerCase()
      .includes(q);
  });

  const total = Number(data?.summary?.eligible_pos || 0);
  const shipped = Number(data?.summary?.classified_shipped_pos || 0);
  const pending = Number(data?.summary?.pending_shipments || 0);

  const loadCustomerDrillDown = async (
    customerName = "",
    status = drillStatus
  ) => {
    try {
      const actualCustomer = String(customerName || "").trim();

      setSelectedCustomer(actualCustomer || "All Customers");
      setDrillCustomerFilter(actualCustomer);
      setDrillStatus(status);
      setDrillLoading(true);
      setDrillError("");
      setDrillData(null);

      const response = await api.post(
        "/ai/ceo-chat/shipment-performance/detail",
        {
          from_date: filters?.dateFrom || undefined,
          to_date: filters?.dateTo || undefined,
          customer_name: actualCustomer || undefined,
          promptness_status: status,
        },
        { timeout: 35000 }
      );

      setDrillData(response?.data ?? response);
    } catch (e) {
      setDrillData(null);
      setDrillError(
        e?.response?.data?.detail ||
          e?.message ||
          "Customer PO drill-down could not be loaded safely."
      );
    } finally {
      setDrillLoading(false);
    }
  };

  useEffect(() => {
    const handlePromptnessKpiDrill = (event) => {
      const status = event?.detail?.status;

      if (!["early", "on time", "delayed"].includes(status)) {
        return;
      }

      onOpenDetail?.({
        scope: "po",
        status,
        customer: "",
      });
    };

    const handleOnWaterKpiDrill = () => {
      onOpenDetail?.({
        scope: "on-water",
        status: "all",
        customer: "",
      });
    };

    const handleFdaKpiDrill = () => {
      onOpenDetail?.({
        scope: "fda",
        status: "all",
        customer: "",
      });
    };

    const handlePaymentsKpiDrill = () => {
      onOpenDetail?.({
        scope: "payments",
        status: "all",
        customer: "",
      });
    };

    window.addEventListener(
      "ceo-shipment-promptness-drill",
      handlePromptnessKpiDrill
    );

    window.addEventListener(
      "ceo-shipment-on-water-drill",
      handleOnWaterKpiDrill
    );

    window.addEventListener(
      "ceo-fda-release-hold-drill",
      handleFdaKpiDrill
    );

    window.addEventListener(
      "ceo-payments-received-pending-drill",
      handlePaymentsKpiDrill
    );

    return () => {
      window.removeEventListener(
        "ceo-shipment-promptness-drill",
        handlePromptnessKpiDrill
      );

      window.removeEventListener(
        "ceo-shipment-on-water-drill",
        handleOnWaterKpiDrill
      );

      window.removeEventListener(
        "ceo-fda-release-hold-drill",
        handleFdaKpiDrill
      );

      window.removeEventListener(
        "ceo-payments-received-pending-drill",
        handlePaymentsKpiDrill
      );
    };
  }, [onOpenDetail]);

  const closeCustomerDrillDown = () => {
    setSelectedCustomer("");
    setDrillCustomerFilter("");
    setDrillStatus("all");
    setDrillData(null);
    setDrillError("");
  };

  const exportCustomerDrillCsv = () => {
    const exportRows = Array.isArray(drillData?.rows)
      ? drillData.rows
      : [];

    if (!exportRows.length) return;

    if (drillData?.truncated) {
      setDrillError(
        "Export blocked because the drill-down result is truncated. Narrow the filter before downloading."
      );
      return;
    }

    const exportColumns = [
      "PoNo",
      "PoDate",
      "ShipByDate",
      "StockOutDate",
      "CustomerName",
      "BuyerName",
      "DestinationCountry",
      "PromptnessStatus",
      "VarianceDays",
    ];

    const csvEscape = (value) => {
      const s = value === null || value === undefined ? "" : String(value);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csv = [
      exportColumns.map(csvEscape).join(","),
      ...exportRows.map((row) =>
        exportColumns.map((column) => csvEscape(row?.[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeCustomer = String(selectedCustomer || "customer")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    a.href = url;
    a.download =
      `shipment-performance-${safeCustomer || "customer"}-${drillStatus.replace(/\s+/g, "-")}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ceo-block">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="ceo-block-title">
            Customer / Buyer-wise Shipment Performance
          </div>
          <div className="ceo-block-subtitle">
            Operational customer is mapped from Consignee. Buyer master remains
            separate where ERP BuyerID is populated. Promptness compares
            StockOutDate against the PO ShipByDate.
          </div>
        </div>

        {data?.ok && (
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: "#dcfce7", color: "#166534" }}
            >
              Technically Mapped
            </span>
            <span
              className="text-[11px]"
              style={{ color: "var(--c-text-muted)" }}
            >
              {data?.date_range?.from_date || "—"} →{" "}
              {data?.date_range?.to_date || "—"}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-sm text-slate-500">
          Loading customer shipment performance...
        </div>
      ) : error ? (
        <div
          className="rounded-xl border p-4 mt-4 text-sm"
          style={{
            background: "#fef2f2",
            borderColor: "#fecaca",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      ) : data?.ok ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
            {[
              {
                label: "Eligible POs",
                value: total,
                description: "POs eligible for shipment promptness analysis",
                footer: "View all eligible POs",
                scope: "po",
                status: "all",
              },
              {
                label: "Shipped / Classified",
                value: shipped,
                description: "POs classified as Early, On Time or Delayed",
                footer: "View classified shipped POs",
                scope: "po",
                status: "shipped",
              },
              {
                label: "Pending Shipments",
                value: pending,
                description: "Eligible POs without an actual StockOutDate",
                footer: "View pending shipment POs",
                scope: "po",
                status: "pending",
              },
              {
                label: "Customers",
                value: rows.length,
                description: "Operational customers in the selected ShipByDate period",
                footer: "View customer performance",
                scope: "customers",
                status: "all",
              },
            ].map((card) => (
              <button
                key={card.label}
                type="button"
                className="bg-white border rounded-2xl text-left overflow-hidden transition hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                style={{
                  borderColor: "var(--c-border)",
                  minHeight: "172px",
                }}
                onClick={() =>
                  onOpenDetail?.({
                    scope: card.scope,
                    status: card.status,
                    customer: "",
                  })
                }
              >
                <div
                  className="px-4 py-3 border-b font-semibold text-[13px]"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  {card.label}
                </div>

                <div className="px-4 py-4">
                  <div className="text-[30px] leading-none font-semibold text-slate-950">
                    {Number(card.value || 0).toLocaleString()}
                  </div>

                  <div className="text-[12.5px] text-slate-500 mt-3 leading-5 min-h-[40px]">
                    {card.description}
                  </div>

                  <div className="text-[11.5px] text-blue-600 mt-2">
                    {card.footer} →
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] text-slate-500">
              Rate denominator = POs with an actual StockOutDate in the selected
              ShipByDate period.
            </div>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer / buyer..."
              className="border rounded-lg px-3 py-2 text-sm min-w-[240px]"
            />
          </div>

          <div className="overflow-auto mt-3 border rounded-xl">
            <div className="flex justify-end p-2 border-b" style={{ borderColor: "var(--c-border)" }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    "ceo-shipment-performance-by-customer.csv",
                    [
                      "Customer / Buyer",
                      "Total POs",
                      "Early",
                      "On Time",
                      "Delayed",
                      "Pending",
                      "On / Before %",


                    ],
                    filteredRows.map((row) => [
                      row.CustomerName || "Unassigned",
                      Number(row.TotalPOs || 0).toLocaleString(),
                      Number(row.EarlyCount || 0).toLocaleString(),
                      Number(row.OnTimeCount || 0).toLocaleString(),
                      Number(row.DelayedCount || 0).toLocaleString(),
                      Number(row.PendingShipment || 0).toLocaleString(),
                      row.OnOrBeforeRatePct === null || row.OnOrBeforeRatePct === undefined
                        ? "—"
                        : `${Number(row.OnOrBeforeRatePct).toFixed(2)}%`,
                    ])
                  )
                }
                className="rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                style={{ borderColor: "var(--c-border)", background: "#fff" }}
                title="Download filtered shipment performance rows as CSV"
              >
                Download CSV
              </button>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left"
                  style={{ background: "var(--c-surface-soft)" }}
                >
                  <th className="px-3 py-3 whitespace-nowrap">Customer / Buyer</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Total POs</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Early</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">On Time</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Delayed</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Pending</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">
                    On / Before %
                  </th>
                </tr>
              </thead>

              <tbody>
                {(shipmentTableExpanded
                  ? filteredRows
                  : filteredRows.slice(0, 5)
                ).map((row, index) => (
                  <tr
                    key={`${row.CustomerName}-${index}`}
                    className="border-t cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      onOpenDetail?.({
                        scope: "po",
                        status: "all",
                        customer: row.CustomerName || "Unassigned",
                      })
                    }
                    title="Open full PO-level shipment performance"
                  >
                    <td className="px-3 py-3 min-w-[300px] font-medium">
                      <div className="flex items-center justify-between gap-3">
                        <span>{row.CustomerName || "Unassigned"}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenDetail?.({
                              scope: "po",
                              status: "all",
                              customer: row.CustomerName || "Unassigned",
                            });
                          }}
                          className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                          aria-label={`View PO records for ${row.CustomerName || "Unassigned"}`}
                        >
                          View POs →
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.TotalPOs || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.EarlyCount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.OnTimeCount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.DelayedCount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.PendingShipment || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right font-medium">
                      {row.OnOrBeforeRatePct === null ||
                      row.OnOrBeforeRatePct === undefined
                        ? "—"
                        : `${Number(row.OnOrBeforeRatePct).toFixed(2)}%`}
                    </td>
                  </tr>
                ))}

                {filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No customer shipment performance rows match the current
                      search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {filteredRows.length > 5 && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  onClick={() =>
                    setShipmentTableExpanded((current) => !current)
                  }
                  className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                  style={{
                    borderColor: "var(--c-border)",
                    background: "#fff",
                  }}
                >
                  {shipmentTableExpanded
                    ? "Collapse"
                    : `Expand · Show all ${filteredRows.length.toLocaleString()} rows`}
                </button>
              </div>
            )}
          </div>

          <div
            className="mt-3 text-[11px] leading-5"
            style={{ color: "var(--c-text-muted)" }}
          >
            Early = StockOutDate before ShipByDate · On Time = same date ·
            Delayed = after ShipByDate · Pending = StockOutDate not recorded.
            On-water is maintained separately as a shipment lifecycle state.
          </div>
        </>
      ) : (
        <div className="py-8 text-sm text-slate-500">
          Shipment performance data is unavailable.
        </div>
      )}

    </div>
  );
}


function SectionNote({ children, tone = "info" }) {
  return (
    <div className={`ceo-section-note ${tone}`}>
      <AlertTriangle size={16} strokeWidth={1.8} />
      <div>{children}</div>
    </div>
  );
}

// R26 - Buyer total orders vs confirmed orders. Self-contained so it can follow
// the global buyer / country / plant filters as well as the dates, and hand the
// same context to its page; the shared special-widget loader only tracks dates.
function BuyerOrderCountsCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const plant = filters?.plant || "";
  const buyer = filters?.buyer || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/buyer-order-counts", { from_date: dateFrom, to_date: dateTo, consignee, country, plant, buyer, limit: 500 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load buyer order counts."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, consignee, country, plant, buyer]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    if (plant) params.set("plant", plant);
    if (buyer) params.set("buyer", buyer);
    const qs = params.toString();
    nav(`/ceo-command-center/buyer-order-analysis${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const top = (data?.buyers || []).slice(0, 10);
  const maxTotal = Math.max(0, ...top.map((b) => b.total));
  const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
  const confirmedName = data?.confirmed_name || "Po Received";
  const scope = [consignee, buyer, country, plant].filter(Boolean).join(" · ");
  const withheld = data && !data.publishable && (t.orders || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Buyer Total Orders vs Confirmed Orders</div>
          <div className="mt-1 text-sm text-slate-600">
            How many orders each buyer raised and how many were confirmed — an order counts as confirmed once the ERP marks it “{confirmedName}”, the point at which the purchase order is recorded.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          View Details
        </button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading buyer order counts…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Validation check failed — figures withheld until the ERP data matches the validated structure. {data.reconciliation?.equation}
        </div>
      ) : null}
      {!loading && data && !withheld && !(t.orders || 0) ? (
        <div className="mt-4 text-sm text-slate-500">No orders match the selected filters.</div>
      ) : null}

      {!loading && data && !withheld && (t.orders || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Total Orders", fmt(t.orders), "Every order raised, any status", "#0f172a"],
              ["Confirmed Orders", fmt(t.confirmed), `ERP status “${confirmedName}”`, "#2563eb"],
              ["Not Confirmed", fmt(t.not_confirmed), "Pending, approved, cancelled or rejected", "#0f172a"],
              ["Confirmation Rate", t.confirmation_pct == null ? "—" : `${t.confirmation_pct}%`, "Confirmed ÷ total orders", "#0f172a"],
              ["Buyers", fmt(t.buyers), `${fmt(t.buyers_with_confirmed)} with a confirmed order`, "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Top buyers by orders</div>
            <div className="mt-0.5 text-[11px] text-slate-500">Blue = confirmed · grey = not confirmed. Bar length is the buyer's total orders.</div>
            <div className="mt-2 space-y-1.5">
              {top.map((b) => (
                <div key={b.consignee_id} className="flex items-center gap-3">
                  <div className="w-56 shrink-0 truncate text-xs text-slate-700" title={b.buyers ? `${b.consignee} (${b.buyers})` : b.consignee}>{b.consignee}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${maxTotal ? (b.confirmed / maxTotal) * 100 : 0}%`, backgroundColor: "#2563eb" }} />
                    <div style={{ width: `${maxTotal ? (b.not_confirmed / maxTotal) * 100 : 0}%`, backgroundColor: "#cbd5e1" }} />
                  </div>
                  <div className="w-44 shrink-0 text-right text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">{fmt(b.confirmed)}</span> of {fmt(b.total)} confirmed
                    {b.confirmation_pct != null ? <span className="text-slate-500"> · {b.confirmation_pct}%</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: order (proforma invoice) date{dateFrom || dateTo ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : " · all dates"}{scope ? ` · ${scope}` : ""}.
            {" "}Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}. Buyer is the consignee on the order, as on the ERP order list.
          </div>
        </>
      ) : null}
    </div>
  );
}

// R54 - Sale price vs actual price (ERP Price Book: Sale Invoice Price vs
// Invoice Price (DDP)). Self-contained so it follows the global date / buyer /
// consignee / country filters and hands the same context to its page.
function SaleVsActualPriceCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const consignee = filters?.consignee || "";
  const buyer = filters?.buyer || "";
  const country = filters?.country || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/sale-vs-actual-price", { from_date: dateFrom, to_date: dateTo, consignee, buyer, country })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the price comparison."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, consignee, buyer, country]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (consignee) params.set("consignee", consignee);
    if (buyer) params.set("buyer", buyer);
    if (country) params.set("country", country);
    const qs = params.toString();
    nav(`/ceo-command-center/sale-vs-actual-price${qs ? `?${qs}` : ""}`);
  };

  const groups = data?.groups || [];
  const g = data?.primary != null ? groups[data.primary] : null;
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN");
  const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const withheld = data && !data.publishable && groups.some((x) => x.lines > 0);
  const terms = g ? g.by_terms.filter((t) => t.comparable_lines > 0).slice(0, 6) : [];
  const maxQty = Math.max(0, ...terms.map((t) => t.quantity));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Sale Price vs Actual Price</div>
          <div className="mt-1 text-sm text-slate-600">
            The price we invoice against the actual price behind it on every order line, as on the ERP Price Book. On DDP orders to the USA the invoice price is grossed up for US duties and tariffs; the actual price is what remains before them.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          View Details
        </button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading price comparison…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}
        </div>
      ) : null}
      {!loading && data && !withheld && !g ? <div className="mt-4 text-sm text-slate-500">No order lines match the selected filters.</div> : null}

      {!loading && data && !withheld && g ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Sale Invoice Price", `${g.currency} ${fmt2(g.weighted_sale_price)}/${g.unit}`, "Weighted average, as invoiced", "#64748b"],
              ["Actual (DDP) Price", `${g.currency} ${fmt2(g.weighted_ddp_price)}/${g.unit}`, "Weighted average, before US duties", "#2563eb"],
              ["Difference per Unit", `${g.currency} ${fmt2(g.price_difference)}`, g.difference_pct == null ? "" : `${g.difference_pct}% of the sale price`, "#0f172a"],
              ["Value Difference", `${g.currency} ${fmt0(g.value_difference)}`, `Sale ${fmt0(g.sale_value)} vs actual ${fmt0(g.ddp_value)}`, "#0f172a"],
              ["Lines Compared", `${fmt0(g.comparable_lines)} of ${fmt0(g.lines)}`, `${fmt0(g.orders)} orders · ${fmt0(g.buyers)} buyers`, "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By shipping terms — {g.currency} per {g.unit}</div>
            <div className="mt-0.5 text-[11px] text-slate-500">Bar length is the weight compared. DDP orders carry the duty gap; CFR / CIF lines should show the two prices equal.</div>
            <div className="mt-2 space-y-1.5">
              {terms.map((t) => (
                <div key={t.key} className="flex items-center gap-3">
                  <div className="w-16 shrink-0 text-xs font-medium text-slate-700">{t.key}</div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${maxQty ? (t.quantity / maxQty) * 100 : 0}%`, backgroundColor: "#2563eb", height: "100%" }} />
                  </div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700">
                    sale <span className="font-semibold text-slate-900">{fmt2(t.weighted_sale_price)}</span> · actual <span className="font-semibold" style={{ color: "#2563eb" }}>{fmt2(t.weighted_ddp_price)}</span>
                    {t.difference_pct != null ? <span className="text-slate-500"> · gap {t.difference_pct}%</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: order (PO) date{dateFrom || dateTo ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : " · all dates"}{[consignee, buyer, country].filter(Boolean).length ? ` · ${[consignee, buyer, country].filter(Boolean).join(" · ")}` : ""}.
            {groups.length > 1 ? ` Showing ${g.currency}/${g.unit}; ${groups.length - 1} other currency/unit group(s) on the detail page.` : ""}
            {" "}Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
            {data.business_validation === "pending" ? <span className="ml-1 rounded border border-amber-200 bg-amber-50 px-1 text-amber-800">business validation pending</span> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R56 - Monthly consumables usage by plant (salt & chemicals, ERP store
// consumption entries). Self-contained so it follows the global date / plant
// filters and hands the same context to its page.
function ConsumablesUsageCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/consumables-expense-approved", { from_date: dateFrom, to_date: dateTo, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load consumables usage."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/consumables-usage-by-plant${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const expense = data?.expense || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const fmtMoney = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const withheld = data && !data.publishable && (t.entries || 0) > 0;
  const months = (data?.months || []).slice(-6);
  const plants = (data?.plants || []).slice(0, 6);
  const cell = {}; (data?.matrix || []).forEach((x) => { cell[`${x.month}|${x.plant}`] = x.kg; });
  const maxCell = Math.max(0, ...months.flatMap((m) => plants.map((p) => cell[`${m}|${p.plant}`] || 0)));
  const ml = (m) => { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "short", year: "2-digit" }); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Consumables Usage by Plant — Salt &amp; Chemicals</div>
          <div className="mt-1 text-sm text-slate-600">
            Monthly plant usage and expense for salt, processing chemicals and approved supplemental consumables such as masks, blades and scissors. ERP quantities remain the source for recorded chemical/salt usage; supplemental costs are shown only after explicit approval in Client Requirements.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          View Details
        </button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading consumables usage…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}
        </div>
      ) : null}
      {!loading && data && !withheld && !(t.entries || 0) ? <div className="mt-4 text-sm text-slate-500">No consumption entries match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.entries || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Consumed", `${fmt0(t.kg)} kg`, `${fmt0(t.bags)} bags`, "#2563eb"],
              ["Per Month", t.avg_kg_per_month == null ? "—" : `${fmt0(t.avg_kg_per_month)} kg`, `Average over ${t.months} month(s)`, "#0f172a"],
              ["Plants", fmt0(t.plants), "With consumption recorded", "#0f172a"],
              ["Chemicals", fmt0(t.chemicals), "Including salt", "#0f172a"],
              ["Expense Value", expense.published ? `INR ${fmtMoney(expense.total_value)}` : "Pending", expense.published ? `ERP chemicals INR ${fmtMoney(expense.erp_chemical_value)} · manual INR ${fmtMoney(expense.manual_consumables_value)}` : "Approve costs/manual usage in Client Requirements", expense.published ? "#059669" : "#b45309"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Kilograms consumed — last {months.length} month(s) in scope, by plant</div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead><tr className="border-b border-slate-200 uppercase tracking-wide text-slate-500">
                  <th className="py-1.5 pr-3">Plant</th>{months.map((m) => <th key={m} className="whitespace-nowrap py-1.5 pr-3 text-right">{ml(m)}</th>)}<th className="py-1.5 pr-3 text-right">Period</th>
                </tr></thead>
                <tbody>{plants.map((p) => (
                  <tr key={p.plant} className="border-b border-slate-100">
                    <td className="whitespace-nowrap py-1.5 pr-3 font-medium text-slate-800">{p.plant}</td>
                    {months.map((m) => { const v = cell[`${m}|${p.plant}`] || 0; const a = maxCell ? 0.08 + (v / maxCell) * 0.55 : 0; return (
                      <td key={m} className="whitespace-nowrap py-1.5 pr-3 text-right text-slate-800" style={{ backgroundColor: v ? `rgba(37, 99, 235, ${a.toFixed(2)})` : undefined }}>{v ? fmt0(v) : "—"}</td>); })}
                    <td className="whitespace-nowrap py-1.5 pr-3 text-right font-semibold text-slate-900">{fmt0(p.kg)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: consumption entry date{dateFrom || dateTo ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : " · all dates"}{plant ? ` · ${plant}` : ""}. Transfers between plants ({fmt0(t.transfer_entries)}) are not counted.
            {" "}Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.{" "}
            <span className="ml-1 rounded border border-amber-200 bg-amber-50 px-1 text-amber-800">expense value pending business validation</span>
          </div>
        </>
      ) : null}
    </div>
  );
}

// R57 - Raw material arrivals by count at purchase centres ("how much 42-count
// arrived at a centre today?"). Self-contained: follows the global dates (a
// single day when none are set - today), with its own count box, and hands the
// context to its page.
function RmArrivalsByCountCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [count, setCount] = useState("");
  const [countApplied, setCountApplied] = useState("");
  const [dayOverride, setDayOverride] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";
  const todayIso = new Date().toISOString().slice(0, 10);
  const singleDay = dayOverride || (!dateFrom && !dateTo ? todayIso : (dateFrom && dateFrom === dateTo ? dateFrom : ""));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const body = singleDay ? { date: singleDay, count: countApplied, plant } : { from_date: dateFrom, to_date: dateTo, count: countApplied, plant };
    api
      .post("/ai/ceo-chat/rm-arrivals-by-count", body)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load arrivals."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [singleDay, dateFrom, dateTo, countApplied, plant]);
  useEffect(() => { setDayOverride(""); }, [dateFrom, dateTo]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (singleDay) params.set("date", singleDay); else { if (dateFrom) params.set("from", dateFrom); if (dateTo) params.set("to", dateTo); }
    if (countApplied) params.set("count", countApplied);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/rm-arrivals-by-count${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const fmt1 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const withheld = data && !data.publishable && (t.lines || 0) > 0;
  const empty = data && data.publishable && (t.lines || 0) === 0;
  const latest = t.latest_arrival_date_in_scope || t.latest_arrival_date_overall || "";
  const centres = (data?.by_centre || []).slice(0, 8);
  const maxKg = Math.max(0, ...centres.map((c) => c.kg));
  const countTxt = countApplied ? `${countApplied}-count` : "all counts";
  const noneTxt = countApplied ? `No ${countApplied}-count arrival` : "No arrival";
  const lastTxt = countApplied ? `The last ${countApplied}-count arrival` : "The last arrival";
  const scope = data?.filters?.scope || "";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Raw Material Arrivals by Count</div>
          <div className="mt-1 text-sm text-slate-600">
            How much shrimp of a given count arrived at each purchase centre on the day, from the ERP arrival register. Type a count (for example 42) to see just that count; counts are matched exactly, as in the ERP count-wise report.
          </div>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Count
            <input type="number" step="0.5" min="0" value={count} onChange={(e) => setCount(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setCountApplied(count); }} placeholder="e.g. 42"
              className="mt-1 block w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-800" /></label>
          <button type="button" onClick={() => setCountApplied(count)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Apply</button>
          <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
        </div>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading arrivals…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}
        </div>
      ) : null}
      {!loading && empty ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          {noneTxt} is recorded at any centre {scope}.
          {latest ? <> {lastTxt} in the register was on <strong>{latest}</strong>. <button type="button" onClick={() => setDayOverride(latest)} className="text-blue-700 underline">Show that day</button></> : null}
        </div>
      ) : null}

      {!loading && data && !withheld && (t.lines || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Arrived", `${fmt1(t.kg)} kg`, `${countTxt} · ${scope}`, "#2563eb"],
              ["Arrivals", fmt0(t.arrivals), `${fmt0(t.lines)} arrival line(s)`, "#0f172a"],
              ["Centres", fmt0(t.centres), "With arrivals in scope", "#0f172a"],
              ["Counts", fmt0(t.counts), "Distinct counts", "#0f172a"],
              ["Suppliers", fmt0(t.suppliers), "Distinct suppliers", "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By purchase centre — {countTxt}</div>
            <div className="mt-2 space-y-1.5">
              {centres.map((c) => (
                <div key={c.centre} className="flex items-center gap-3">
                  <div className="w-48 shrink-0 truncate text-xs text-slate-700" title={c.centre}>{c.centre} <span className="text-slate-400">({c.code})</span></div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxKg ? (c.kg / maxKg) * 100 : 0}%`, backgroundColor: "#2563eb", height: "100%" }} /></div>
                  <div className="w-52 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold text-slate-900">{fmt1(c.kg)} kg</span> · {fmt0(c.arrivals)} arrival(s) · {fmt0(c.counts)} count(s)</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: arrival date at the purchase centre · {scope}{plant ? ` · plant ${plant}` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

// R50 - MPEDA farm certificates behind EU orders. Self-contained: open EU
// orders by default, follows the global dates / consignee / buyer / country /
// plant filters and hands the same context to its page.
function EuFarmCertificatesCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const consignee = filters?.consignee || "";
  const buyer = filters?.buyer || "";
  const country = filters?.country || "";
  const plant = filters?.plant || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/eu-farm-certificates", { scope: "open", from_date: dateFrom, to_date: dateTo, consignee, buyer, country, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the EU farm certificate check."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, consignee, buyer, country, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    params.set("scope", "open");
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (consignee) params.set("consignee", consignee);
    if (buyer) params.set("buyer", buyer);
    if (country) params.set("country", country);
    if (plant) params.set("plant", plant);
    nav(`/ceo-command-center/eu-farm-certificates?${params.toString()}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const withheld = data && !data.publishable && (t.eu_orders || 0) > 0;
  const states = (data?.by_state || []).filter((s) => s.orders > 0);
  const countries = (data?.by_country || []).slice(0, 6);
  const maxOrders = Math.max(0, ...countries.map((c) => c.orders));
  const nonEuCountry = country && !(data?.options?.countries || []).includes(country) && data?.options?.countries?.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">MPEDA Farm Certificates for EU Orders</div>
          <div className="mt-1 text-sm text-slate-600">
            For open orders going to an EU country: which farms are allocated in the QC code list and whether each farm's MPEDA/CAA certificate is in place, complete and in date. Orders with no farms allocated yet cannot be certified.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Checking EU orders…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.eu_orders || 0) ? <div className="mt-4 text-sm text-slate-500">{nonEuCountry ? `${country} is not an EU destination — no EU orders to check.` : "No open EU orders match the selected filters."}</div> : null}

      {!loading && data && !withheld && (t.eu_orders || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Open EU Orders", fmt0(t.eu_orders), `${fmt0(t.countries)} countr${t.countries === 1 ? "y" : "ies"}`, "#0f172a"],
              ["Needing Attention", fmt0(t.needs_attention), "Certificates not fully in place", "#b45309"],
              ["No Farms Allocated", fmt0(t.no_farms), "No QC code list yet", "#0f172a"],
              ["Expired Certificates", fmt0(data.farm_summary?.expired), `farm links · ${fmt0(data.farm_summary?.no_expiry)} without expiry date`, data.farm_summary?.expired ? "#b45309" : "#0f172a"],
              ["Certified & Valid", fmt0(t.certified), "Every farm certified, in date", "#059669"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold text-slate-700">By certificate state</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {states.map((s) => (
                  <span key={s.state} className={`rounded-full border px-3 py-1 text-xs ${s.needs_attention ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>{s.label}: <strong>{fmt0(s.orders)}</strong></span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700">By EU country — orders needing attention / all</div>
              <div className="mt-2 space-y-1.5">
                {countries.map((c) => (
                  <div key={c.key} className="flex items-center gap-3">
                    <div className="w-28 shrink-0 truncate text-xs text-slate-700">{c.key}</div>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxOrders ? (c.needs_attention / maxOrders) * 100 : 0}%`, backgroundColor: "#b45309", height: "100%" }} /></div>
                    <div className="w-24 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold text-slate-900">{fmt0(c.needs_attention)}</span> / {fmt0(c.orders)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: order (PO) date{dateFrom || dateTo ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : " · all open orders"}. Certificate expiry judged against today. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
            {data.business_validation === "pending" ? <span className="ml-1 rounded border border-amber-200 bg-amber-50 px-1 text-amber-800">business validation pending</span> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R51 - Total volume vs finished product by type (HL / raw / cooked / blanched),
// on the same traced head-on -> finished chain as the head-on vs finished card.
// Self-contained: follows the global dates and plant, hands them to its page.
function FinishedProductVolumeCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/finished-product-volume-approved", { from_date: dateFrom, to_date: dateTo, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the finished product comparison."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/finished-product-volume${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct1 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const colors = { hl: "#0891b2", raw: "#2563eb", cooked: "#d97706", blanched: "#7c3aed", headon: "#059669", unclassified: "#94a3b8" };
  const cats = (t.by_category || []).filter((c) => c.finished_kg > 0);
  const withheld = data && !data.publishable && (t.records || 0) > 0;
  const breaded = data?.breaded || null;
  const main = ["hl", "raw", "cooked", "blanched"].map((k) => cats.find((c) => c.category === k) || null);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Total Volume vs Finished Product — HL / Raw / Cooked / Breaded</div>
          <div className="mt-1 text-sm text-slate-600">
            How much head-on raw material went in and how much came out as each finished-product type, traced through ERP processing records. Raw, cooked, HL and blanched remain ERP-native; Breaded is included only when its ERP aliases are explicitly approved in Client Requirements.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Tracing head-on to finished product…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.head_on_kg || 0) ? <div className="mt-4 text-sm text-slate-500">No graded lots match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.head_on_kg || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-7">
            {[
              ["Total Volume (head-on)", `${fmt0(t.head_on_kg)} kg`, `${fmt0(t.lots)} lots graded`, "#0f172a"],
              ["Finished Product", `${fmt0(t.finished_kg)} kg`, `${pct1(t.yield_finished_pct)} of head-on`, "#2563eb"],
              ...main.map((c, i) => [["HL (shell-on)", "Raw (peeled)", "Cooked", "Blanched"][i], c ? `${fmt0(c.finished_kg)} kg` : "0 kg", c ? `${pct1(c.share_pct)} of finished · ${pct1(c.pct_of_head_on)} of head-on` : "none in period", c ? colors[c.category] : "#94a3b8"]),
              ["Breaded", breaded?.published ? `${fmt0(breaded.finished_kg)} kg` : "Pending", breaded?.published ? `Approved ERP alias mapping · ${breaded.aliases?.join(", ") || "configured"}` : "Approve ERP aliases in Client Requirements", breaded?.published ? "#059669" : "#b45309"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Where the head-on volume went</div>
            <div className="mt-2 flex h-4 w-full overflow-hidden rounded-full bg-slate-100">
              {cats.map((c) => <div key={c.category} style={{ width: `${(c.finished_kg / t.head_on_kg) * 100}%`, backgroundColor: colors[c.category] || "#94a3b8", height: "100%" }} title={`${c.label}: ${fmt0(c.finished_kg)} kg`} />)}
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-600">
              {cats.map((c) => <span key={c.category}><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: colors[c.category] }} />{c.label} {pct1(c.pct_of_head_on)}</span>)}
              <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-slate-200 align-middle" />process loss &amp; untraced {pct1(100 - (t.yield_finished_pct || 0))}</span>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: grading date of the head-on lots{dateFrom || dateTo ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : " · all dates"}{plant ? ` · ${plant}` : ""}. Finished kg include rejection ({fmt0(t.rejection_kg)} kg), as the ERP report counts them. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

// R52 - Ship-date promptness by plant, on the validated shipment-performance
// rule. Self-contained: follows the global dates / plant / consignee / country
// (defaults to the current financial year when no dates are set) and hands
// the same context to its page.
function ShipPromptnessByPlantCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/ship-promptness-by-plant", { from_date: dateFrom, to_date: dateTo, plant, consignee, country })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load ship-date promptness."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant, consignee, country]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    const qs = params.toString();
    nav(`/ceo-command-center/ship-promptness-by-plant${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct1 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const colors = { early: "#059669", on_time: "#2563eb", delayed: "#dc2626", pending: "#cbd5e1" };
  const plants = data?.by_plant || [];
  const withheld = data && !data.publishable && (t.orders || 0) > 0;
  const period = data?.filters || {};

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Ship-Date Promptness — by Plant</div>
          <div className="mt-1 text-sm text-slate-600">
            For every plant, how many of the orders due to ship in the period went out early, on the ship-by date, or late — the same early / on-time / delayed rule as the Shipment Performance view, compared plant against plant.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading ship-date promptness…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.orders || 0) ? <div className="mt-4 text-sm text-slate-500">No orders were due to ship in this period for the selected filters.</div> : null}

      {!loading && data && !withheld && (t.orders || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Orders Due", fmt0(t.orders), `${fmt0(t.shipped)} shipped · ${fmt0(t.pending)} not yet`, "#0f172a"],
              ["Shipped On / Before", pct1(t.on_or_before_pct), `${fmt0(t.on_or_before)} of ${fmt0(t.shipped)} shipped`, colors.early],
              ["Early", fmt0(t.early), pct1(t.early_pct), colors.early],
              ["On Time", fmt0(t.on_time), pct1(t.on_time_pct), colors.on_time],
              ["Delayed", fmt0(t.delayed), `${pct1(t.delayed_pct)} · avg ${Number(t.avg_delay_days || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 })} days late`, colors.delayed],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Plants compared — green early · blue on time · red delayed · grey not yet shipped</div>
            <div className="mt-2 space-y-1.5">
              {plants.map((p) => (
                <div key={p.plant} className="flex items-center gap-3">
                  <div className="w-44 shrink-0 truncate text-xs text-slate-700" title={p.plant}>{p.plant}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    {["early", "on_time", "delayed", "pending"].map((k) => <div key={k} style={{ width: `${p.orders ? (p[k] / p.orders) * 100 : 0}%`, backgroundColor: colors[k], height: "100%" }} title={`${k.replace("_", " ")}: ${p[k]}`} />)}
                  </div>
                  <div className="w-64 shrink-0 text-right text-xs text-slate-700">
                    <span className="font-semibold" style={{ color: colors.early }}>{pct1(p.on_or_before_pct)}</span> on/before · <span className="font-semibold" style={{ color: colors.delayed }}>{fmt0(p.delayed)}</span> late of {fmt0(p.orders)}{p.avg_delay_days != null ? <span className="text-slate-500"> · avg {Number(p.avg_delay_days).toLocaleString("en-IN", { maximumFractionDigits: 0 })} d late</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            Date basis: ship-by date · {period.period_defaulted_to_current_fy ? `current financial year ${period.from_date} to ${period.to_date}` : `${period.from_date || "…"} to ${period.to_date || "…"}`}{[plant, consignee, country].filter(Boolean).length ? ` · ${[plant, consignee, country].filter(Boolean).join(" · ")}` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function YearlyDispatchComparisonCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const yearType = filters?.periodMode === "Financial Year" ? "financial" : "calendar";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/yearly-dispatch-comparison", { year_type: yearType, date_basis: "dispatch", exporter: "DFL", plant, consignee, country })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the yearly comparison."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [yearType, plant, consignee, country]);

  const openPage = () => {
    const params = new URLSearchParams();
    params.set("year_type", yearType);
    params.set("exporter", "DFL");
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    nav(`/ceo-command-center/yearly-dispatch-comparison?${params.toString()}`);
  };

  const t = data?.totals || {};
  const years = data?.by_year || [];
  const latest = years.length ? years[years.length - 1] : null;
  const sp = data?.same_period;
  const spLatest = sp ? sp.rows[sp.rows.length - 1] : null;
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const tonnes = (kgs) => Number((kgs || 0) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const change = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const changeColor = (v) => (v === null || v === undefined ? "#64748b" : v >= 0 ? "#059669" : "#dc2626");
  const colors = { volume: "#2563eb", containers: "#059669" };
  const maxKgs = Math.max(0, ...years.map((y) => y.kgs || 0));
  const maxCtr = Math.max(0, ...years.map((y) => y.containers || 0));
  const withheld = data && !data.publishable && (t.invoices || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Yearly DFL Comparison — Volume &amp; Containers</div>
          <div className="mt-1 text-sm text-slate-600">
            Year against year, how much DFL shipped and in how many containers: every customs invoice that has left the cold store, with the ERP's net weight per invoice and the container numbers on it.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading yearly comparison…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.invoices || 0) ? <div className="mt-4 text-sm text-slate-500">No dispatched customs invoices match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.invoices || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`${latest.year} Volume${sp ? " (to date)" : ""}`, `${tonnes(latest.kgs)} t`, latest.prev_year ? `${change(latest.kgs_change_pct)} vs ${latest.prev_year} full year` : "First year on record", colors.volume],
              [`${latest.year} Containers${sp ? " (to date)" : ""}`, fmt0(latest.containers), latest.prev_year ? `${change(latest.containers_change_pct)} vs ${latest.prev_year} full year` : "First year on record", colors.containers],
              [sp ? `Same period ${sp.months}` : "Previous year", sp && spLatest ? `${change(spLatest.kgs_change_pct)} vol` : years.length > 1 ? `${tonnes(years[years.length - 2].kgs)} t` : "—", sp && spLatest ? `${change(spLatest.containers_change_pct)} containers vs ${sp.rows.length > 1 ? sp.rows[sp.rows.length - 2].year : "—"} ${sp.months}` : years.length > 1 ? `${fmt0(years[years.length - 2].containers)} containers` : "", sp && spLatest ? changeColor(spLatest.kgs_change_pct) : "#0f172a"],
              ["All Years", `${tonnes(t.kgs)} t`, `${fmt0(t.containers)} containers · ${fmt0(t.invoices)} shipments`, "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Year by year — blue volume (tonnes) · green containers</div>
            <div className="mt-2 space-y-2">
              {years.map((y) => (
                <div key={y.year} className="flex items-center gap-3">
                  <div className="w-16 shrink-0 text-xs font-medium text-slate-700">{y.year}{y === latest && sp ? <span className="block text-[10px] font-normal text-slate-400">to date</span> : null}</div>
                  <div className="flex-1 space-y-1">
                    <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxKgs ? (y.kgs / maxKgs) * 100 : 0}%`, backgroundColor: colors.volume, height: "100%" }} title={`${tonnes(y.kgs)} t`} /></div>
                    <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxCtr ? (y.containers / maxCtr) * 100 : 0}%`, backgroundColor: colors.containers, height: "100%" }} title={`${fmt0(y.containers)} containers`} /></div>
                  </div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700">
                    <span className="font-semibold" style={{ color: colors.volume }}>{tonnes(y.kgs)} t</span> <span style={{ color: changeColor(y.kgs_change_pct) }}>({change(y.kgs_change_pct)})</span> · <span className="font-semibold" style={{ color: colors.containers }}>{fmt0(y.containers)}</span> ctr <span style={{ color: changeColor(y.containers_change_pct) }}>({change(y.containers_change_pct)})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {yearType === "financial" ? "Financial years (Apr–Mar)" : "Calendar years"} of the stock-out date · exporter DFL · all years on record{[plant, consignee, country].filter(Boolean).length ? ` · ${[plant, consignee, country].filter(Boolean).join(" · ")}` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function StockFinanceExposureCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const variety = filters?.variety || "";
  const grade = filters?.grade || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/finance-cost-approved", { from_date: dateFrom, to_date: dateTo, plant, consignee, country, variety, grade })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the finance exposure."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant, consignee, country, variety, grade]);

  const openPage = (bucket) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    if (variety) params.set("variety", variety);
    if (grade) params.set("grade", grade);
    if (bucket) params.set("bucket", bucket);
    const qs = params.toString();
    nav(`/ceo-command-center/stock-finance-exposure${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const ccy = data?.currency || "";
  const fx = data?.exchange_rate || null;
  const bk = Object.fromEntries((data?.by_bucket || []).map((b) => [b.bucket, b]));
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const tonnes = (kgs) => Number((kgs || 0) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const dys = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })} d`);
  const colors = { dummy: "#b45309", awaiting_shipment: "#2563eb", value: "#059669" };
  const ages = ["0-30", "31-60", "61-90", "91-180", ">180"];
  const ageMcs = (bucket) => ages.map((a) => (data?.by_age || []).find((x) => x.age === a && x.bucket === bucket)?.mcs || 0);
  const financeCost = data?.finance_cost || {};
  const withheld = data && !data.publishable && (t.stock_rows || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Finance Cost — Product in Dummy / Awaiting Shipment</div>
          <div className="mt-1 text-sm text-slate-600">
            Finished product waiting in the cold store and tying up working capital: cartons packed as dummy with no order yet, and cartons packed for orders that have not shipped — how much, how long it has waited, and what it is worth where a price exists.
          </div>
        </div>
        <button type="button" onClick={() => openPage("")} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading cold-store exposure…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.stock_rows || 0) ? <div className="mt-4 text-sm text-slate-500">No stock in the cold store matches the selected filters.</div> : null}

      {!loading && data && !withheld && (t.stock_rows || 0) > 0 ? (
        <>
          <div className={`mt-4 rounded-xl border px-3 py-2 text-xs ${financeCost.published ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
            {financeCost.published ? (
              <><span className="font-semibold">Finance cost published using an approved Analytics rate.</span> Stock, value and ageing remain ERP-derived; the approved annual rate is {financeCost.annual_rate_pct}% and priced coverage remains visible.</>
            ) : (
              <><span className="font-semibold">Finance cost: business validation pending.</span> The ERP holds no finance rate for finished stock and a price exists for {t.priced_share_pct ?? 0}% of the cartons, so the cost is not published until an approved annual rate is saved in Client Requirements.</>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Dummy Stock", `${fmt0(bk.dummy?.mcs)} ctn`, `${tonnes(bk.dummy?.kgs)} t · avg ${dys(bk.dummy?.avg_days)} in store`, colors.dummy, "dummy"],
              ["Awaiting Shipment", `${fmt0(bk.awaiting_shipment?.mcs)} ctn`, `${tonnes(bk.awaiting_shipment?.kgs)} t · avg ${dys(bk.awaiting_shipment?.avg_days)} · ${fmt0(bk.awaiting_shipment?.overdue_mcs)} past ship-by`, colors.awaiting_shipment, "awaiting_shipment"],
              ["Priced Value", `${ccy} ${fmt0(t.priced_value)}`, `${t.priced_share_pct ?? 0}% of cartons priced${fx ? ` · ≈ INR ${fmt0(t.priced_value * fx)}` : ""}`, colors.value, ""],
              ["Finance Cost", financeCost.published ? `${financeCost.currency || ccy} ${fmt0(financeCost.total)}` : "Pending", financeCost.published ? `${financeCost.annual_rate_pct}% p.a. approved supplemental rate` : "Approve annual rate in Client Requirements", financeCost.published ? "#059669" : "#94a3b8", ""],
            ].map(([label, value, sub, color, bucket]) => (
              <div key={label} className={`rounded-xl border border-slate-200 p-3${bucket ? " cursor-pointer hover:bg-slate-50" : ""}`} onClick={bucket ? () => openPage(bucket) : undefined}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">How long it has waited — cartons by days in store (0-30 · 31-60 · 61-90 · 91-180 · over 180)</div>
            <div className="mt-2 space-y-1.5">
              {[["Dummy", "dummy"], ["Awaiting shipment", "awaiting_shipment"]].map(([label, bucket]) => { const parts = ageMcs(bucket); const total = parts.reduce((a, b) => a + b, 0); return (
                <div key={bucket} className="flex items-center gap-3">
                  <div className="w-36 shrink-0 text-xs text-slate-700">{label}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    {parts.map((v, i) => <div key={ages[i]} style={{ width: `${total ? (v / total) * 100 : 0}%`, backgroundColor: colors[bucket], opacity: 0.35 + i * 0.16, height: "100%" }} title={`${ages[i]} days: ${fmt0(v)} cartons`} />)}
                  </div>
                  <div className="w-64 shrink-0 text-right text-xs text-slate-700">{parts.map((v, i) => <span key={ages[i]} className={i ? "ml-2" : ""} title={`${ages[i]} days`}>{fmt0(v)}</span>)}</div>
                </div>); })}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of today, aged from the production date{dateFrom || dateTo ? ` · produced ${dateFrom || "…"} to ${dateTo || "…"}` : ""}{[plant, consignee, country, variety, grade].filter(Boolean).length ? ` · ${[plant, consignee, country, variety, grade].filter(Boolean).join(" · ")}` : ""}. {fmt0(t.mcs)} cartons / {tonnes(t.kgs)} t in store in all. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function PackingMaterialContractsCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const grade = filters?.grade || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/packing-material-availability-approved", { preset: "sysco", plant, consignee, country, grade })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the packing-material view."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [plant, consignee, country, grade]);

  const openPage = () => {
    const params = new URLSearchParams();
    params.set("preset", "sysco");
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    if (grade) params.set("grade", grade);
    nav(`/ceo-command-center/packing-material-contracts?${params.toString()}`);
  };

  const t = data?.totals || {};
  const stock = data?.packing_material_stock || {};
  const styles = data?.by_style_with_stock || data?.by_style || [];
  const stockApproved = stock.status === "approved_supplemental";
  const availableStock = styles.reduce((sum, row) => sum + Number(row.available_cartons || 0), 0);
  const shortageStock = styles.reduce((sum, row) => sum + Number(row.shortage_cartons || 0), 0);
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct1 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const colors = { packed: "#059669", balance: "#2563eb" };
  const withheld = data && !data.publishable && (t.lines || 0) > 0;
  const brands = (data?.options?.presets || []).find((p) => p.key === "sysco")?.brands || [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Packing Material for Sysco Contracts</div>
          <div className="mt-1 text-sm text-slate-600">
            What the open Sysco (Portico-brand) contracts still need to be packed in: cartons ordered, cartons already packed and in the cold store, and the balance still to pack — the printed master cartons and inner packs that must be on hand.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading packing-material requirement…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.lines || 0) ? <div className="mt-4 text-sm text-slate-500">No open Sysco contract lines match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.lines || 0) > 0 ? (
        <>
          <div className={`mt-4 rounded-xl border px-3 py-2 text-xs ${stockApproved ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
            {stockApproved ? (
              <><span className="font-semibold">Packing-material stock is supplied through approved Analytics business inputs.</span> Contract requirements remain ERP-derived. Available and shortage quantities below are calculated against the approved supplemental stock.{brands.length ? ` Sysco is identified by ${brands.join(", ")}.` : ""}</>
            ) : (
              <><span className="font-semibold">Packing material in stock: not recorded in the ERP.</span> The packaging inventory and packing-store modules are empty, so stock-on-hand remains pending until approved supplemental stock is saved in Client Requirements.{brands.length ? ` Sysco is identified by its brands ${brands.join(", ")} (to be confirmed).` : ""}</>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Open Contracts", fmt0(t.contracts), `${fmt0(t.lines)} lines`, "#0f172a"],
              ["Cartons Ordered", fmt0(t.ordered_mcs), "master cartons", "#475569"],
              ["Packed in Store", fmt0(t.packed_mcs), `${pct1(t.packed_pct)} of ordered`, colors.packed],
              ["Still to Pack", fmt0(t.balance_mcs), `printed master cartons needed · ${fmt0(t.inner_packs_needed)} inner packs`, colors.balance],
              ["Material in Stock", stockApproved ? fmt0(availableStock) : "Not recorded", stockApproved ? `${fmt0(shortageStock)} carton shortage against open requirement` : (stock.status === "erp_data_gap" ? "ERP inventory empty" : "awaiting validation"), stockApproved ? "#059669" : "#94a3b8"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By brand and packing style — green packed · blue still to pack</div>
            <div className="mt-2 space-y-1.5">
              {styles.slice(0, 6).map((s) => (
                <div key={`${s.brand}|${s.packing_style}`} className="flex items-center gap-3">
                  <div className="w-64 shrink-0 truncate text-xs text-slate-700" title={`${s.brand} · ${s.packing_style}`}>{s.brand} · {s.packing_style}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${s.ordered_mcs ? (s.packed_mcs / s.ordered_mcs) * 100 : 0}%`, backgroundColor: colors.packed, height: "100%" }} title={`packed ${fmt0(s.packed_mcs)}`} />
                    <div style={{ width: `${s.ordered_mcs ? (s.balance_mcs / s.ordered_mcs) * 100 : 0}%`, backgroundColor: colors.balance, height: "100%" }} title={`still to pack ${fmt0(s.balance_mcs)}`} />
                  </div>
                  <div className="w-80 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: colors.balance }}>{fmt0(s.balance_mcs)}</span> needed · {stockApproved && s.available_cartons != null ? `${fmt0(s.available_cartons)} available · ${fmt0(s.shortage_cartons)} short · ` : ""}{fmt0(s.inner_packs_needed)} inner packs ({fmt0(s.inner_per_carton)}/ctn) · {fmt0(s.contracts)} contracts</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of today, open contracts only{[plant, consignee, country, grade].filter(Boolean).length ? ` · ${[plant, consignee, country, grade].filter(Boolean).join(" · ")}` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function ChemicalStockCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const unit = plant || "2";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/chemical-stock", { unit, chemical: "carfisol" })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the chemical stock."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [unit]);

  const openPage = (chemical) => {
    const params = new URLSearchParams();
    params.set("unit", unit);
    params.set("chemical", chemical == null ? "carfisol" : chemical);
    nav(`/ceo-command-center/chemical-stock?${params.toString()}`);
  };

  const h = data?.headline || null;
  const ut = data?.unit_totals || {};
  const unitName = data?.resolved?.unit?.name || (plant ? plant : "Unit 2");
  const chems = data?.unit_chemicals || [];
  const kgFmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const color = (v) => (v < 0 ? "#dc2626" : v > 0 ? "#2563eb" : "#64748b");
  const withheld = data && !data.publishable && (ut.chemicals || 0) > 0;
  const maxStock = Math.max(0, ...chems.map((c) => c.stock_kgs || 0));
  const stale = (d) => (d == null ? "" : d > 90 ? ` · no movement for ${fmt0(d)} days` : ` · ${fmt0(d)} days ago`);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Chemical Stock — Carfisol at {unitName}</div>
          <div className="mt-1 text-sm text-slate-600">
            How much treatment chemical and salt the unit has on hand: everything received (purchases, opening stock, transfers in) less everything used (production and transfers out) — the ERP chemical balance rule, chemical by chemical.
          </div>
        </div>
        <button type="button" onClick={() => openPage(null)} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading chemical stock…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(ut.chemicals || 0) ? <div className="mt-4 text-sm text-slate-500">No chemical receipts or usage are recorded for {unitName}.</div> : null}

      {!loading && data && !withheld && (ut.chemicals || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [h ? `${h.chemical} in stock` : "Carfisol in stock", h ? `${kgFmt(h.stock_kgs)} kg` : "—", h ? `${kgFmt(h.stock_bags)} bags${h.kg_per_bag ? ` of ${kgFmt(h.kg_per_bag)} kg` : ""}${h.no_movements ? " · no entries at this unit" : ""}` : "", h ? color(h.stock_kgs) : "#64748b"],
              ["Received / Used", h ? `${kgFmt(h.received_kgs)} / ${kgFmt(h.used_kgs)} kg` : "—", h ? `${kgFmt(h.transfer_in_kgs)} kg came in from other units · ${kgFmt(h.transfer_out_kgs)} kg went out` : "", "#0f172a"],
              ["Last Movement", h?.last_movement || "—", h ? `receipt ${h.last_receipt || "—"} · usage ${h.last_usage || "—"}${stale(h.days_since_last_movement)}` : "", "#0f172a"],
              [`All chemicals at ${data.resolved?.unit?.code || "unit"}`, `${kgFmt(ut.stock_kgs)} kg`, `${kgFmt(ut.stock_bags)} bags · ${fmt0(ut.chemicals_in_stock)} of ${fmt0(ut.chemicals)} chemicals in stock${ut.negative_stock_chemicals ? ` · ${ut.negative_stock_chemicals} negative` : ""}`, "#2563eb"],
            ].map(([label, value, sub, c]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color: c }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Every chemical at {unitName} — stock in kg (click to open its ledger)</div>
            <div className="mt-2 space-y-1.5">
              {chems.slice(0, 8).map((c) => (
                <div key={c.chemical_id} className="flex cursor-pointer items-center gap-3 hover:bg-slate-50" onClick={() => openPage(c.chemical)}>
                  <div className="w-64 shrink-0 truncate text-xs text-slate-700" title={c.chemical}>{c.chemical}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxStock && c.stock_kgs > 0 ? (c.stock_kgs / maxStock) * 100 : 0}%`, backgroundColor: "#2563eb", height: "100%" }} /></div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: color(c.stock_kgs) }}>{kgFmt(c.stock_kgs)} kg</span> · {kgFmt(c.stock_bags)} bags · last {c.last_movement || "—"}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of today (receipts by bill date, usage by entry date) · {unitName}{data.resolved?.chemical?.matched === "spelling" ? ` · "Carfisol" is ${data.resolved.chemical.name} in the ERP master` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function FloorBalanceCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const asOf = filters?.dateTo || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/floor-balance", { as_of: asOf, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the floor balance."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [asOf, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (asOf) params.set("as_of", asOf);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/floor-balance${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const f = data?.filters || {};
  const plants = data?.by_plant || [];
  const kgFmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const colors = { floor: "#2563eb", graded: "#059669", sent: "#b45309" };
  const maxPlant = Math.max(0, ...plants.map((p) => p.floor_kgs || 0));
  const withheld = data && !data.publishable && (t.tracks || 0) > 0;
  const older = (data?.by_age || []).filter((a) => a.age === ">30").reduce((s, a) => s + a.floor_kgs, 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Floor Balance on a Date</div>
          <div className="mt-1 text-sm text-slate-600">
            Headless material graded (and soaked) on the processing floor but not yet sent to value addition at the end of the day — the ERP's VA floor balance, per plant. Set the global To Date to look at any past day; otherwise today is shown.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading floor balance…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(t.tracks || 0) ? <div className="mt-4 text-sm text-slate-500">No graded tracks arrived in the {f.lookback_days || 7} days up to {f.as_of}{plant ? ` at ${plant}` : ""}.</div> : null}

      {!loading && data && !withheld && (t.tracks || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`Floor balance on ${f.as_of}`, `${kgFmt(t.floor_kgs)} kg`, `${fmt0(t.tracks_with_balance)} track(s) with material · ${fmt0(t.crates)} crates`, colors.floor],
              ["Graded / Received", `${kgFmt(t.input_kgs)} kg`, `${fmt0(t.tracks)} tracks arrived in the last ${f.lookback_days} days`, colors.graded],
              ["Sent to VA", `${kgFmt(t.sent_kgs)} kg`, "up to the same date", colors.sent],
              ["Older records", older ? `${kgFmt(older)} kg` : "—", older ? "tracks over 30 days still open" : "no unclosed tracks over 30 days in the window", older ? "#dc2626" : "#64748b"],
            ].map(([label, value, sub, c]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color: c }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By plant — kg on the floor at end of {f.as_of}</div>
            <div className="mt-2 space-y-1.5">
              {plants.map((p) => (
                <div key={p.plant} className="flex items-center gap-3">
                  <div className="w-44 shrink-0 truncate text-xs text-slate-700" title={p.plant}>{p.plant}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxPlant ? (p.floor_kgs / maxPlant) * 100 : 0}%`, backgroundColor: colors.floor, height: "100%" }} /></div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: colors.floor }}>{kgFmt(p.floor_kgs)} kg</span> on {fmt0(p.tracks_with_balance)} of {fmt0(p.tracks)} tracks · {fmt0(p.crates)} crates</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of {f.as_of} end of day · arrivals from {f.window_from} · graded weight less weight sent to VA, both dated on or before that day{plant ? ` · ${plant}` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function ChemicalUsedOnDateCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const day = filters?.dateTo || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/chemical-used-on-date", { date: day, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the chemical usage."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [day, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (data?.filters?.date) params.set("date", data.filters.date);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/chemical-used-on-date${qs ? `?${qs}` : ""}`);
  };

  const f = data?.filters || {};
  const st = data?.store?.totals || {}; const sk = data?.soaking?.totals || {};
  const kgFmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const colors = { store: "#2563eb", tubs: "#059669", salt: "#0891b2" };
  const chems = (data?.store?.by_chemical || []);
  const maxChem = Math.max(0, ...chems.map((c) => c.kgs || 0));
  const dosed = Object.fromEntries((data?.soaking?.by_chemical || []).map((c) => [c.chemical, c.chemical_kgs]));
  const withheld = data && !data.publishable && ((st.entries || 0) > 0 || (sk.tubs || 0) > 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Chemical Used on a Date</div>
          <div className="mt-1 text-sm text-slate-600">
            Treatment chemical and salt used on one day — what the store issued to each plant and what the floor actually dosed into the soaking tubs. Set the global To Date to pick the day; otherwise the latest day with store entries is shown.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading chemical usage…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">Validation check failed — figures withheld until the ERP data matches the validated structure. {data.compatibility?.reason || data.reconciliation?.equation}</div> : null}
      {!loading && data && !withheld && !(st.entries || 0) && !(sk.tubs || 0) ? <div className="mt-4 text-sm text-slate-500">No chemical was issued or dosed on {f.date}{plant ? ` at ${f.plant_name || plant}` : ""}.</div> : null}

      {!loading && data && !withheld && ((st.entries || 0) > 0 || (sk.tubs || 0) > 0) ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`Issued from store on ${f.date}`, `${kgFmt(st.kgs)} kg`, `${fmt0(st.bags)} bags · ${fmt0(st.entries)} entries · ${fmt0(st.plants)} plant(s)`, colors.store],
              ["Dosed into tubs", `${kgFmt(sk.chemical_kgs)} kg`, `chemical on ${fmt0(sk.tubs)} soaks · ${fmt0(sk.plants)} plant(s)`, colors.tubs],
              ["Salt dosed", `${kgFmt(sk.salt_kgs)} kg`, "on the same soaks", colors.salt],
              ["Material soaked", `${kgFmt(sk.soak_in_kgs)} kg`, st.transfer_entries ? `${kgFmt(st.transfer_kgs)} kg also moved between units` : "no unit-to-unit transfers that day", "#0f172a"],
            ].map(([label, value, sub, c]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color: c }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By chemical — issued from store (blue) with the amount dosed into tubs that day</div>
            <div className="mt-2 space-y-1.5">
              {chems.slice(0, 8).map((c) => (
                <div key={c.chemical} className="flex items-center gap-3">
                  <div className="w-64 shrink-0 truncate text-xs text-slate-700" title={c.chemical}>{c.chemical}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxChem ? (c.kgs / maxChem) * 100 : 0}%`, backgroundColor: colors.store, height: "100%" }} /></div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: colors.store }}>{kgFmt(c.kgs)} kg</span> issued · {fmt0(c.bags)} bags{dosed[c.chemical] != null ? <span style={{ color: colors.tubs }}> · {kgFmt(dosed[c.chemical])} kg dosed</span> : /salt/i.test(c.chemical) ? <span style={{ color: colors.salt }}> · {kgFmt(sk.salt_kgs)} kg dosed</span> : null}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {f.date_defaulted ? `No date set — showing ${f.date}, the latest day with store entries` : `Day ${f.date}`} · store entries by entry date, soaking records by soaking date{plant ? ` · ${f.plant_name || plant}` : ""}. Totals reconcile: {data.reconciliation?.ok ? "yes" : "no"}.
          </div>
        </>
      ) : null}
    </div>
  );
}

function Grid({ children }) {
  return <div className="ceo-live-grid">{children}</div>;
}

function KpiRow({ filters }) {
  return (
    <div className="grid grid-cols-12 gap-4">
      {[W.totalSalesUsd, W.orderedMcs, W.availableMcs, W.receivedQty].map((w) => (
        <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
          <WidgetCard widget={w} filters={filters} />
        </div>
      ))}
    </div>
  );
}

function PlaceholderBlock({ title, items }) {
  return (
    <div className="ceo-block">
      <div className="ceo-block-title">{title}</div>
      <div className="ceo-block-subtitle">
        This section is prepared for CEO-level analytics. Final calculations require ERP table mapping and business formula confirmation.
      </div>
      <div className="ceo-list">
        {items.map((item) => (
          <div key={item} className="ceo-list-item">
            <CheckCircle2 size={14} strokeWidth={1.8} />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CEOFilterBar({ filters, setFilters, filterOptions }) {
  const set = (patch) => setFilters((current) => ({ ...current, ...patch }));

  const applyPreset = (preset) => {
    if (preset === "fy") {
      const r = currentFinancialYearRange();
      set({ dateFrom: r.from, dateTo: r.to, periodMode: "Financial Year" });
    } else if (preset === "cy") {
      const r = currentCalendarYearRange();
      set({ dateFrom: r.from, dateTo: r.to, periodMode: "Calendar Year" });
    } else if (preset === "prev1") {
      const r = previousCalendarYearRange(1);
      set({ dateFrom: r.from, dateTo: r.to, periodMode: "Previous Year" });
    } else if (preset === "clear") {
      setFilters({
        dateFrom: "",
        dateTo: "",
        periodMode: "Custom",
        buyer: "",
        consignee: "",
        country: "",
        grade: "",
        variety: "",
        brand: "",
        coldStore: "",
        plant: "",
      });
    }
  };

  const activeCount = Object.entries(filters).filter(([k, v]) => k !== "periodMode" && v).length;

  const SelectFilter = ({ label, value, options = [], onChange }) => (
    <div>
      <label>{label}</label>
      <select
        className="select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        {(options || []).map((option) => {
          const optionValue = typeof option === "object" ? option.value : option;
          const optionLabel = typeof option === "object" ? option.label : option;
          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </div>
  );

  return (
    <div className="ceo-filter-panel">
      <div className="ceo-filter-head">
        <div>
          <div className="ceo-filter-title">CEO Global Filters</div>
          <div className="ceo-filter-subtitle">
            Filters are applied automatically to matching datasets. Fields that do not exist in a dataset are safely skipped.
          </div>
        </div>
        <div className="ceo-filter-actions">
          <button type="button" onClick={() => applyPreset("fy")}>Current FY</button>
          <button type="button" onClick={() => applyPreset("cy")}>Current CY</button>
          <button type="button" onClick={() => applyPreset("prev1")}>Previous Year</button>
          <button type="button" onClick={() => applyPreset("clear")}>Clear</button>
        </div>
      </div>

      <div className="ceo-filter-grid">
        <div>
          <label>Date From</label>
          <input type="date" value={filters.dateFrom} onChange={(e) => set({ dateFrom: e.target.value, periodMode: "Custom" })} />
        </div>
        <div>
          <label>Date To</label>
          <input type="date" value={filters.dateTo} onChange={(e) => set({ dateTo: e.target.value, periodMode: "Custom" })} />
        </div>
        <SelectFilter
          label="Buyer"
          value={filters.buyer}
          options={filterOptions?.buyers || []}
          onChange={(value) => set({ buyer: value })}
        />
        <SelectFilter
          label="Consignee"
          value={filters.consignee}
          options={filterOptions?.consignees || []}
          onChange={(value) => set({ consignee: value })}
        />
        <SelectFilter
          label="Country"
          value={filters.country}
          options={filterOptions?.countries || []}
          onChange={(value) => set({ country: value })}
        />
        <SelectFilter
          label="Grade"
          value={filters.grade}
          options={filterOptions?.grades || []}
          onChange={(value) => set({ grade: value })}
        />
        <SelectFilter
          label="Variety"
          value={filters.variety}
          options={filterOptions?.varieties || []}
          onChange={(value) => set({ variety: value })}
        />
        <SelectFilter
          label="Brand"
          value={filters.brand}
          options={filterOptions?.brands || []}
          onChange={(value) => set({ brand: value })}
        />
        <SelectFilter
          label="Cold Store"
          value={filters.coldStore}
          options={filterOptions?.coldStores || []}
          onChange={(value) => set({ coldStore: value })}
        />
        <SelectFilter
          label="Plant"
          value={filters.plant}
          options={filterOptions?.plants || []}
          onChange={(value) => set({ plant: value })}
        />
      </div>

      <div className="ceo-filter-footer">
        <span>{filters.periodMode || "Custom"}</span>
        <span>{activeCount} active filter(s)</span>
      </div>
    </div>
  );
}


function DocumentationStatusCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.post("/ai/ceo-chat/documentation-status", {})
      .then((r) => { if (alive) { setData(r.data); setError(""); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e?.message || "Documentation status could not be loaded."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: "var(--c-border)" }} data-analytics-target="po-documentation-status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-900">Entire Documentation — Pending / Completed</div>
          <div className="mt-1 text-sm text-slate-500">PO document checklist from the ERP with explicit completion status where the legacy ERP does not store it.</div>
        </div>
        <button type="button" onClick={() => nav("/ceo-command-center/client-requirements")} className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Manage Business Inputs</button>
      </div>
      {loading ? <div className="mt-4 text-sm text-slate-500">Loading documentation status…</div> : error ? <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["POs Checked", data?.matching_pos ?? 0, "#0f172a"],
            ["Completed", data?.completed_pos ?? 0, "#15803d"],
            ["Pending", data?.pending_pos ?? 0, "#b45309"],
            ["Source", "ERP + Status", "#2563eb"],
          ].map(([label, value, color]) => <div key={label} className="rounded-xl border border-slate-200 p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div></div>)}
        </div>
      )}
    </div>
  );
}

export default function CEOCommandCenter() {
  const nav = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    return TABS.some((item) => item.id === tab) ? tab : "overview";
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    if (tab && TABS.some((item) => item.id === tab) && tab !== active) {
      setActive(tab);
    }

    if (!tab && active !== "overview") {
      setActive("overview");
    }
  }, [location.search, active]);
  useAnalyticsSearchTarget(active, location);
  const [aiPrompt, setAiPrompt] = useState(CEO_AI_EXAMPLES[0]);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiProvider, setAiProvider] = useState("");
  const [aiError, setAiError] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCreating, setAiCreating] = useState(false);
  const [createdDashboard, setCreatedDashboard] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    periodMode: "Custom",
    buyer: "",
    consignee: "",
    country: "",
    grade: "",
    variety: "",
    brand: "",
    coldStore: "",
    plant: "",
  });
  const [dummyCartonPeriod, setDummyCartonPeriod] = useState("month");


  const [filterOptions, setFilterOptions] = useState({
    exporters: [],
    buyers: [],
    consignees: [],
    brands: [],
    countries: [],
    grades: [],
    varieties: [],
    coldStores: [],
    plants: [],
    priority: [],
  });

  const openShipmentPerformanceDetail = ({
    scope = "po",
    status = "all",
    customer = "",
  } = {}) => {
    const params = new URLSearchParams();

    params.set("scope", scope);
    params.set("status", status || "all");

    if (customer) {
      params.set("customer", customer);
    }

    if (filters.dateFrom) {
      params.set("from", filters.dateFrom);
    }

    if (filters.dateTo) {
      params.set("to", filters.dateTo);
    }

    nav(
      scope === "fda"
        ? `/ceo-command-center/fda-release-hold?${params.toString()}`
        : scope === "payments"
          ? `/ceo-command-center/payments-received-pending?${params.toString()}`
          : scope === "pending-shipments"
            ? `/ceo-command-center/pending-shipments?${params.toString()}`
            : scope === "variety-analysis"
              ? `/ceo-command-center/variety-analysis?${params.toString()}`
              : scope === "confirmed-orders-production-status"
                ? `/ceo-command-center/confirmed-orders-production-status?${params.toString()}`
                : scope === "production-plant-order-view"
                  ? `/ceo-command-center/production-plant-order-view?${params.toString()}`
                  : scope === "species-analysis"
                    ? `/ceo-command-center/species-analysis?${params.toString()}`
                    : scope === "headon-vs-finished-plant"
                      ? `/ceo-command-center/headon-vs-finished-plant?${params.toString()}`
                      : scope === "po-day-code-status"
                        ? `/ceo-command-center/po-day-code-status?${params.toString()}`
                        : scope === "sales-price-analysis"
                          ? `/ceo-command-center/sales-price-analysis?${params.toString()}`
                          : scope === "order-delivery-status"
                            ? `/ceo-command-center/order-delivery-status?${params.toString()}`
                            : scope === "under-production-by-buyer"
                              ? `/ceo-command-center/under-production-by-buyer?${params.toString()}`
                              : scope === "headon-cy-vs-fy"
                                ? `/ceo-command-center/headon-cy-vs-fy?${params.toString()}`
                                : scope === "dummy-stock-pending-contracts"
                                  ? `/ceo-command-center/dummy-stock-pending-contracts?${params.toString()}`
                                : scope === "rm-purchase-value"
                                  ? `/ceo-command-center/rm-purchase-value?${params.toString()}`
                                : scope === "purchase-quality-by-variety"
                                  ? `/ceo-command-center/purchase-quality-by-variety?${params.toString()}`
                                : scope === "supplier-antibiotic-positive"
                                  ? `/ceo-command-center/supplier-antibiotic-positive?${params.toString()}`
                                : scope === "batch-antibiotic-history"
                                  ? `/ceo-command-center/batch-antibiotic-history?${params.toString()}`
                                  : `/ceo-command-center/shipment-performance?${params.toString()}`
    );
  };

  useEffect(() => {
    let cancelled = false;

    api.get("/ceo/filter-options")
      .then((res) => {
        if (!cancelled) setFilterOptions(res.data || {});
      })
      .catch(() => {
        if (!cancelled) {
          setFilterOptions({
            exporters: [],
            buyers: [],
            consignees: [],
            brands: [],
            countries: [],
            grades: [],
            varieties: [],
            coldStores: [],
            plants: [],
            priority: [],
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Publish the active filter scope so the globally-mounted Ask AI drawer can
  // answer for the period shown on screen instead of all-time.
  useEffect(() => {
    setAskAiContext(ceoFiltersToAskAiContext(filters));
    return () => setAskAiContext(null);
  }, [filters]);

  const previewAiPlan = async () => {
    const text = aiPrompt.trim();
    if (text.length < 8) {
      setAiError("Please enter a clearer CEO analytics prompt.");
      return;
    }

    setAiError("");
    setCreatedDashboard(null);
    setAiLoading(true);

    try {
      const r = await api.post("/ai/analytics-agent/plan", { prompt: text });
      setAiPlan(r.data.plan);
      setAiProvider(r.data.provider || "");
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || "Failed to generate AI plan.");
    } finally {
      setAiLoading(false);
    }
  };

  const createAiDashboard = async () => {
    const text = aiPrompt.trim();
    if (text.length < 8) {
      setAiError("Please enter a clearer CEO analytics prompt.");
      return;
    }

    setAiError("");
    setAiCreating(true);

    try {
      const r = await api.post("/ai/analytics-agent/create-dashboard", { prompt: text });
      setAiPlan(r.data.plan);
      setAiProvider(r.data.provider || "");
      setCreatedDashboard(r.data.dashboard);
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || "Failed to create AI dashboard.");
    } finally {
      setAiCreating(false);
    }
  };

  const renderFavoriteAnalytics = (entry) => {
    if (!entry) return null;
    const widget = Object.values(W).find(item => item.id === entry.target);
    if (widget) return <WidgetCard widget={widget} filters={filters} dummyCartonPeriod={dummyCartonPeriod} setDummyCartonPeriod={setDummyCartonPeriod} />;
    switch (entry.id) {
      case "overview:section-overview-erp-operational-flow":
        return <FavoriteAnalyticsShell entry={entry}><div className="space-y-6"><div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <Factory size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-erp-operational-flow">ERP Operational Flow</div>
                  <div className="ceo-block-subtitle">
                    End-to-end operational path represented by the mapped ERP modules.
                    Stage conversion and yield percentages are intentionally excluded
                    until their business formulas are approved.
                  </div>
                </div>
              </div>

              <div className="ceo-process-flow-shell">
                <div className="ceo-process-flow-grid">
                  {[
                    { step: "01", label: "RM Purchase", widget: W.purchaseWeight },
                    { step: "02", label: "Receiving", widget: W.receivedQty },
                    { step: "03", label: "Deheading", widget: W.deheadingOutputWeight },
                    { step: "04", label: "Value Addition", widget: W.vaOutputWeight },
                    { step: "05", label: "Soaking", widget: W.soakOutQty },
                    { step: "06", label: "Freezing", widget: W.freezingProductionWeight },
                    { step: "07", label: "Packing", widget: W.dailyActualMcs },
                    { step: "08", label: "Cold Store", widget: W.availableMcs },
                    { step: "09", label: "PO Demand", widget: W.orderedMcs },
                    { step: "10", label: "Shipment", widget: W.shipmentOutMcs },
                  ].map((stage, index, stages) => (
                    <div className="ceo-process-stage-wrap" key={stage.step}>
                      <div className="ceo-process-step-row">
                        <span className="ceo-process-step">{stage.step}</span>
                        <span className="ceo-process-step-label">{stage.label}</span>
                        {index < stages.length - 1 && (
                          <span className="ceo-process-arrow" aria-hidden="true">→</span>
                        )}
                      </div>
                      <WidgetCard
                        widget={stage.widget}
                        filters={filters}
                        compact
                      />
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 mt-5">
                  <WidgetCard widget={W.crossModuleLineage} filters={filters} />
                  <WidgetCard widget={W.documentationStatus} filters={filters} />
                </div>

                <div className="ceo-process-validation-note">
                  Stage values come from their mapped ERP datasets and follow the CEO global
                  filter context where supported. They are operational snapshots only —
                  no stage-to-stage yield or conversion percentage is implied until those
                  business formulas are approved.
                </div>
              </div></div></FavoriteAnalyticsShell>;
      case "overview:section-overview-species-analysis-vannamei-vs-black-tiger":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-overview-species-analysis-vannamei-vs-black-tiger">
                  Species Analysis — Vannamei vs Black Tiger
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  How the business splits between the two shrimp species we process, on one shared definition across shipped sales and the confirmed order book, with a species filter for drill-down.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "species-analysis",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Species Split
                </button>
              </div></FavoriteAnalyticsShell>;
      case "overview:card-eufarmcertificatescard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-eufarmcertificatescard"><EuFarmCertificatesCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "overview:card-executiveattentionqueue":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-executiveattentionqueue"><ExecutiveAttentionQueue filters={filters} /></div></FavoriteAnalyticsShell>;
      case "purchase:card-rmarrivalsbycountcard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-rmarrivalsbycountcard"><RmArrivalsByCountCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "purchase:section-purchase-suppliers-linked-to-antibiotic-positive-batches":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-purchase-suppliers-linked-to-antibiotic-positive-batches">
                  Suppliers Linked to Antibiotic-Positive Batches
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Every screened raw-material lot traced back to the supplier it was bought from, so suppliers whose batches returned a positive antibiotic reading can be identified with their affected lots, antibiotics found, purchase centre and receiving plant.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "supplier-antibiotic-positive",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Flagged Suppliers
                </button>
              </div></FavoriteAnalyticsShell>;
      case "purchase:section-purchase-raw-material-purchase-value-in-lakhs-crores":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-purchase-raw-material-purchase-value-in-lakhs-crores">
                  Raw Material Purchase Value — in Lakhs &amp; Crores
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  What we paid for raw material, in rupees, with totals in crores and individual arrivals in lakhs, by month, plant and purchase variety, reconciled to the bills the ERP recorded.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "rm-purchase-value",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Purchase Value
                </button>
              </div></FavoriteAnalyticsShell>;
      case "commercial:section-commercial-selling-price-invoice-value-by-currency":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-commercial-selling-price-invoice-value-by-currency">
                  Selling Price &amp; Invoice Value — by Currency
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  The price we are selling at per pound or per kilo, and what each invoice is worth, with every figure kept in the currency it was invoiced in so dollars are never mixed with rupees or euros.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "sales-price-analysis",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Prices
                </button>
              </div></FavoriteAnalyticsShell>;
      case "commercial:card-salevsactualpricecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-salevsactualpricecard"><SaleVsActualPriceCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "commercial:card-stockfinanceexposurecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-stockfinanceexposurecard"><StockFinanceExposureCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "commercial:section-commercial-variety-analysis":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    R23 · Validated ERP
                  </div>
                  <div className="mt-2 text-lg font-semibold text-slate-900" data-analytics-target="section-commercial-variety-analysis">
                    Variety Analysis
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    Commercial Variety mix by ERP Variety ShortName, measured by MCS. USD/value is intentionally excluded until FX validation is approved.
                  </div>
                  <button
                    type="button"
                    className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                    onClick={() =>
                      openShipmentPerformanceDetail({
                        scope: "variety-analysis",
                        status: "all",
                        customer: "",
                      })
                    }
                  >
                    Open Variety Analysis
                  </button>
                </div></FavoriteAnalyticsShell>;
      case "po:section-po-day-codes-assigned-vs-not-assigned":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-po-day-codes-assigned-vs-not-assigned">
                  Day Codes — Assigned vs Not Assigned
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Which orders already have their carton day code assigned through the quality code list, and which are still waiting, so the orders that cannot be coded yet can be chased.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "po-day-code-status",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Day Codes
                </button>
              </div></FavoriteAnalyticsShell>;
      case "po:card-buyerordercountscard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-buyerordercountscard"><BuyerOrderCountsCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "store:section-store-dummy-cartons-on-active-pending-contracts":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-store-dummy-cartons-on-active-pending-contracts">
                  Dummy Cartons on Active Pending Contracts
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Dummy-packed cartons in cold store that are tied to a contract which still has quantity to deliver, alongside dummy stock on fulfilled contracts and stock with no contract at all.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "dummy-stock-pending-contracts",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Dummy Stock
                </button>
              </div></FavoriteAnalyticsShell>;
      case "store:card-consumablesusagecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-consumablesusagecard"><ConsumablesUsageCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "store:card-packingmaterialcontractscard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-packingmaterialcontractscard"><PackingMaterialContractsCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "store:card-chemicalstockcard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-chemicalstockcard"><ChemicalStockCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:section-production-head-on-vs-finished-product-by-plant":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-production-head-on-vs-finished-product-by-plant">
                  Head-On vs Finished Product — by Plant
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Compares how much whole (head-on) raw material each plant took in against the finished frozen product that came out of that same material, so unit-by-unit conversion can be seen side by side.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "headon-vs-finished-plant",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Conversion
                </button>
              </div></FavoriteAnalyticsShell>;
      case "production:card-finishedproductvolumecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-finishedproductvolumecard"><FinishedProductVolumeCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:card-floorbalancecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-floorbalancecard"><FloorBalanceCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:card-chemicalusedondatecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-chemicalusedondatecard"><ChemicalUsedOnDateCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:section-production-confirmed-orders-under-production-vs-yet-to-start":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mt-2 text-lg font-semibold text-slate-900" data-analytics-target="section-production-confirmed-orders-under-production-vs-yet-to-start">
                  Confirmed Orders — Under Production vs Yet to Start
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Splits the confirmed order book still in hand into orders that
                  have actually started production and orders with no production
                  recorded yet, and ranks the not-started orders by how close
                  they are to their shipment deadline.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "confirmed-orders-production-status",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Orders
                </button>
              </div></FavoriteAnalyticsShell>;
      case "production:section-production-under-production-by-buyer":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-production-under-production-by-buyer">
                  Under Production — by Buyer
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  For each buyer, how many of their confirmed open orders have started production and how many are still waiting to start — the same rule as the Confirmed Orders view, broken out by buyer.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "under-production-by-buyer",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View by Buyer
                </button>
              </div></FavoriteAnalyticsShell>;
      case "production:section-production-orders-by-production-plant":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mt-2 text-lg font-semibold text-slate-900" data-analytics-target="section-production-orders-by-production-plant">
                  Orders by Production Plant
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Shows the orders assigned to each production plant, including
                  buyer, order quantity, production status, and plant-level
                  order drill-down.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "production-plant-order-view",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Orders by Plant
                </button>
              </div></FavoriteAnalyticsShell>;
      case "shipment:card-shippromptnessbyplantcard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-shippromptnessbyplantcard"><ShipPromptnessByPlantCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "shipment:card-yearlydispatchcomparisoncard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-yearlydispatchcomparisoncard"><YearlyDispatchComparisonCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "shipment:section-shipment-delivered-orders":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-shipment-delivered-orders">
                  Delivered Orders
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Which orders have reached the customer, which have shipped but are still awaiting delivery, and which have not left yet — using the same delivered rule as the ERP orders-by-buyer screen.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "order-delivery-status",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Deliveries
                </button>
              </div></FavoriteAnalyticsShell>;
      case "shipment:card-shipmentperformancematrix":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-shipmentperformancematrix"><ShipmentPerformanceMatrix
                filters={filters}
                onOpenDetail={openShipmentPerformanceDetail}
              /></div></FavoriteAnalyticsShell>;
      case "qc:section-qc-batch-antibiotic-testing-status-retest-history":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-qc-batch-antibiotic-testing-status-retest-history">
                  Batch Antibiotic Testing — Status &amp; Retest History
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Every screened raw-material batch with its antibiotic status, how many times it was tested, and the full test-by-test history — a retest is added to the record, never written over the earlier result.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "batch-antibiotic-history",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Batch History
                </button>
              </div></FavoriteAnalyticsShell>;
      case "qc:section-qc-suppliers-linked-to-antibiotic-positive-batches":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-qc-suppliers-linked-to-antibiotic-positive-batches">
                  Suppliers Linked to Antibiotic-Positive Batches
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Every screened raw-material lot traced back to the supplier it was bought from, so suppliers whose batches returned a positive antibiotic reading can be identified with their affected lots, antibiotics found, purchase centre and receiving plant.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "supplier-antibiotic-positive",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Flagged Suppliers
                </button>
              </div></FavoriteAnalyticsShell>;
      case "qc:section-qc-quality-at-purchase-by-purchase-variety":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-qc-quality-at-purchase-by-purchase-variety">
                  Quality at Purchase — by Purchase Variety
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Antibiotic screening outcomes on raw material lots, grouped by the variety recorded when the lot was bought, so quality at the point of purchase can be compared variety by variety.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "purchase-quality-by-variety",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Purchase Quality
                </button>
              </div></FavoriteAnalyticsShell>;
      case "comparisons:section-comparisons-head-on-volume-calendar-year-vs-financial-year":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-comparisons-head-on-volume-calendar-year-vs-financial-year">
                  Head-On Volume — Calendar Year vs Financial Year
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Total head-on volume by calendar year and by financial year (April to March), side by side, so the two year definitions can be compared without mixing them.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "headon-cy-vs-fy",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Comparison
                </button>
              </div></FavoriteAnalyticsShell>;
      default: return null;
    }
  };

  return (
    <div className="erp-home-shell ceo-analytics-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content ceo-analytics-content">
        <div className="erp-page-title-row ceo-title-row">
          <div>
            <h1>CEO Command Center</h1>
            <p>Executive analytics across sales, purchase, QA, process, store, PO, dispatch, master search, and reports.</p>
          </div>


        </div>

        <div className="ceo-erp-content space-y-5">
          <CEOFilterBar filters={filters} setFilters={setFilters} filterOptions={filterOptions} />

          {active === "quick-access" && <QuickAccessPanel renderAnalytics={renderFavoriteAnalytics} />}

          {active === "overview" && (
            <div className="space-y-6" data-analytics-target="tab-overview">

              {renderFavoriteAnalytics(analyticsById["overview:section-overview-species-analysis-vannamei-vs-black-tiger"])}

              {/* ======================================================
                  1. EXECUTIVE PULSE
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <LayoutDashboard size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-pulse">Executive Pulse</div>
                  <div className="ceo-block-subtitle">
                    CEO-level snapshot of commercial demand, procurement, production,
                    inventory and validated customer dispatch activity.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[
                  W.totalSalesUsd,
                  W.orderedMcs,
                  W.availableMcs,
                  W.availableKgs,
                  W.purchaseWeight,
                  W.receivedQty,
                  W.shipmentOutMcs,
                  W.shipmentOutKgs,
                ].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>


              {/* ======================================================
                  R38. FARM CERTIFICATION SUMMARY
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <LayoutDashboard size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-farm-certification-summary">Farm Certification Summary</div>
                  <div className="ceo-block-subtitle">
                    Total farms with BAP, ASC, and non-certified classification from the
                    validated farm audit source.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <WidgetCard
                    widget={W.farmCertificationSummary}
                    filters={filters}
                  />
                </div>
              </div>


              {/* ======================================================
                  R39. PLANT AUDIT SCHEDULE & CERTIFICATE RENEWALS
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <LayoutDashboard size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-plant-audit-schedule-certificate-renewals">
                    Plant Audit Schedule & Certificate Renewals
                  </div>
                  <div className="ceo-block-subtitle">
                    Due, expired, valid, and upcoming certificate renewals using
                    AuditDate, ValidUpto, and RenewalDate from the validated ERP source.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <WidgetCard
                    widget={W.farmAuditSchedule}
                    filters={filters}
                  />
                </div>
              </div>

              {renderFavoriteAnalytics(analyticsById["overview:card-eufarmcertificatescard"])}


              {/* ======================================================
                  2. EXECUTIVE RISK & EXCEPTIONS
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <AlertTriangle size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-risk-exceptions">Executive Risk & Exceptions</div>
                  <div className="ceo-block-subtitle">
                    Operational conditions requiring management attention. Values use
                    validated ERP rules where available and remain fail-closed where
                    business validation is still pending.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[
                  W.qcDetectedTests,
                  W.poCriticalRisk,
                  W.poWarningRisk,
                  W.shipmentPendingRisk,
                ].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>


              {/* ======================================================
                  3. BUSINESS CONTRIBUTION & DISTRIBUTION
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <BarChart3 size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-business-contribution-distribution">Business Contribution & Distribution</div>
                  <div className="ceo-block-subtitle">
                    Buyer, destination, grade, inventory-location and dispatch-location
                    contribution for the selected CEO filter context.
                  </div>
                </div>
              </div>

              <Grid>
                <div className="ceo-buyer-sales-full-row">
                  <WidgetCard widget={W.dashboardCustomerSales} filters={filters} />
                </div>
                <WidgetCard widget={W.destinationSales} filters={filters} />
                <WidgetCard widget={W.gradeSales} filters={filters} />
                <WidgetCard widget={W.coldStoreStock} filters={filters} />
                <WidgetCard widget={W.shipmentByStore} filters={filters} />
              </Grid>


              {/* ======================================================
                  4. EXECUTIVE TRENDS
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <BarChart3 size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-trends">Executive Trends</div>
                  <div className="ceo-block-subtitle">
                    Time-based movement across sales, procurement, production and
                    customer shipment activity.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.salesTrend} filters={filters} />
                <WidgetCard widget={W.purchaseTrend} filters={filters} />
                <WidgetCard widget={W.productionTrend} filters={filters} />
                <WidgetCard widget={W.shipmentTrend} filters={filters} />
              </Grid>


              {/* ======================================================
                  5. ERP OPERATIONAL FLOW
              ====================================================== */}
              {renderFavoriteAnalytics(analyticsById["overview:section-overview-erp-operational-flow"])}

              {/* ======================================================
                  6. EXECUTIVE ATTENTION QUEUE
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <AlertTriangle size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-attention-required">Executive Attention Required</div>
                  <div className="ceo-block-subtitle">
                    Consolidated CEO action queue for validated PO, QA, shipment and
                    dispatch exceptions. Procurement and stock exceptions will appear once
                    their source rules are fully reconciled and approved.
                  </div>
                </div>
              </div>

              {renderFavoriteAnalytics(analyticsById["overview:card-executiveattentionqueue"])}

            </div>
          )}

          {active === "buyer" && (
            <div className="space-y-6" data-analytics-target="tab-buyer">
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-sales-customer-performance">Sales & Customer Performance</div>
                  <div className="ceo-block-subtitle">
                    Validated invoice-line sales analysis by customer, destination, grade,
                    variety, brand and year. Buyer and Consignee are intentionally shown as
                    separate ERP dimensions. Sales Gross KGS is excluded because the invoice
                    header weight repeats across sales lines and is not approved for aggregation.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.totalSalesUsd, W.salesMcs, W.salesInvoiceCount, W.salesConsigneeCount].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-1-sales-trend-annual-performance">1. Sales Trend & Annual Performance</div>
                  <div className="ceo-block-subtitle">
                    Invoice-date sales movement, year-wise USD sales value and each mapped
                    year's percentage contribution from the validated Sales dataset.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.salesTrend} filters={filters} />
                <WidgetCard widget={W.salesYearComparison} filters={filters} />
                <WidgetCard widget={W.salesAnnualContribution} filters={filters} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-2-customer-market-contribution">2. Customer & Market Contribution</div>
                  <div className="ceo-block-subtitle">
                    Buyer, consignee and destination contribution are kept separate so sparse
                    Buyer mapping is visible rather than silently substituted.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.buyerSales} filters={filters} />
                <WidgetCard widget={W.consigneeSales} filters={filters} />
                <WidgetCard widget={W.destinationSales} filters={filters} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-3-product-mix">3. Product Mix</div>
                  <div className="ceo-block-subtitle">
                    Sales contribution by grade, brand and validated business Variety code.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.gradeSales} filters={filters} />
                <WidgetCard widget={W.brandSales} filters={filters} />
                <WidgetCard widget={W.varietySales} filters={filters} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-4-invoice-sales-drill-down">4. Invoice & Sales Drill-down</div>
                  <div className="ceo-block-subtitle">
                    Invoice, PO, customer, product, shipment and shipping-bill level detail
                    for reconciliation and executive drill-down.
                  </div>
                </div>
              </div>

              <WidgetCard widget={W.salesTable} filters={filters} />
            </div>
          )}

          {active === "purchase" && (
            <div className="space-y-5" data-analytics-target="tab-purchase">

              {renderFavoriteAnalytics(analyticsById["purchase:card-rmarrivalsbycountcard"])}

              {renderFavoriteAnalytics(analyticsById["purchase:section-purchase-suppliers-linked-to-antibiotic-positive-batches"])}

              {renderFavoriteAnalytics(analyticsById["purchase:section-purchase-raw-material-purchase-value-in-lakhs-crores"])}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon"><ShoppingCart size={18} strokeWidth={1.8} /></div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-purchase-purchase-raw-material-procurement">Purchase & Raw Material Procurement</div>
                  <div className="ceo-block-subtitle">
                    Supplier-wise, centre-wise and lot-level RM procurement using verified ERP purchase data. The RM Requirement & Procurement Gap card below executes the versioned ERP Flow Planner definition only after business approval and remains fail-closed otherwise.
                  </div>
                </div>
              </div>

              <WidgetCard widget={W.rmRequirementGap} filters={filters} />

              <div className="grid grid-cols-12 gap-4">
                {[W.purchaseWeight, W.purchaseProductionWeight, W.purchaseLotCount].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-4" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>

              <Grid>
                <WidgetCard widget={W.purchaseSupplierWeight} filters={filters} />
                <WidgetCard widget={W.purchaseCentreWeight} filters={filters} />
                <WidgetCard widget={W.purchaseStationWeight} filters={filters} />
                <WidgetCard widget={W.purchaseLotStatus} filters={filters} />
              </Grid>

              <WidgetCard widget={W.purchaseTable} filters={filters} />
            </div>
          )}

          {active === "commercial" && (
            <div className="space-y-5" data-analytics-target="tab-commercial">

              {renderFavoriteAnalytics(analyticsById["commercial:section-commercial-selling-price-invoice-value-by-currency"])}

              {renderFavoriteAnalytics(analyticsById["commercial:card-salevsactualpricecard"])}

              {renderFavoriteAnalytics(analyticsById["commercial:card-stockfinanceexposurecard"])}

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-commercial-commercial-finance-control-center">
                    Commercial / Finance Control Center
                  </div>
                  <div className="ceo-block-subtitle">
                    Commercial sales, collections, payment realization,
                    pending payments, shipment exposure and buyer-wise
                    commercial analysis.
                  </div>
                </div>
              </div>

              <div
                className="bg-white border rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3"
                style={{ borderColor: "var(--c-border)" }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">
                  Commercial Views
                </span>

                <button
                  type="button"
                  className="px-4 py-2 rounded-xl border bg-slate-900 text-white text-sm font-medium"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "payments",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  Payments Received / Pending
                </button>

                <button
                  type="button"
                  className="px-4 py-2 rounded-xl border bg-slate-900 text-white text-sm font-medium"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "pending-shipments",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  Pending Shipments
                </button>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.commercialSalesUsd, W.commercialSalesKgs, W.commercialUtilizedValue, W.commercialBankCharges].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <WidgetCard widget={W.commercialPendingShipments} filters={filters} />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <WidgetCard widget={W.commercialPaymentsReceivedPending} filters={filters} />
                </div>
              </div>
              <Grid>
                <WidgetCard widget={W.commercialBuyerSales} filters={filters} />
                <WidgetCard widget={W.commercialDestinationSales} filters={filters} />
                <WidgetCard widget={W.commercialCollectionRate} filters={filters} />
                {renderFavoriteAnalytics(analyticsById["commercial:section-commercial-variety-analysis"])}
                <WidgetCard widget={W.commercialAntidumping} filters={filters} />
              </Grid>
              <WidgetCard widget={W.commercialTable} filters={filters} />
              <WidgetCard widget={W.commercialPaymentTable} filters={filters} />
            </div>
          )}

          {active === "po" && (
            <div className="space-y-6" data-analytics-target="tab-po">

              {renderFavoriteAnalytics(analyticsById["po:section-po-day-codes-assigned-vs-not-assigned"])}
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-po-po-fulfilment-risk-control-center">PO Fulfilment & Risk Control Center</div>
                  <div className="ceo-block-subtitle">
                    Order demand, dispatched quantity, current running-order stock coverage, MASTER/DUMMY readiness, and fulfilment risk using the ERP Running Orders rule.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.orderedMcs, W.dispatchedMcs].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>

              <WidgetCard widget={W.poFulfilmentRisk} filters={filters} />

              <DocumentationStatusCard />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-po-po-portfolio-analysis">PO Portfolio Analysis</div>
                  <div className="ceo-block-subtitle">
                    Filterable PO analysis from the validated PO-grade dataset. This section follows the CEO global filters; the live running-order risk queue above represents the current ERP operational state.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.poBrand} filters={filters} />
                <WidgetCard widget={W.poStatus} filters={filters} />
              </Grid>

              {renderFavoriteAnalytics(analyticsById["po:card-buyerordercountscard"])}

              <WidgetCard widget={W.poTable} filters={filters} />
            </div>
          )}

          {active === "store" && (
            <div className="space-y-6" data-analytics-target="tab-store">

              {renderFavoriteAnalytics(analyticsById["store:section-store-dummy-cartons-on-active-pending-contracts"])}

              {renderFavoriteAnalytics(analyticsById["store:card-consumablesusagecard"])}
              {renderFavoriteAnalytics(analyticsById["store:card-packingmaterialcontractscard"])}
              {renderFavoriteAnalytics(analyticsById["store:card-chemicalstockcard"])}
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-store-cold-store-control-center">Store / Cold Store Control Center</div>
                  <div className="ceo-block-subtitle">
                    Physical barcode stock, cold-store location, ageing, PO-linked stock, packing state, buyer exposure, and contract-state visibility.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.availableMcs, W.availableKgs, W.dummyStockMcs, W.poPendingMcsStore].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-1-physical-barcode-stock">1. Physical Barcode Stock</div>
                  <div className="ceo-block-subtitle">
                    Current available stock by cold store, grade, production date, rack location, barcode, packing style, and PO link.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.coldStoreStock} filters={filters} />
                <WidgetCard widget={W.gradeStock} filters={filters} />
                <WidgetCard widget={W.stockAgeing} filters={filters} />
              </Grid>

              <WidgetCard widget={W.stockTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-2-stock-transfers-by-plant">2. Stock Transfers by Plant</div>
                  <div className="ceo-block-subtitle">
                    Stock transfer volume grouped by source plant and ranked from least to highest MCS.
                  </div>
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.stockTransfersByPlant} filters={filters} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-2-po-contract-linked-stock">2. PO / Contract-linked Stock</div>
                  <div className="ceo-block-subtitle">
                    Stock linked back to buyer and PO with contract-state and pending-order context. ContractState is sourced from the validated PO line balance logic rather than the unreliable legacy PO status field.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.dummyStockMcs, W.dummyStockKgs, W.poPendingMcsStore].map((w) => (
                  <div className="col-span-12 md:col-span-4" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>

              <Grid>
                <WidgetCard widget={W.stockByPackingType} filters={filters} />
                <WidgetCard widget={W.stockByContractState} filters={filters} />
                <WidgetCard widget={W.stockByPlant} filters={filters} />
                <WidgetCard widget={W.stockByBuyer} filters={filters} />
                <WidgetCard widget={W.stockByPo} filters={filters} />
              </Grid>

              <WidgetCard widget={W.poLinkedStockTable} filters={filters} />

              <WidgetCard widget={W.stockReadyLabAwaiting} filters={filters} />
            </div>
          )}

          {active === "production" && (
            <div className="space-y-6" data-analytics-target="tab-production">

              {renderFavoriteAnalytics(analyticsById["production:section-production-head-on-vs-finished-product-by-plant"])}

              {renderFavoriteAnalytics(analyticsById["production:card-finishedproductvolumecard"])}
              {renderFavoriteAnalytics(analyticsById["production:card-floorbalancecard"])}
              {renderFavoriteAnalytics(analyticsById["production:card-chemicalusedondatecard"])}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon"><Factory size={18} strokeWidth={1.8} /></div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-process-production-control-center">Process / Production Control Center</div>
                  <div className="ceo-block-subtitle">
                    ERP production flow from receiving through deheading, value addition, soaking, freezing, packed production, and dummy/master packing using stage-specific validated datasets.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <WidgetCard widget={W.receivedQty} filters={filters} />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <WidgetCard widget={W.deheadingOutputWeight} filters={filters} />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <WidgetCard widget={W.freezingProductionWeight} filters={filters} />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <WidgetCard widget={W.dailyActualMcs} filters={filters} />
                </div>
              </div>

              {renderFavoriteAnalytics(analyticsById["production:section-production-confirmed-orders-under-production-vs-yet-to-start"])}

              {renderFavoriteAnalytics(analyticsById["production:section-production-under-production-by-buyer"])}

              {renderFavoriteAnalytics(analyticsById["production:section-production-orders-by-production-plant"])}

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-1-receiving-preprocessing">1. Receiving & Preprocessing</div>
                  <div className="ceo-block-subtitle">
                    Entry-stage material receiving and preprocessing context retained from the existing validated receiving dataset.
                  </div>
                </div>
              </div>
              <Grid>
                <WidgetCard widget={W.plantProduction} filters={filters} />
                <WidgetCard widget={W.productionYearComparison} filters={filters} />
              </Grid>
              <WidgetCard widget={W.productionTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-2-deheading-headless-processing">2. Deheading / Headless Processing</div>
                  <div className="ceo-block-subtitle">
                    Tracks received material, processed output, loose shell generated, plant, centre, counts, and processing dates without deriving an unvalidated yield percentage.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.deheadingReceivedWeight, W.deheadingOutputWeight, W.looseShellWeight].map((w) => (
                  <div className="col-span-12 md:col-span-4" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <Grid>
                <WidgetCard widget={W.deheadingByPlant} filters={filters} />
              </Grid>
              <WidgetCard widget={W.deheadingTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-3-value-addition">3. Value Addition</div>
                  <div className="ceo-block-subtitle">
                    Peeling/value-addition input and output weights by plant, grade, and variety, with validated aggregate VA% comparison across plants using SUM(VA Output Weight) / SUM(VA Input Weight) × 100.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.vaInputWeight, W.vaOutputWeight].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <WidgetCard widget={W.vaPercentByPlant} filters={filters} />
              <Grid>
                <WidgetCard widget={W.vaByPlant} filters={filters} />
                <WidgetCard widget={W.vaByVariety} filters={filters} />
              </Grid>
              <WidgetCard widget={W.vaTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-4-soaking">4. Soaking</div>
                  <div className="ceo-block-subtitle">
                    Soaking input and output quantities with plant, treatment, grade, and variety drill-down. Pickup percentage will be added only as SUM(output) / SUM(input), never as an average of row percentages.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.soakInQty, W.soakOutQty].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <Grid>
                <WidgetCard widget={W.soakingByPlant} filters={filters} />
                <WidgetCard widget={W.soakingByTreatment} filters={filters} />
              </Grid>
              <WidgetCard widget={W.soakingPickupByItemPlant} filters={filters} />
              <WidgetCard widget={W.soakingTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-5-freezing">5. Freezing</div>
                  <div className="ceo-block-subtitle">
                    Production and rejection weight by plant and freezing type, with record-level production traceability.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.freezingProductionWeight, W.freezingRejectionWeight].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <Grid>
                <WidgetCard widget={W.freezingByType} filters={filters} />
                <WidgetCard widget={W.freezingByPlant} filters={filters} />
              </Grid>
              <WidgetCard widget={W.freezingTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-6-daily-production-packing">6. Daily Production & Packing</div>
                  <div className="ceo-block-subtitle">
                    Finished packed production by plant, grade, variety, product form, freezing type, PO, and production type.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.dailyActualMcs, W.dailyActualKgs].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <Grid>
                <WidgetCard widget={W.productionByPlantValidated} filters={filters} />
                <WidgetCard widget={W.productionByFreezingType} filters={filters} />
              </Grid>
              <WidgetCard widget={W.dailyProductionTable} filters={filters} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-7-dummy-vs-master-packing">7. Dummy vs Master Packing</div>
                  <div className="ceo-block-subtitle">
                    Separates dummy and master carton production directly from PackingTypeID instead of inferring packing state from an unrelated source.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.dummyMcs, W.masterMcs].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <WidgetCard widget={w} filters={filters} />
                  </div>
                ))}
              </div>
              <Grid>
                <WidgetCard widget={W.packingTypeDistribution} filters={filters} />
                <WidgetCard widget={W.packingByPlant} filters={filters} />
              </Grid>
              <WidgetCard widget={W.packingTable} filters={filters} />
              <WidgetCard widget={W.dummyCartonUsageByPeriod} filters={filters} dummyCartonPeriod={dummyCartonPeriod} setDummyCartonPeriod={setDummyCartonPeriod} />
            </div>
          )}

          {active === "shipment" && (
            <div className="space-y-6" data-analytics-target="tab-shipment">

              {renderFavoriteAnalytics(analyticsById["shipment:card-shippromptnessbyplantcard"])}

              {renderFavoriteAnalytics(analyticsById["shipment:card-yearlydispatchcomparisoncard"])}

              {renderFavoriteAnalytics(analyticsById["shipment:section-shipment-delivered-orders"])}

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-shipment-dispatch-control-center">
                    Shipment / Dispatch Control Center
                  </div>
                  <div className="ceo-block-subtitle">
                    Tracks the ERP shipment workflow from PO ship-by obligation through
                    stock-out recording and final Shipment Out completion. Exceptions are
                    surfaced before charts so operational delays can be acted on immediately.
                  </div>
                </div>
              </div>

              <WidgetCard
                widget={W.shipmentDispatchStatus}
                filters={filters}
              />


              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-shipment-execution-volume">
                    Shipment Execution Volume
                  </div>
                  <div className="ceo-block-subtitle">
                    Transaction-level stock-out volume and movement analysis from the
                    Shipment Details dataset using the CEO global date and compatible filters.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.shipmentOutMcs, W.shipmentOutKgs].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <WidgetCard
                      widget={w}
                      filters={filters}
                    />
                  </div>
                ))}
              </div>

              <Grid>
                <WidgetCard
                  widget={W.shipmentByStore}
                  filters={filters}
                />
                <WidgetCard
                  widget={W.shipmentTrend}
                  filters={filters}
                />
              </Grid>


              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-delivery-regulatory-performance">
                    Delivery & Regulatory Performance
                  </div>
                  <div className="ceo-block-subtitle">
                    Existing FY-based delivery timing, on-water and FDA indicators are retained
                    separately because their current backend calculations do not yet use the
                    selected CEO date range.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[
                  W.onTimeDeliveries,
                  W.earlyShipments,
                  W.delayedShipments,
                  W.onWaterShipments,
                  W.fdaReleaseHold,
                ].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <WidgetCard
                      widget={w}
                      filters={filters}
                    />
                  </div>
                ))}
              </div>

              {renderFavoriteAnalytics(analyticsById["shipment:card-shipmentperformancematrix"])}


              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-shipment-stock-out-drill-down">
                    Shipment / Stock-Out Drill-down
                  </div>
                  <div className="ceo-block-subtitle">
                    Detailed batch, cold-store, rack, PO, grade and stock-out transaction records.
                  </div>
                </div>
              </div>

              <WidgetCard
                widget={W.shipmentTable}
                filters={filters}
              />

            </div>
          )}

          {active === "qc" && (
            <div className="space-y-5" data-analytics-target="tab-qc">

              {renderFavoriteAnalytics(analyticsById["qc:section-qc-batch-antibiotic-testing-status-retest-history"])}

              {renderFavoriteAnalytics(analyticsById["qc:section-qc-suppliers-linked-to-antibiotic-positive-batches"])}

              {renderFavoriteAnalytics(analyticsById["qc:section-qc-quality-at-purchase-by-purchase-variety"])}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon"><FlaskConical size={18} strokeWidth={1.8} /></div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-qc-qa-quality-control">QA / Quality Control</div>
                  <div className="ceo-block-subtitle">
                    Antibiotic and laboratory control using the validated QcRmAbReport source, with lot, centre, plant, result, parameter, sulphite, external laboratory status, and tester-level detail.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <WidgetCard widget={W.qcTotalTests} filters={filters} />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <WidgetCard widget={W.qcClearTests} filters={filters} />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <WidgetCard widget={W.qcDetectedTests} filters={filters} />
                </div>
              </div>

              <Grid>
                <WidgetCard widget={W.qcResultDistribution} filters={filters} />
                <WidgetCard widget={W.qcDetectedByPlant} filters={filters} />
                <WidgetCard widget={W.qcDetectedByCentre} filters={filters} />
                <WidgetCard widget={W.qcParameterVolume} filters={filters} />
                <WidgetCard widget={W.qcExternalResult} filters={filters} />
              </Grid>

              <WidgetCard widget={W.qcTable} filters={filters} />
            </div>
          )}


          {active === "comparisons" && (
            <div className="space-y-5" data-analytics-target="tab-comparisons">

              {renderFavoriteAnalytics(analyticsById["comparisons:section-comparisons-head-on-volume-calendar-year-vs-financial-year"])}
              <SectionNote>
                Current comparison view supports trend-based analysis using selected date range or preset period. Exact FY vs CY and 2–3 year comparison cards will be enhanced after creating dedicated CEO comparison datasets.
              </SectionNote>
              <Grid>
                <WidgetCard widget={W.salesTrend} filters={filters} />
                <WidgetCard widget={W.poTrend} filters={filters} />
                <WidgetCard widget={W.productionTrend} filters={filters} />
                <WidgetCard widget={W.stockTrend} filters={filters} />
                <WidgetCard widget={W.shipmentTrend} filters={filters} />
                <WidgetCard widget={W.priceComparisonOverYears} filters={filters} />
              </Grid>
            </div>
          )}

          {active === "master" && (
            <div className="space-y-5" data-analytics-target="tab-master">
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-master-master-search-field-catalogue">Master Search &amp; Field Catalogue</div>
                  <div className="ceo-block-subtitle">
                    Universal search across buyers, suppliers, lots, orders, invoices, varieties, plants, centres, countries, brands, species, day codes and farms, plus every field every module exposes with its ERP source.
                  </div>
                </div>
              </div>
              <MasterSearchPanel embedded />
            </div>
          )}
          {active === "ai" && (
            <div className="space-y-5" data-analytics-target="tab-ai">
              <div className="ceo-block">
                <div className="flex items-start gap-3">
                  <div className="ceo-section-icon"><Brain size={18} strokeWidth={1.8} /></div>
                  <div>
                    <div className="ceo-block-title" data-analytics-target="section-ai-ai-ask-ceo-dashboard">AI Ask CEO Dashboard</div>
                    <div className="ceo-block-subtitle">
                      Ask the AI Agent to create dashboards and reports using the mapped ERP analytics datasets. OpenAI is used when available; fallback planning is used only if AI is unavailable.
                    </div>
                  </div>
                </div>

                <div className="ceo-ai-builder">
                  <div className="ceo-ai-left">
                    <label className="ceo-ai-label">CEO Prompt</label>
                    <textarea
                      className="ceo-ai-textarea"
                      rows="7"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Example: Create a CEO dashboard for buyer wise sales, stock availability, PO fulfilment, production received quantity, shipment trend, and QC table."
                    />

                    {aiError && (
                      <div className="ceo-ai-error">
                        <AlertTriangle size={15} strokeWidth={1.8} />
                        <span>{aiError}</span>
                      </div>
                    )}

                    <div className="ceo-ai-actions">
                      <button type="button" className="btn btn-outline" onClick={previewAiPlan} disabled={aiLoading || aiCreating}>
                        {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} strokeWidth={1.7} />}
                        Preview Plan
                      </button>
                      <button type="button" className="btn btn-primary" onClick={createAiDashboard} disabled={aiLoading || aiCreating}>
                        {aiCreating ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} strokeWidth={1.7} />}
                        Create Dashboard
                      </button>
                      {createdDashboard && (
                        <button type="button" className="btn btn-outline" onClick={() => nav(`/dashboards/${createdDashboard.id}`)}>
                          <LayoutDashboard size={14} strokeWidth={1.7} />
                          Open Created Dashboard
                        </button>
                      )}
                    </div>

                    {createdDashboard && (
                      <div className="ceo-ai-success">
                        Created: <strong>{createdDashboard.name}</strong>
                      </div>
                    )}
                  </div>

                  <div className="ceo-ai-right">
                    <div className="ceo-ai-panel-title">CEO-ready prompt examples</div>
                    <div className="ceo-ai-prompts compact">
                      {CEO_AI_EXAMPLES.map((prompt) => (
                        <button
                          type="button"
                          key={prompt}
                          className="ceo-ai-prompt"
                          onClick={() => {
                            setAiPrompt(prompt);
                            setAiPlan(null);
                            setCreatedDashboard(null);
                            setAiError("");
                          }}
                        >
                          <BarChart3 size={14} strokeWidth={1.8} />
                          <span>{prompt}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="ceo-block">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="ceo-block-title" data-analytics-target="section-ai-ai-generated-plan">AI Generated Plan</div>
                    <div className="ceo-block-subtitle">
                      Review what AI will create before saving it as a dashboard.
                    </div>
                  </div>
                  {aiProvider && <span className="ceo-provider-pill">{aiProvider === "openai" ? "OpenAI" : "Fallback"}</span>}
                </div>

                {!aiPlan ? (
                  <div className="ceo-ai-empty">
                    <Brain size={24} strokeWidth={1.6} />
                    <div className="mt-2 font-medium">No AI plan generated yet</div>
                    <div className="mt-1 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
                      Type a CEO analytics request and click Preview Plan.
                    </div>
                  </div>
                ) : (
                  <div className="ceo-ai-plan">
                    <div className="ceo-ai-plan-header">
                      <div>
                        <div className="ceo-ai-plan-title">{aiPlan.dashboard_name}</div>
                        <div className="ceo-ai-plan-desc">{aiPlan.description}</div>
                      </div>
                      <div className="ceo-ai-dataset">
                        <span>Dataset</span>
                        <strong>{aiPlan.dataset_name || aiPlan.dataset_id}</strong>
                      </div>
                    </div>

                    {aiPlan.filters?.length > 0 && (
                      <div>
                        <div className="ceo-ai-panel-title mt-4">Filters</div>
                        <div className="ceo-ai-filter-list">
                          {aiPlan.filters.map((f) => (
                            <span key={`${f.field}-${f.type}`}>{f.label || f.field} · {f.type}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="ceo-ai-panel-title mt-4">Widgets</div>
                    <div className="ceo-ai-widget-list">
                      {(aiPlan.widgets || []).map((w, idx) => (
                        <div key={`${w.title}-${idx}`} className="ceo-ai-widget-row">
                          <div className="ceo-ai-widget-type">{w.type}</div>
                          <div>
                            <div className="ceo-ai-widget-title">{w.title}</div>
                            <div className="ceo-ai-widget-meta">
                              {w.dimension && <span>Dimension: {w.dimension}</span>}
                              {w.measure && <span>Measure: {w.measure}</span>}
                              {w.aggregation && <span>Aggregation: {w.aggregation}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {aiPlan.notes?.length > 0 && (
                      <div className="ceo-ai-notes">
                        {aiPlan.notes.map((n, idx) => <div key={idx}>• {n}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
